import {
  calculateReadinessSummary,
  canMoveToNextStage,
  deriveProjectHealth,
  groupBy,
  isTaskOverdue,
  nextStageKey,
  stageGateLabels,
  stageLabels,
  stageOrder,
  statusLabels,
} from "./domain.js";
import { checklistItems, countdownPlan, l2SubProcesses, l3Flows } from "./reference-data.js";
import { projects, readinessItems, reviews, risks, tasks, templates, users, messages, stageHistory, kpiRecords } from "./seed.js";
import mermaid from "mermaid";

mermaid.initialize({ startOnLoad: false, theme: "default", securityLevel: "loose" });

/* ============================================
   全局流程节点 Tooltip
   ============================================ */
const flowTooltip = document.createElement("div");
flowTooltip.className = "flow-tooltip";
document.body.appendChild(flowTooltip);

document.addEventListener("mouseover", (e) => {
  const node = e.target.closest(".flow-node[data-note]");
  if (!node) return;
  const note = node.dataset.note;
  if (!note) return;
  flowTooltip.textContent = note;
  flowTooltip.style.display = "block";
  const rect = node.getBoundingClientRect();
  let left = rect.left + rect.width / 2 - flowTooltip.offsetWidth / 2;
  let top = rect.top - flowTooltip.offsetHeight - 8;
  if (left < 8) left = 8;
  if (left + flowTooltip.offsetWidth > window.innerWidth - 8) {
    left = window.innerWidth - flowTooltip.offsetWidth - 8;
  }
  if (top < 8) top = rect.bottom + 8;
  flowTooltip.style.left = left + "px";
  flowTooltip.style.top = top + "px";
});

document.addEventListener("mouseout", (e) => {
  const node = e.target.closest(".flow-node[data-note]");
  if (node && !node.contains(e.relatedTarget)) {
    flowTooltip.style.display = "none";
  }
});

const storageKey = "jomoo-gtm-command-center-state-v1";

function loadPersistedState() {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const persisted = loadPersistedState();

const state = persisted || {
  route: "intro",
  selectedProjectId: "p-smart-toilet",
  taskStatusFilter: "all",
  taskProjectFilter: "all",
  projectData: structuredClone(projects),
  taskData: structuredClone(tasks),
  readinessData: structuredClone(readinessItems),
  reviewData: structuredClone(reviews),
  riskData: structuredClone(risks),
  stageHistory: structuredClone(stageHistory),
  kpiData: structuredClone(kpiRecords),
};

if (!state.templateStageFilter) state.templateStageFilter = "all";
if (!state.taskSearchQuery) state.taskSearchQuery = "";
if (!state.taskProjectFilter) state.taskProjectFilter = "all";
if (!state.riskSearchQuery) state.riskSearchQuery = "";
if (!state.currentUserId) state.currentUserId = null;
if (!state.messageData) state.messageData = structuredClone(messages);
if (typeof state.sidebarCollapsed !== "boolean") state.sidebarCollapsed = false;
if (!state.workbenchStageDetail) state.workbenchStageDetail = null;
if (!state.stageDetailKey) state.stageDetailKey = null;
if (!state.stageL2View) state.stageL2View = null;
if (!state.stageHistory) state.stageHistory = [];
if (!state.projectStageFilter) state.projectStageFilter = "all";
if (!state.projectStatusFilter) state.projectStatusFilter = "all";
if (!state.projectCategoryFilter) state.projectCategoryFilter = "all";
if (!state.projectViewMode) state.projectViewMode = "card";
if (!state.dashboardTab) state.dashboardTab = "overview";
if (!state.projectDetailStage) state.projectDetailStage = null;
if (!state.projectDetailTab) state.projectDetailTab = "overview";
if (!["overview", "execution", "gates", "risks"].includes(state.projectDetailTab)) state.projectDetailTab = "overview";
if (!state.taskPriorityFilter) state.taskPriorityFilter = "all";
state.flowHighlightStage = "all";

function persistState() {
  localStorage.setItem(storageKey, JSON.stringify(state));
}

const validRoutes = ["intro", "workbench", "projects", "project-detail", "countdown", "tasks", "readiness", "reviews", "risks", "dashboards", "improvements", "templates", "settings", "messages", "stage-detail"];

function navigateTo(route, params = {}) {
  if (route === "stage-detail" && params.stageKey) {
    state.route = route;
    state.stageDetailKey = params.stageKey;
    persistState();
    const hash = `#stage-detail/${params.stageKey}`;
    if (location.hash !== hash) {
      history.pushState(null, "", hash);
    }
    render();
    return;
  }
  state.route = route;
  persistState();
  const hash = `#${route}`;
  if (location.hash !== hash) {
    history.pushState(null, "", hash);
  }
  render();
}

function restoreRouteFromHash() {
  const hash = location.hash.slice(1);
  if (hash.startsWith("stage-detail/")) {
    const stageKey = hash.slice("stage-detail/".length);
    if (stageOrder.includes(stageKey)) {
      state.route = "stage-detail";
      state.stageDetailKey = stageKey;
      persistState();
      return;
    }
  }
  if (validRoutes.includes(hash)) {
    state.route = hash;
  } else if (!hash) {
    state.route = "intro";
  }
  persistState();
}

restoreRouteFromHash();

/* ============================================
   Toast 通知系统
   ============================================ */
function showToast(message, type = "info") {
  let container = document.querySelector(".toast-container");
  if (!container) {
    container = document.createElement("div");
    container.className = "toast-container";
    document.body.appendChild(container);
  }

  const icons = {
    success: icon.check,
    error: "✕",
    info: "ℹ",
  };

  const toast = document.createElement("div");
  toast.className = `toast toast-${type}`;
  toast.innerHTML = `<span class="toast-icon">${icons[type]}</span><span>${message}</span>`;
  container.appendChild(toast);

  setTimeout(() => {
    toast.classList.add("hiding");
    toast.addEventListener("animationend", () => toast.remove());
  }, 2800);
}

let canvasHitRegions = [];
let layerPositions = { l1: [], l2: [] };

/* ============================================
   Modal 弹窗系统
   ============================================ */
let activeModal = null;

function openModal(title, bodyHtml, footerHtml) {
  closeModal();
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal-panel">
      <div class="modal-header">
        <h2>${title}</h2>
        <button class="modal-close" id="modal-close-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
      <div class="modal-body">${bodyHtml}</div>
      ${footerHtml ? `<div class="modal-footer">${footerHtml}</div>` : ""}
    </div>
  `;
  document.body.appendChild(overlay);
  activeModal = overlay;

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeModal();
  });
  overlay.querySelector("#modal-close-btn")?.addEventListener("click", closeModal);
  document.addEventListener("keydown", handleModalEsc);
}

function closeModal() {
  if (activeModal) {
    activeModal.remove();
    activeModal = null;
  }
  document.removeEventListener("keydown", handleModalEsc);
}

function handleModalEsc(e) {
  if (e.key === "Escape") closeModal();
}

/* ============================================
   导航配置
   ============================================ */
const navIcons = {
  workbench: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>',
  projects: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>',
  "project-detail": '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
  countdown: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
  tasks: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>',
  readiness: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
  reviews: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>',
  risks: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  dashboards: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>',
  improvements: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 15l4-4 3 3 5-7"/><path d="M15 7h4v4"/></svg>',
  intro: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>',
  templates: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>',
};

/* ============================================
   扁平化图标库（替换 emoji）
   ============================================ */
const icon = (() => {
  const s = (paths, w = 16, h = 16) => `<svg width="${w}" height="${h}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;
  return {
    target: s('<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>'),
    package: s('<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>'),
    store: s('<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>'),
    calendar: s('<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>'),
    user: s('<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>'),
    settings: s('<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>'),
    mapPin: s('<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>'),
    clipboard: s('<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>'),
    clipboardList: s('<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><line x1="12" y1="12" x2="12" y2="12.01"/><line x1="12" y1="16" x2="12" y2="16.01"/><line x1="12" y1="8" x2="12" y2="8.01"/>'),
    shield: s('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>'),
    shieldCheck: s('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 12 15 16 10"/>'),
    barChart: s('<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>'),
    alertTriangle: s('<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>'),
    map: s('<polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>'),
    layout: s('<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/>'),
    users: s('<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>'),
    fileText: s('<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>'),
    check: s('<polyline points="20 6 9 17 4 12"/>'),
    checkCircle: s('<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>'),
    checkSquare: s('<polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>'),
    clock: s('<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>'),
    play: s('<polygon points="5 3 19 12 5 21 5 3"/>'),
    playCircle: s('<circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>'),
    circle: s('<circle cx="12" cy="12" r="10"/>'),
    arrowRightCircle: s('<circle cx="12" cy="12" r="10"/><polyline points="12 16 16 12 12 8"/><line x1="8" y1="12" x2="16" y2="12"/>'),
    eye: s('<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>'),
    xCircle: s('<circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>'),
    zap: s('<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>'),
    activity: s('<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>'),
    flag: s('<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>'),
    layers: s('<polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>'),
    trendingUp: s('<polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>'),
    alertCircle: s('<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>'),
    info: s('<circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>'),
    search: s('<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>'),
    bell: s('<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>'),
    chevronLeft: s('<polyline points="15 18 9 12 15 6"/>'),
    chevronRight: s('<polyline points="9 18 15 12 9 6"/>'),
    moreHorizontal: s('<circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>'),
    trash: s('<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>'),
    edit: s('<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>'),
    plus: s('<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>'),
    list: s('<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>'),
    log: s('<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>'),
  };
})();

const navSections = [
  {
    title: "工作台",
    items: [
      { key: "workbench", label: "操盘首页", description: "项目状态与关键提醒" },
      { key: "tasks", label: "任务中心", description: "我的待办与团队任务追踪" },
    ],
  },
  {
    title: "产品项目",
    items: [
      { key: "projects", label: "项目池", description: "全部GTM产品项目" },
      { key: "project-detail", label: "单产品操盘", description: "阶段、门禁与里程碑" },
    ],
  },
  {
    title: "运营看板",
    items: [
      { key: "dashboards", label: "运营总览", description: "多项目进展、风险与经营表现" },
      { key: "countdown", label: "上市倒排表", description: "上市前关键动作与节点" },
      { key: "readiness", label: "成熟度检查表", description: "上市准备度与阻塞项" },
      { key: "risks", label: "风险台账", description: "问题升级与闭环管理" },
      { key: "improvements", label: "复盘改进表", description: "首销复盘、经营复盘与经验沉淀" },
    ],
  },
  {
    title: "流程资产",
    items: [
      { key: "intro", label: "流程蓝图", description: "GTM端到端流程与执行对象" },
      { key: "templates", label: "资料模板", description: "流程动作、清单与模板库" },
    ],
  },

];

const introL2Flows = [
  ["规划与立项", "机会洞察 / 需求定位 / 卖点模拟 / 量价测算 / 立项决策", "市场洞察、需求图谱、卖点假设、量价预测、GR1/CDCP结论"],
  ["拓展准备", "产品定义 / 上市策略 / 样机计划 / 预算资源 / PCT协同", "产品策略纲要、样机需求、费用预算、PCT计划、GR2/PDCP结论"],
  ["市场拓展", "拓展包开发 / 终端物料 / 渠道规划 / 营销方案 / 备料准备", "五点三包V1、拓展培训、渠道计划、IMC方案、新品备料计划"],
  ["上市准备", "价格授权 / 首销分货 / 终端训练 / 服务导入 / 系统上线", "街价/授权、首批分货、培训记录、服务预案、GR4/TR5结论"],
  ["上市销售", "发布上市 / 零售执行 / 电商承接 / 渠道动销 / 首销复盘", "上市战报、Sell-out/DOS、渠道反馈、首销复盘、GR5结论"],
  ["稳定销售", "PSI监控 / 价格管控 / VOC闭环 / 经营优化 / 资源调整", "经营看板、价盘监控、问题闭环、策略优化、GR5A复盘"],
  ["退市操盘", "退市判断 / 尾盘清库 / 价格沟通 / 服务延续 / 案例沉淀", "EOM/EOX方案、清库计划、服务承诺、生命周期复盘、GR6结论"],
];

const introL3Actions = [
  ["输入", "IPD节点、消费者洞察、渠道反馈、VOC、历史PSI与经营数据"],
  ["动作", "卖点模拟、五点三包、样机/物料、价格授权、分货、培训、上线"],
  ["门禁", "KICKOFF、GR1-GR6与CDCP/PDCP/ADCP/EOX互锁"],
  ["闭环", "Sell-out、Retail Price、DOS、VOC、ROI、库存和案例复盘"],
];



/* ============================================
   工具函数
   ============================================ */
function userName(id) {
  return users.find((user) => user.id === id)?.name || "未分配";
}

function projectName(id) {
  return state.projectData.find((project) => project.id === id)?.name || "未知项目";
}

function statusBadge(value, extra = "") {
  const label = statusLabels[value] || value;
  return `<span class="badge badge-${value} ${extra}">${label}</span>`;
}

function priorityBadge(priority) {
  return `<span class="priority priority-${priority}">${priority}</span>`;
}

function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/* ============================================
   自动化提醒
   ============================================ */
function checkAutoReminders() {
  if (!state.currentUserId) return;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const existingKeys = new Set(state.messageData.map((m) => m._key).filter(Boolean));
  let added = 0;

  function addReminder(key, userId, type, title, content) {
    if (existingKeys.has(key)) return;
    state.messageData.push({ id: `msg-auto-${Date.now()}-${added}`, _key: key, userId, type, title, content, read: false, createdAt: now.toISOString() });
    existingKeys.add(key);
    added++;
  }

  state.taskData.forEach((task) => {
    if (task.status === "completed_task" || task.status === "canceled") return;
    const due = new Date(task.dueDate);
    const daysUntil = Math.ceil((due - today) / 86400000);
    if (daysUntil <= 3 && daysUntil > 0) {
      addReminder(`task-due-soon-${task.id}`, task.ownerId, "task", `任务即将到期：${task.title}`, `还有 ${daysUntil} 天到期，截止时间 ${task.dueDate}`);
    }
    if (daysUntil < 0) {
      addReminder(`task-overdue-${task.id}`, task.ownerId, "task", `任务已超期：${task.title}`, `已超期 ${-daysUntil} 天，截止时间 ${task.dueDate}`);
    }
  });

  state.riskData.forEach((risk) => {
    if (risk.status === "closed") return;
    if (risk.priority === "P0" || risk.priority === "P1") {
      addReminder(`risk-high-${risk.id}`, risk.ownerId, "risk", `${risk.priority} 风险：${risk.title}`, `${risk.domain} · ${risk.priority} · 请尽快处理`);
    }
  });

  state.reviewData.forEach((review) => {
    if (review.status === "closed") return;
    const scheduled = new Date(review.scheduledAt);
    const daysUntil = Math.ceil((scheduled - today) / 86400000);
    if (daysUntil <= 2 && daysUntil >= 0) {
      const project = state.projectData.find((p) => p.id === review.projectId);
      const ownerId = project?.gtmOwnerId || "u-gtm";
      addReminder(`review-soon-${review.id}`, ownerId, "review", `评审即将召开：${review.title}`, `${daysUntil === 0 ? "今天" : daysUntil + "天后"}召开 · ${review.scheduledAt}`);
    }
  });

  state.readinessData.forEach((item) => {
    if (item.status === "blocked") {
      addReminder(`ready-blocked-${item.id}`, item.ownerId, "system", `成熟度阻塞：${item.dimension}`, `${item.blockerNote || "阻塞中，请尽快处理"}`);
    }
  });

  if (added > 0) persistState();
}

/* ============================================
   核心渲染
   ============================================ */
function render() {
  const app = document.querySelector("#app");

  if (!state.currentUserId) {
    app.setAttribute("data-layout", "login");
    app.innerHTML = renderLogin();
    bindEvents();
    return;
  }

  app.setAttribute("data-layout", "app");
  if (state.sidebarCollapsed) {
    app.setAttribute("data-sidebar-collapsed", "true");
  } else {
    app.removeAttribute("data-sidebar-collapsed");
  }

  const currentUser = users.find((u) => u.id === state.currentUserId);
  if (!currentUser) {
    state.currentUserId = null;
    persistState();
    render();
    return;
  }

  checkAutoReminders();

  const unreadCount = state.messageData.filter((m) => m.userId === state.currentUserId && !m.read).length;

  const collapsed = state.sidebarCollapsed;

  app.innerHTML = `
    <aside class="sidebar ${collapsed ? "collapsed" : ""}">
      <div class="brand">
        <div class="brand-mark">G</div>
        <div class="brand-text">
          <strong>九牧GTM</strong>
          <span>操盘工作台</span>
        </div>
      </div>

      <nav class="nav-groups">
        ${navSections
          .map(
            (section) => `
              <section class="nav-group">
                <h2>${section.title}</h2>
                ${section.items
                  .map(
                    (item) => {
                      const isMessages = item.key === "messages";
                      const badge = isMessages && unreadCount > 0 ? `<span class="nav-badge">${unreadCount}</span>` : "";
                      const icon = navIcons[item.key] || "";
                      return `
                        <button class="nav-item ${state.route === item.key ? "active" : ""}" data-route="${item.key}">
                          ${icon ? `<i class="nav-icon">${icon}</i>` : ""}
                          <div class="nav-text">
                            <span>${item.label}${badge}</span>
                            <small>${item.description}</small>
                          </div>
                        </button>
                      `;
                    }
                  )
                  .join("")}
              </section>
            `
          )
          .join("")}
      </nav>

      <div class="sidebar-note">
        <span>当前环境</span>
        <strong>MVP 演示模式</strong>
      </div>
    </aside>
    <button class="sidebar-toggle" id="sidebar-toggle" title="${collapsed ? "展开" : "收起"}导航">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
    </button>
    <main class="main">
      ${renderHeader()}
      ${renderRoute()}
    </main>
  `;

  bindEvents();
  if (state.route === "intro") requestAnimationFrame(drawIntroFlowCanvas);
  if (state.route === "stage-detail" && state.stageL2View) {
    requestAnimationFrame(() => {
      const container = document.querySelector(`[data-swimlane-stage="${state.stageDetailKey}"][data-swimlane-l2="${state.stageL2View}"]`);
      if (container) {
        drawSwimlaneConnections(container);
      }
    });
  }
}

function renderHeader() {
  const selected = state.projectData.find((project) => project.id === state.selectedProjectId) || state.projectData[0];
  const currentUser = users.find((u) => u.id === state.currentUserId);
  const unreadCount = state.messageData.filter((m) => m.userId === state.currentUserId && !m.read).length;
  const showProjectSwitcher = ["project-detail", "countdown", "readiness", "reviews", "risks", "improvements"].includes(state.route);
  const showTaskAction = ["project-detail", "tasks", "countdown"].includes(state.route);
  const showRiskAction = ["project-detail", "risks", "readiness", "reviews"].includes(state.route);

  return `
    <header class="topbar">
      <div>
        <p class="eyebrow">Go-To-Market Command Center</p>
        <h1>${pageTitle()}</h1>
      </div>
      <div class="topbar-actions">
        ${showProjectSwitcher ? `
          <select id="project-picker" aria-label="切换当前产品项目">
            ${state.projectData.map((project) => `<option value="${project.id}" ${project.id === selected?.id ? "selected" : ""}>${escapeHtml(project.name)}</option>`).join("")}
          </select>
        ` : ""}
        ${showTaskAction ? `<button class="primary-action" id="new-task-btn">新建任务</button>` : ""}
        ${showRiskAction ? `<button class="ghost-action" id="quick-risk">新增风险</button>` : ""}
        ${(showTaskAction || showRiskAction) ? `<div class="topbar-divider"></div>` : ""}

        <button class="msg-trigger" data-route="messages" title="消息中心">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          ${unreadCount > 0 ? `<span class="msg-badge">${unreadCount}</span>` : ""}
        </button>

        <div class="user-menu">
          <button class="user-menu-trigger" id="user-menu-trigger" title="${escapeHtml(currentUser?.name || "")}">
            <div class="user-avatar-small">${currentUser?.name?.charAt(0) || "?"}</div>
            <span class="user-name">${escapeHtml(currentUser?.name || "")}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
          </button>
          <div class="user-dropdown" id="user-dropdown">
            <div class="dropdown-header">
              <div class="user-avatar-small">${currentUser?.name?.charAt(0) || "?"}</div>
              <div>
                <strong>${escapeHtml(currentUser?.name || "")}</strong>
                <span>${escapeHtml(currentUser?.role || "")} · ${escapeHtml(currentUser?.department || "")}</span>
              </div>
            </div>
            <div class="dropdown-divider"></div>
            <button class="dropdown-item" data-route="templates">资料模板</button>
            <button class="dropdown-item" data-route="messages">
              <span>消息中心</span>
              ${unreadCount > 0 ? `<span class="dropdown-badge">${unreadCount}</span>` : ""}
            </button>
            <button class="dropdown-item" data-route="settings">系统设置</button>
            <div class="dropdown-divider"></div>
            <button class="dropdown-item danger" id="logout-btn">退出登录</button>
          </div>
        </div>
      </div>
    </header>
  `;
}

function pageTitle() {
  const map = {
    intro: "流程蓝图",
    workbench: "GTM操盘工作台",
    projects: "产品项目",
    "project-detail": "单产品操盘详情",
    countdown: "上市倒排计划",
    tasks: "操盘任务",
    readiness: "上市准备成熟度",
    reviews: "评审决策",
    risks: "风险台账",
    dashboards: "运营看板",
    improvements: "复盘改进表",
    templates: "资料模板",
    settings: "系统设置",
    messages: "消息中心",
  };
  return map[state.route] || "工作台";
}

function renderRoute() {
  switch (state.route) {
    case "intro": return renderIntro();
    case "projects": return renderProjects();
    case "project-detail": return renderProjectDetail();
    case "countdown": return renderCountdown();
    case "tasks": return renderTasks();
    case "readiness": return renderReadiness();
    case "reviews": return renderReviews();
    case "risks": return renderRisks();
    case "dashboards": return renderDashboards();
    case "improvements": return renderImprovements();
    case "templates": return renderTemplates();
    case "settings": return renderSettings();
    case "messages": return renderMessages();
    case "stage-detail": return renderStageDetail();
    default: return renderWorkbench();
  }
}

/* ============================================
   视图：登录页
   ============================================ */
function renderLogin() {
  const loginGroups = [
    {
      title: "项目操盘核心",
      users: ["u-gtm", "u-product", "u-gm"],
    },
    {
      title: "渠道与销售协同",
      users: ["u-retail", "u-channel", "u-ecom", "u-marketing"],
    },
    {
      title: "服务与支撑协同",
      users: ["u-service", "u-finance", "u-it", "u-vp"],
    },
  ];

  const demoAccounts = loginGroups
    .map((group) => {
      const groupUsers = group.users.map((id) => users.find((user) => user.id === id)).filter(Boolean);
      return `
        <div class="login-role-group">
          <div class="login-role-label">${icon.users} ${group.title}</div>
          <div class="login-user-list">
            ${groupUsers
              .map(
                (user) => `
                  <button class="login-user-card" data-demo-user="${user.username}">
                    <span class="login-avatar">${user.name.slice(0, 1)}</span>
                    <span class="login-user-info">
                      <strong>${user.name}</strong>
                      <span>${user.role}</span>
                      <small>${user.department} · ${user.username}</small>
                    </span>
                  </button>
                `
              )
              .join("")}
          </div>
        </div>
      `;
    })
    .join("");

  const adminAccount = users.find((user) => user.id === "u-admin");

  return `
    <div class="login-page">
      <div class="login-container">
        <div class="login-left">
          <div class="login-brand">
            <div class="brand-mark" style="width:56px;height:56px;font-size:22px;">G</div>
            <div>
              <h1>九牧GTM<br>操盘工作台</h1>
              <p>Go-To-Market Command Center</p>
            </div>
          </div>
          <div class="login-desc">
            <p>将关键阶段、关键动作、关键评审、关键责任和关键风险固化为线上可执行机制。</p>
          </div>
          <div class="login-features">
            <div class="login-feature">
              <span class="login-feature-icon">${icon.layout}</span>
              <div>
                <strong>七阶段流程管控</strong>
                <small>从规划立项到退市复盘，全流程门禁控制</small>
              </div>
            </div>
            <div class="login-feature">
              <span class="login-feature-icon">${icon.users}</span>
              <div>
                <strong>跨部门任务协同</strong>
                <small>GTM、产品、渠道、零售、电商、服务六域联动</small>
              </div>
            </div>
            <div class="login-feature">
              <span class="login-feature-icon">${icon.barChart}</span>
              <div>
                <strong>经营数据看板</strong>
                <small>项目推进、流程执行、经营复盘三维可视</small>
              </div>
            </div>
          </div>
        </div>
        <div class="login-right">
          <h2>账号登录</h2>
          <p class="login-subtitle">请输入用户名和密码进入系统</p>

          <form class="login-form" id="login-form">
            <div class="form-group">
              <label>用户名</label>
              <input type="text" id="login-username" placeholder="请输入用户名" autocomplete="username" />
            </div>
            <div class="form-group">
              <label>密码</label>
              <input type="password" id="login-password" placeholder="请输入密码" autocomplete="current-password" />
            </div>
            <button type="submit" class="primary-action" style="width:100%;margin-top:8px;">登录</button>
          </form>

          <div class="login-demo">
            <p class="login-demo-title">${icon.info} 测试账号（点击自动填入）</p>
            <div class="login-demo-list">
              ${demoAccounts}
              ${
                adminAccount
                  ? `
                    <div class="login-role-group">
                      <div class="login-role-label">${icon.settings} 系统管理</div>
                      <div class="login-user-list">
                        <button class="login-user-card login-user-mgmt" data-demo-user="${adminAccount.username}">
                          <span class="login-avatar">${adminAccount.name.slice(0, 1)}</span>
                          <span class="login-user-info">
                            <strong>${adminAccount.name}</strong>
                            <span>${adminAccount.role}</span>
                            <small>${adminAccount.department} · ${adminAccount.username}</small>
                          </span>
                        </button>
                      </div>
                    </div>
                  `
                  : ""
              }
            </div>
            <p class="login-demo-hint">密码统一为 <strong>123456</strong>，admin 密码为 <strong>admin</strong></p>
          </div>
        </div>
      </div>
    </div>
  `;
}

/* ============================================
   视图：介绍页
   ============================================ */
function renderIntro() {
  return `
    <section class="panel flow-canvas-panel">
      <div class="panel-header">
        <div>
          <p class="eyebrow">L1-L3 Canvas</p>
          <h2>从IPD互锁到经营复盘：九牧GTM三层流程画布</h2>
          <p>点击 Canvas 上任意 L1 阶段或 L2 动作节点，查看该阶段完整的 L1-L5 流程详情。</p>
        </div>
      </div>
      <div class="canvas-shell">
        <canvas id="gtm-flow-canvas" aria-label="GTM L1-L3流程画布"></canvas>
      </div>
      <div class="canvas-legend">
        <span><i class="legend-l1"></i>L1：七阶段主链路</span>
        <span><i class="legend-l2"></i>L2：阶段内全量子流程</span>
        <span><i class="legend-l3"></i>L3：IPD/GR/经营闭环</span>
      </div>
    </section>

    <section class="panel l2-full-panel">
      <div class="panel-header">
        <div>
          <p class="eyebrow">L2 Full List</p>
          <h2>九牧GTM L2全量流程清单</h2>
          <p>这里把每个 L1 阶段下的 L2 子流程全部展开，便于客户确认流程边界、责任协同和后续系统固化范围。</p>
        </div>
      </div>
      <div class="l2-full-grid">
        ${stageOrder
          .map((stageKey, index) => {
            const items = l2SubProcesses[stageKey] || [];
            return `
              <article class="l2-full-card">
                <header>
                  <span>${String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <strong>${stageLabels[stageKey]}</strong>
                    <small>${stageGateLabels[stageKey]}</small>
                  </div>
                </header>
                <div class="l2-full-list">
                  ${items
                    .map(
                      (item, itemIndex) => `
                        <button class="l2-full-item" data-stage-l2-jump="${stageKey}" data-l2-view="${item.name}">
                          <b>${itemIndex + 1}</b>
                          <span>
                            <strong>${item.name}</strong>
                            <small>${item.desc}</small>
                          </span>
                        </button>
                      `
                    )
                    .join("")}
                </div>
              </article>
            `;
          })
          .join("")}
      </div>
    </section>

    <section class="panel flow-panel">
      <div class="panel-header">
        <div>
          <p class="eyebrow">L3 Execution</p>
          <h2>GTM三级执行：九牧系统里真正需要被操作和留下痕迹的对象</h2>
        </div>
      </div>
      <div class="l3-map">
        ${introL3Actions
          .map(
            ([title, text]) => `
              <article>
                <strong>${title}</strong>
                <p>${text}</p>
              </article>
            `
          )
          .join("")}
      </div>
      <div class="system-map">
        ${[
          ["产品项目", "一个新品一条GTM操盘主线"],
          ["IPD互锁", "CDCP/PDCP/ADCP/EOX节点同步"],
          ["五点三包", "卖点、拓展包、物料包、培训包、服务包"],
          ["上市倒排", "样机、物料、价格、分货、培训按节点推进"],
          ["阶段门禁", "GR1-GR6控制进入下一阶段"],
          ["风险台账", "阻塞、延期、价盘、库存和服务问题闭环"],
          ["经营复盘", "Sell-out、DOS、VOC、ROI和案例沉淀"],
        ]
          .map(([title, text]) => `<article><strong>${title}</strong><span>${text}</span></article>`)
          .join("")}
      </div>
    </section>

    <section class="panel flow-localization-panel">
      <div class="panel-header">
        <div>
          <p class="eyebrow">Jomoo Localization</p>
          <h2>九牧流程蓝图本地化调整点</h2>
          <p>相比通用 GTM，九牧的流程图需要把“IPD节点互锁、五点三包、渠道执行和经营复盘”放到主链路里，避免只画流程、不落执行。</p>
        </div>
      </div>
      <div class="localization-grid">
        ${[
          ["补齐IPD互锁", "在L1门禁中显式标注CDCP、PDCP、ADCP、EOX，避免GTM与研发节点脱节。"],
          ["突出五点三包", "把卖点模拟、产品传播名、拓展包、营销/零售/服务物料包作为可检查交付物。"],
          ["强化渠道差异", "零售高定、电商、渠道、服务、交付和财经不再只是参与方，而是并行泳道。"],
          ["前移供应交付", "样机、备料、首批订单、分货、物流仓储要进入上市准备和上市销售节点。"],
          ["经营指标闭环", "稳定销售阶段重点看Sell-out、Retail Price、DOS、PSI、VOC和Marketing ROI。"],
          ["保留退市管理", "退市不是尾声动作，要管理EOM/EOX、尾盘清库、服务承诺和生命周期案例复盘。"],
        ]
          .map(([title, text]) => `<article><strong>${title}</strong><p>${text}</p></article>`)
          .join("")}
      </div>
    </section>
  `;
}

/* ============================================
   工具：L2 关键词分类
   ============================================ */
function classifyL2(stageKey, activity) {
  const keywords = {
    planning_initiation: {
      "市场机会识别": ["市场洞察", "竞品", "渠道反馈", "大盘", "市场空间", "市场数据", "沟通需求", "行业报告"],
      "用户需求洞察": ["用户画像", "消费者需求", "服务需求", "用户需求", "调研消费者", "用户洞察", "收集需求", "功能卖点需求", "电商消费者", "沟通"],
      "产品概念与商业模式设计": ["产品概念", "商业模式", "卖点", "规格", "定价", "生命周期销量预测", "功能卖点", "产品定义", "上市策略", "整体销量预测"],
      "可行性评估与立项决策": ["财务概算", "立项", "评审", "决策", "启动会", "Kick Off", "收益测算", "风险评估", "PCT", "GR1", "CDCP"],
    },
    expansion_preparation: {
      "产品定义确认": ["产品定义", "规格", "卖点", "技术支撑", "功能卖点", "产品规格"],
      "上市路径设计": ["上市路径", "项目计划", "渠道导入", "门店样板", "上市策略", "操盘方案", "GTM策略", "评审", "决策", "销量预测"],
      "目标渠道与客群规划": ["渠道政策", "目标客群", "费用预算", "渠道拓展", "覆盖计划", "营销策略", "预算审批"],
      "项目资源配置与计划编制": ["资源配置", "PCT", "预算", "备料", "项目计划", "角色分工", "团队组建", "样机"],
    },
    market_expansion: {
      "渠道拓展准备": ["渠道拓展", "覆盖计划", "渠道政策", "要货预测", "渠道导入", "准入", "导入方案", "渠道激励"],
      "市场推广策略设计": ["IMC", "营销方案", "推广策略", "营销策略", "传播", "营销预算", "电商运营", "评审", "决策", "整合营销", "KOL"],
      "产品卖点与内容资产准备": ["卖点", "内容资产", "拓展包", "物料设计", "产品卖点", "FAB", "内容审核", "宣传素材"],
      "上市资源前置协同": ["资源前置", "样机", "物料", "定制化", "新品物料", "资源协同", "操盘方案", "预算", "服务方案", "要货", "M+6"],
    },
    launch_preparation: {
      "上市成熟度检查": ["成熟度", "checklist", "上市准备", "把控上市", "操盘方案", "时间", "风险", "评审", "决策"],
      "物料准备": ["物料", "终端物料", "零售物料", "主视觉", "销售工具", "陈列标准"],
      "培训准备": ["培训", "零售培训", "导购培训", "渠道培训", "话术", "陈列落地"],
      "服务准备": ["服务方案", "售后", "知识库", "服务准备", "安装", "交付标准"],
      "库存和交付准备": ["库存", "发货", "交付", "备料", "全球发货", "要货预测", "首批订单"],
      "系统及数据准备": ["系统", "数据", "电商", "页面", "发布会", "Listing"],
    },
    launch_sales: {
      "首发上市执行": ["发布会", "上市执行", "开售", "页面", "预热", "首发", "页面上线", "交付", "首销", "销售", "服务", "SO", "竞品", "零售方案"],
      "终端导入与渠道激活": ["终端", "渠道激活", "PSI", "渠道导入", "门店", "渠道承接", "要货"],
      "上市传播与转化运营": ["传播", "转化", "IMC执行", "促销", "电商", "营销执行", "预算", "营销方案"],
      "首销复盘": ["首销复盘", "复盘报告", "首销上市", "IMC复盘", "首销情况", "反馈", "决策"],
    },
    stable_sales: {
      "动销监控": ["动销", "PSI", "SO", "竞品价格", "要货计划", "销售监控", "库存周转", "动销分析"],
      "问题闭环与改进": ["VOC", "售后", "问题闭环", "市场反馈", "改进", "售后问题", "改进计划", "闭环改进"],
      "产品经营优化": ["经营优化", "销售节奏", "策略调整", "零售复盘", "服务复盘", "经营优化", "营销", "促销", "零售方案", "服务阶段", "评审", "经营分析"],
      "资源调整与持续经营": ["资源调整", "预算", "月度经营", "迭代", "新品规划", "持续经营", "月度产品", "经营数据", "资源分配"],
    },
    phase_out: {
      "退市判断": ["退市", "EOM", "生命周期", "退市判断", "退市方案", "退市评估"],
      "库存与渠道处理": ["库存处理", "清尾", "Last Buy", "渠道清尾", "库存策略", "库存消化"],
      "价格与客户影响管理": ["价格", "客户沟通", "服务延续", "退市价格", "客户影响", "服务方案", "价格策略"],
      "经验复盘与知识沉淀": ["经验复盘", "知识沉淀", "经营分析", "产品路标", "生命周期复盘", "接续计划", "服务阶段", "评审", "决策", "知识文档"],
    },
  };

  const stageKeywords = keywords[stageKey];
  if (!stageKeywords) return "其他活动";

  for (const [l2Name, words] of Object.entries(stageKeywords)) {
    for (const word of words) {
      if (activity.includes(word)) return l2Name;
    }
  }
  return "其他活动";
}

/* ============================================
   工具：将 Canvas L2 简写名称匹配到完整 L2 名称
   ============================================ */
function matchL2Name(stageKey, shortName) {
  const l2Items = l2SubProcesses[stageKey] || [];
  if (l2Items.length === 0) return null;

  const aliasMap = {
    planning_initiation: {
      "机会洞察": "市场机会识别",
      "需求定位": "用户需求洞察",
      "卖点模拟": "产品概念与商业模式设计",
      "量价测算": "产品概念与商业模式设计",
      "立项决策": "可行性评估与立项决策",
    },
    expansion_preparation: {
      "产品定义": "产品定义确认",
      "上市策略": "上市路径设计",
      "样机计划": "项目资源配置与计划编制",
      "预算资源": "项目资源配置与计划编制",
      "PCT协同": "项目资源配置与计划编制",
    },
    market_expansion: {
      "拓展包开发": "产品卖点与内容资产准备",
      "终端物料": "产品卖点与内容资产准备",
      "渠道规划": "渠道拓展准备",
      "营销方案": "市场推广策略设计",
      "备料准备": "上市资源前置协同",
    },
    launch_preparation: {
      "价格授权": "上市成熟度检查",
      "首销分货": "库存和交付准备",
      "终端训练": "培训准备",
      "服务导入": "服务准备",
      "系统上线": "系统及数据准备",
    },
    launch_sales: {
      "发布上市": "首发上市执行",
      "零售执行": "终端导入与渠道激活",
      "电商承接": "上市传播与转化运营",
      "渠道动销": "终端导入与渠道激活",
      "首销复盘": "首销复盘",
    },
    stable_sales: {
      "PSI监控": "动销监控",
      "价格管控": "动销监控",
      "VOC闭环": "问题闭环与改进",
      "经营优化": "产品经营优化",
      "资源调整": "资源调整与持续经营",
    },
    phase_out: {
      "退市判断": "退市判断",
      "尾盘清库": "库存与渠道处理",
      "价格沟通": "价格与客户影响管理",
      "服务延续": "价格与客户影响管理",
      "案例沉淀": "经验复盘与知识沉淀",
    },
  };

  const alias = aliasMap[stageKey]?.[shortName];
  if (alias && l2Items.some((l2) => l2.name === alias)) return alias;

  // 1. 前缀匹配
  for (const l2 of l2Items) {
    if (l2.name.startsWith(shortName)) return l2.name;
  }
  // 2. 包含匹配
  for (const l2 of l2Items) {
    if (l2.name.includes(shortName)) return l2.name;
  }
  // 3. fallback：按 introL2Flows 中的顺序匹配
  const stageIndex = stageOrder.indexOf(stageKey);
  const flowDef = introL2Flows[stageIndex];
  if (flowDef) {
    const items = flowDef[1].split(" / ");
    const idx = items.indexOf(shortName);
    if (idx >= 0 && idx < l2Items.length) return l2Items[idx].name;
  }
  return null;
}

/* ============================================
   工具：从 checklist 动态生成 L3 流程定义
   ============================================ */
function generateFlowDefFromChecklist(stageKey, l2Name) {
  const items = checklistItems.filter(
    (item) => item.stageKey === stageKey && classifyL2(stageKey, item.activity) === l2Name
  );

  const stageIndex = stageOrder.indexOf(stageKey) + 1;
  const l2Index = (l2SubProcesses[stageKey] || []).findIndex((l) => l.name === l2Name) + 1;

  if (items.length === 0) {
    // 无 checklist 数据时生成占位流程，确保所有 L2 都有泳道图
    return {
      code: `L2-${String(stageIndex).padStart(2, "0")}-${String(l2Index).padStart(2, "0")}`,
      start: "开始",
      end: "结束",
      lanes: {
        "GTM": [
          {
            id: `placeholder_${stageKey}_${l2Name.replace(/\s+/g, "_")}`,
            code: `L3-${String(stageIndex).padStart(2, "0")}-${String(l2Index).padStart(2, "0")}-01`,
            name: "流程定义待补充",
            input: "",
            output: "敬请期待",
          },
        ],
      },
      connections: [],
    };
  }

  // 按角色分组，保持原始顺序
  const byRole = {};
  items.forEach((item) => {
    if (!byRole[item.role]) byRole[item.role] = [];
    byRole[item.role].push(item);
  });

  let codeIndex = 1;
  const lanes = {};
  const idMap = {};

  Object.entries(byRole).forEach(([role, activities]) => {
    lanes[role] = activities.map((item, idx) => {
      const id = `auto_${stageKey}_${l2Name.replace(/\s+/g, "_")}_${role}_${idx}`;
      const code = `L3-${String(stageIndex).padStart(2, "0")}-${String(l2Index).padStart(2, "0")}-${String(codeIndex++).padStart(2, "0")}`;
      idMap[`${role}_${idx}`] = id;
      return {
        id,
        code,
        name: item.activity,
        input: "",
        output: item.deliverable,
        note: item.note || `活动：${item.activity}\n交付物：${item.deliverable}`,
      };
    });
  });

  // 生成连接关系：Start → 每个角色首节点，每个角色末节点 → End，同一角色内按顺序串联
  const connections = [];
  Object.entries(byRole).forEach(([role, activities]) => {
    const firstId = idMap[`${role}_0`];
    connections.push(["Start", firstId, ""]);
    for (let i = 1; i < activities.length; i++) {
      connections.push([
        idMap[`${role}_${i - 1}`],
        idMap[`${role}_${i}`],
        ""
      ]);
    }
    const lastId = idMap[`${role}_${activities.length - 1}`];
    connections.push([lastId, "End", activities[activities.length - 1].deliverable || ""]);
  });

  return {
    code: `L2-${String(stageIndex).padStart(2, "0")}-${String(l2Index).padStart(2, "0")}`,
    start: "开始",
    end: "结束",
    lanes,
    connections,
  };
}

/* ============================================
   工具：生成 L3 Mermaid 泳道图语法
   ============================================ */
function generateL3MermaidDiagram(stageKey, l2Name) {
  const flowDef = l3Flows[stageKey]?.[l2Name];
  if (flowDef) {
    let diagram = "flowchart LR\n";
    diagram += `    Start(["${flowDef.start}"])\n`;
    diagram += `    End(["${flowDef.end}"])\n`;

    Object.entries(flowDef.lanes).forEach(([role, activities], roleIndex) => {
      const safeId = `R${roleIndex}`;
      diagram += `    subgraph ${safeId}["${role}"]\n`;
      diagram += `        direction TB\n`;

      activities.forEach((act, idx) => {
        diagram += `        ${act.id}["${act.name}"]\n`;
        if (idx > 0) {
          const prev = activities[idx - 1];
          diagram += `        ${prev.id} -->|"${prev.output}"| ${act.id}\n`;
        }
      });

      diagram += `    end\n`;
    });

    flowDef.connections.forEach(([from, to]) => {
      diagram += `    ${from} --> ${to}\n`;
    });

    return diagram;
  }

  // Fallback: 使用关键词分类的 checklist items
  const items = checklistItems.filter(
    (item) => item.stageKey === stageKey && classifyL2(stageKey, item.activity) === l2Name
  );
  return generateMermaidDiagram(items);
}

/* ============================================
   工具：生成 Mermaid 泳道图语法（fallback）
   ============================================ */
function generateMermaidDiagram(items) {
  const byRole = {};
  items.forEach((item) => {
    if (!byRole[item.role]) byRole[item.role] = [];
    byRole[item.role].push(item);
  });

  let diagram = "flowchart LR\n";

  Object.entries(byRole).forEach(([role, activities], roleIndex) => {
    const safeId = `R${roleIndex}`;
    diagram += `    subgraph ${safeId}["${role}"]\n`;
    diagram += `        direction TB\n`;

    activities.forEach((item, idx) => {
      const nodeId = `N${roleIndex}_${idx}`;
      const isReview = item.activity.includes("评审") || item.activity.includes("决策");
      const label = item.activity;
      if (isReview) {
        diagram += `        ${nodeId}{"${label}"}\n`;
      } else {
        diagram += `        ${nodeId}["${label}"]\n`;
      }
      if (idx > 0) {
        const prevId = `N${roleIndex}_${idx - 1}`;
        diagram += `        ${prevId} --> ${nodeId}\n`;
      }
    });

    diagram += `    end\n`;
  });

  return diagram;
}

/* ============================================
   工具：渲染手动泳道图 HTML
   ============================================ */
function renderSwimlaneHtml(flowDef, stageKey, l2Name) {
  const laneNames = Object.keys(flowDef.lanes);
  const startLabel = flowDef.start || "开始";
  const endLabel = flowDef.end || "结束";

  function makeNode(act, role) {
    const isReview = act.name.includes("评审") || act.name.includes("决策") || act.name.includes("审核") || act.name.includes("CDCP");
    const hasOutput = act.output && act.output !== "敬请期待";
    const nodeClass = isReview ? "flow-node flow-node-decision" : "flow-node";
    return `
      <div class="swimlane-node-wrapper">
        <div class="${nodeClass}" id="${act.id}" data-lane="${role}" data-note="${(act.note || "").replace(/"/g, "&quot;")}">
          ${act.code ? `<div class="flow-node-code">${act.code}</div>` : ""}
          <div class="flow-node-name">${act.name}</div>
          ${hasOutput ? `
            <div class="flow-node-doc">
              <span class="doc-icon">\u{1F4C4}</span>
              <span class="doc-text">${act.output}</span>
            </div>
          ` : ""}
        </div>
      </div>
    `;
  }

  function startHtml() {
    return `
      <div class="swimlane-node-wrapper">
        <div class="flow-node flow-node-start" id="Start" data-note="${startLabel}">
          <div class="flow-node-name">${startLabel}</div>
        </div>
      </div>
    `;
  }

  function endHtml() {
    return `
      <div class="swimlane-node-wrapper">
        <div class="flow-node flow-node-end" id="End" data-note="${endLabel}">
          <div class="flow-node-name">${endLabel}</div>
        </div>
      </div>
    `;
  }

  const rowsHtml = laneNames
    .map((role, idx) => {
      const activities = flowDef.lanes[role];
      const nodesHtml = activities.map((act) => makeNode(act, role)).join("");
      const isFirst = idx === 0;
      const isLast = idx === laneNames.length - 1;
      return `
        <div class="swimlane-row" data-lane="${role}">
          <div class="swimlane-role-header">${role}</div>
          <div class="swimlane-row-body">${isFirst ? startHtml() : ""}${nodesHtml}${isLast ? endHtml() : ""}</div>
        </div>
      `;
    })
    .join("");

  return `
    <div class="swimlane-container" data-swimlane-stage="${stageKey}" data-swimlane-l2="${l2Name}">
      ${rowsHtml}
      <svg class="swimlane-svg">
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
          </marker>
        </defs>
      </svg>
    </div>
  `;
}

function renderSwimlaneChart(stageKey, l2Name) {
  let flowDef = l3Flows[stageKey]?.[l2Name];
  if (!flowDef) {
    flowDef = generateFlowDefFromChecklist(stageKey, l2Name);
  }
  if (!flowDef) {
    return `<div class="swimlane-container" data-swimlane="${stageKey}-${l2Name}"><div style="padding:20px;color:var(--text-secondary);text-align:center;">暂无详细流程数据</div></div>`;
  }
  return renderSwimlaneHtml(flowDef, stageKey, l2Name);
}

/* ============================================
   工具：绘制泳道图 SVG 连接线
   ============================================ */
function drawSwimlaneConnections(container) {
  const svg = container.querySelector(".swimlane-svg");
  if (!svg) return;

  const stageKey = container.dataset.swimlaneStage;
  const l2Name = container.dataset.swimlaneL2;
  if (!stageKey || !l2Name) return;
  let flowDef = l3Flows[stageKey]?.[l2Name];
  if (!flowDef) {
    flowDef = generateFlowDefFromChecklist(stageKey, l2Name);
  }
  if (!flowDef) return;

  svg.querySelectorAll("path, text").forEach((el) => el.remove());

  const containerRect = container.getBoundingClientRect();
  svg.setAttribute("width", container.offsetWidth);
  svg.setAttribute("height", container.offsetHeight);

  const allPaths = [];

  flowDef.connections.forEach(([from, to, label]) => {
    const fromEl = container.querySelector(`#${CSS.escape(from)}`);
    const toEl = container.querySelector(`#${CSS.escape(to)}`);
    if (!fromEl || !toEl) return;

    const fromRect = fromEl.getBoundingClientRect();
    const toRect = toEl.getBoundingClientRect();
    const fromLane = fromEl.closest(".swimlane-row");
    const toLane = toEl.closest(".swimlane-row");
    const sameLane = fromLane === toLane;

    const x1 = fromRect.right - containerRect.left;
    const y1 = fromRect.top + fromRect.height / 2 - containerRect.top;
    const x2 = toRect.left - containerRect.left;
    const y2 = toRect.top + toRect.height / 2 - containerRect.top;

    let d;
    if (sameLane) {
      const midX = x1 + (x2 - x1) / 2;
      d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
    } else {
      const isStart = from === "Start";
      const isEnd = to === "End";
      const step = 16;

      if (isStart) {
        const exitX = x1 + step;
        const laneIdx = allPaths.filter(p => p.from === "Start").length;
        const offsetX = laneIdx * step;
        d = `M ${x1} ${y1} L ${exitX + offsetX} ${y1} L ${exitX + offsetX} ${y2} L ${x2} ${y2}`;
      } else if (isEnd) {
        const laneIdx = allPaths.filter(p => p.to === "End").length;
        const offsetX = laneIdx * step;
        const approachX = x2 - step - offsetX;
        d = `M ${x1} ${y1} L ${approachX} ${y1} L ${approachX} ${y2} L ${x2} ${y2}`;
      } else {
        const exitX = Math.min(x1 + step, (x1 + x2) / 2);
        d = `M ${x1} ${y1} L ${exitX} ${y1} L ${exitX} ${y2} L ${x2} ${y2}`;
      }
    }

    allPaths.push({ from, to, d, label });
  });

  allPaths.forEach(({ d, label }, idx) => {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", d);
    path.setAttribute("stroke", "#94a3b8");
    path.setAttribute("stroke-width", "2");
    path.setAttribute("fill", "none");
    path.setAttribute("marker-end", "url(#arrowhead)");
    svg.appendChild(path);

    if (label) {
      const parts = d.match(/[\d.]+/g).map(Number);
      const midX = (parts[0] + parts[parts.length - 2]) / 2;
      const midY = (parts[1] + parts[parts.length - 1]) / 2;
      const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
      text.setAttribute("x", midX);
      text.setAttribute("y", midY - 6);
      text.setAttribute("fill", "#64748b");
      text.setAttribute("font-size", "10");
      text.setAttribute("font-weight", "600");
      text.setAttribute("text-anchor", "middle");
      text.textContent = label;
      svg.appendChild(text);
    }
  });
}

/* ============================================
   视图：阶段详情页（L1-L5）
   ============================================ */
function renderStageDetail() {
  const stageKey = state.stageDetailKey || stageOrder[0];
  const index = stageOrder.indexOf(stageKey);
  const num = String(index + 1).padStart(2, "0");
  const l2Items = l2SubProcesses[stageKey] || [];
  const l4Items = checklistItems.filter((item) => item.stageKey === stageKey);

  const prevStage = index > 0 ? stageOrder[index - 1] : null;
  const nextStage = index < stageOrder.length - 1 ? stageOrder[index + 1] : null;

  const l2Cards = l2Items
    .map(
      (l2) => `
        <article class="l2-card">
          <strong>${l2.name}</strong>
          <p>${l2.desc}</p>
        </article>
      `
    )
    .join("");

  const l4Rows = l4Items
    .map(
      (item) => `
        <tr>
          <td class="col-role">${item.role}</td>
          <td class="col-activity">${item.activity}</td>
          <td class="col-deliverable">${item.deliverable}</td>
          <td class="col-note">${item.note || "按标准流程执行"}</td>
        </tr>
      `
    )
    .join("");

  const l2View = state.stageL2View;

  if (l2View) {
    // L2 详情视图
    const l2Data = l2Items.find((l2) => l2.name === l2View);
    const l2Index = l2Items.findIndex((l2) => l2.name === l2View);
    const prevL2 = l2Index > 0 ? l2Items[l2Index - 1] : null;
    const nextL2 = l2Index < l2Items.length - 1 ? l2Items[l2Index + 1] : null;

    const l4ItemsForL2 = checklistItems.filter(
      (item) => item.stageKey === stageKey && classifyL2(stageKey, item.activity) === l2View
    );

    // ===== 概览数据计算 =====
    const roles = [...new Set(l4ItemsForL2.map((i) => i.role))];
    const deliverables = [...new Set(l4ItemsForL2.map((i) => i.deliverable).filter((d) => d && d !== "待补充交付件"))];
    const hasReview = l4ItemsForL2.some((i) => i.activity.includes("评审") || i.activity.includes("决策") || i.activity.includes("CDCP"));
    const flowDef = l3Flows[stageKey]?.[l2View];
    const l3NodeCount = flowDef ? Object.values(flowDef.lanes).flat().length : 0;

    // 概览信息栏
    const overviewHtml = `
      <div class="l2-overview-bar">
        <div class="l2-stat">
          <span class="l2-stat-num">${l4ItemsForL2.length}</span>
          <span class="l2-stat-label">活动项</span>
        </div>
        <div class="l2-stat">
          <span class="l2-stat-num">${roles.length}</span>
          <span class="l2-stat-label">参与角色</span>
        </div>
        <div class="l2-stat">
          <span class="l2-stat-num">${deliverables.length}</span>
          <span class="l2-stat-label">交付物</span>
        </div>
        ${l3NodeCount > 0 ? `
          <div class="l2-stat">
            <span class="l2-stat-num">${l3NodeCount}</span>
            <span class="l2-stat-label">流程节点</span>
          </div>
        ` : ""}
        ${hasReview ? `
          <div class="l2-stat l2-stat-highlight">
            <span class="l2-stat-num">🚪</span>
            <span class="l2-stat-label">含评审/决策</span>
          </div>
        ` : ""}
        <div class="l2-role-tags">
          ${roles.map((r) => `<span class="l2-role-tag">${r}</span>`).join("")}
        </div>
      </div>
    `;

    // 交付物卡片墙
    const deliverableCardsHtml = deliverables.length
      ? `
        <div class="stage-section">
          <h3 class="stage-section-title">${icon.fileText} 关键交付物</h3>
          <div class="deliverable-grid">
            ${deliverables.map((d) => {
              const owner = l4ItemsForL2.find((i) => i.deliverable === d)?.role || "";
              return `
                <div class="deliverable-card">
                  <div class="deliverable-icon">${icon.fileText}</div>
                  <div class="deliverable-name">${d}</div>
                  ${owner ? `<div class="deliverable-owner">${owner}</div>` : ""}
                </div>
              `;
            }).join("")}
          </div>
        </div>
      `
      : "";

    // 角色职责速览
    const roleResponsibilityHtml = roles.length
      ? `
        <div class="stage-section">
          <h3 class="stage-section-title">${icon.users} 角色职责速览</h3>
          <div class="role-resp-grid">
            ${roles.map((role) => {
              const roleActs = l4ItemsForL2.filter((i) => i.role === role);
              return `
                <div class="role-resp-card">
                  <div class="role-resp-header">
                    <strong>${role}</strong>
                    <span class="role-resp-count">${roleActs.length} 项</span>
                  </div>
                  <ul class="role-resp-list">
                    ${roleActs.map((i) => `<li>${i.activity}</li>`).join("")}
                  </ul>
                </div>
              `;
            }).join("")}
          </div>
        </div>
      `
      : "";

    // L2 内导航
    const l2NavHtml = (prevL2 || nextL2)
      ? `
        <div class="l2-inner-nav">
          ${prevL2 ? `<button class="l2-nav-btn" data-l2-view="${prevL2.name}">← ${prevL2.name}</button>` : `<span></span>`}
          <span class="l2-nav-progress">${l2Index + 1} / ${l2Items.length}</span>
          ${nextL2 ? `<button class="l2-nav-btn" data-l2-view="${nextL2.name}">${nextL2.name} →</button>` : `<span></span>`}
        </div>
      `
      : "";

    const l4RowsForL2 = l4ItemsForL2
      .map(
        (item) => `
          <tr>
            <td class="col-role">${item.role}</td>
            <td class="col-activity">${item.activity}</td>
            <td class="col-deliverable">${item.deliverable}</td>
            <td class="col-note">${item.note || "按标准流程执行"}</td>
          </tr>
        `
      )
      .join("");

    return `
      <div class="stage-detail-page">
        <div class="stage-detail-header">
          <button class="ghost-action" id="back-to-stage">← 返回${stageLabels[stageKey]}</button>
          <div class="stage-detail-nav">
            ${prevStage ? `<button class="ghost-action" data-goto-stage="${prevStage}">← ${stageLabels[prevStage]}</button>` : `<span></span>`}
            ${nextStage ? `<button class="ghost-action" data-goto-stage="${nextStage}">${stageLabels[nextStage]} →</button>` : `<span></span>`}
          </div>
        </div>

        <section class="panel flow-stage-panel">
          <div class="stage-l1-header">
            <span class="stage-num">${num}</span>
            <div class="stage-title">
              <h2>${stageLabels[stageKey]} — ${l2View}</h2>
              <p><strong>L2 描述：</strong>${l2Data ? l2Data.desc : ""}</p>
            </div>
          </div>

          ${overviewHtml}

          <div class="stage-section">
            <h3 class="stage-section-title">L3 — 流程泳道图</h3>
            ${renderSwimlaneChart(stageKey, l2View)}
          </div>

          ${deliverableCardsHtml}

          <div class="stage-section">
            <h3 class="stage-section-title">L4 — 操作清单 &amp; L5 — 执行标准</h3>
            <div class="checklist-table-wrap">
              <table class="checklist-table">
                <thead>
                  <tr>
                    <th class="col-role">角色</th>
                    <th class="col-activity">活动</th>
                    <th class="col-deliverable">交付物</th>
                    <th class="col-note">操作要点（L5）</th>
                  </tr>
                </thead>
                <tbody>${l4RowsForL2 || `<tr><td colspan="4" style="text-align:center;color:var(--text-secondary)">暂无数据</td></tr>`}</tbody>
              </table>
            </div>
          </div>

          ${roleResponsibilityHtml}

          ${l2NavHtml}
        </section>
      </div>
    `;
  }

  // 阶段总览视图（默认）
  return `
    <div class="stage-detail-page">
      <div class="stage-detail-header">
        <button class="ghost-action" id="back-to-intro">← 返回流程蓝图</button>
        <div class="stage-detail-nav">
          ${prevStage ? `<button class="ghost-action" data-goto-stage="${prevStage}">← ${stageLabels[prevStage]}</button>` : `<span></span>`}
          ${nextStage ? `<button class="ghost-action" data-goto-stage="${nextStage}">${stageLabels[nextStage]} →</button>` : `<span></span>`}
        </div>
      </div>

      <section class="panel flow-stage-panel">
        <div class="stage-l1-header">
          <span class="stage-num">${num}</span>
          <div class="stage-title">
            <h2>${stageLabels[stageKey]}</h2>
            <p><strong>门禁：</strong>${stageGateLabels[stageKey]}</p>
          </div>
        </div>

        <div class="stage-section">
          <h3 class="stage-section-title">L2 — 阶段内子流程（点击进入详情）</h3>
          <div class="stage-l2-grid">
            ${l2Items
              .map(
                (l2) => `
                  <article class="l2-card" data-l2-view="${l2.name}" style="cursor:pointer;">
                    <strong>${l2.name}</strong>
                    <p>${l2.desc}</p>
                  </article>
                `
              )
              .join("")}
          </div>
        </div>
      </section>
    </div>
  `;
}

/* ============================================
   视图：工作台
   ============================================ */
function renderWorkbench() {
  const stageGroups = groupBy(state.projectData, (project) => project.currentStage);
  const currentUser = users.find((user) => user.id === state.currentUserId) || users[0];

  const myTasks = state.taskData.filter((t) => t.ownerId === state.currentUserId && t.status !== "completed_task" && t.status !== "canceled");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekEnd = new Date(today);
  weekEnd.setDate(weekEnd.getDate() + 7);

  const overdueMyTasks = myTasks.filter((t) => new Date(t.dueDate) < today);
  const todayMyTasks = myTasks.filter((t) => {
    const d = new Date(t.dueDate);
    return d >= today && d < new Date(today.getTime() + 86400000);
  });
  const weekMyTasks = myTasks.filter((t) => {
    const d = new Date(t.dueDate);
    return d >= new Date(today.getTime() + 86400000) && d <= weekEnd;
  });

  const allOpenRisks = state.riskData.filter((r) => r.status !== "closed" && (r.priority === "P0" || r.priority === "P1"));
  const upcomingReviews = state.reviewData.filter((r) => r.status === "open").slice(0, 5);
  const overdueTasks = state.taskData.filter((task) => isTaskOverdue(task));
  const readinessSummaries = state.projectData.map((project) =>
    calculateReadinessSummary(state.readinessData.filter((item) => item.projectId === project.id))
  );
  const avgReadiness = Math.round(readinessSummaries.reduce((sum, item) => sum + item.score, 0) / (readinessSummaries.length || 1));

  const totalMyTasks = myTasks.length;
  const urgentCount = overdueMyTasks.length + todayMyTasks.length;
  const healthWatchProjects = state.projectData.filter((project) => deriveProjectHealth(project, state.taskData, state.riskData, state.readinessData) !== "normal").length;
  const focusMessage = overdueMyTasks.length
    ? `${overdueMyTasks.length} 个超期待办需要先处理`
    : todayMyTasks.length
      ? `${todayMyTasks.length} 个今日到期任务需要推进`
      : allOpenRisks.length
        ? `${allOpenRisks.length} 个高优先级风险需要关注`
        : "今天没有特别紧急的动作，可以关注项目节奏";

  return `
    <div class="wb-page">
      <section class="wb-command-strip">
        <div class="wb-command-copy">
          <p class="eyebrow">Today Command Desk</p>
          <h2>${currentUser?.name || "GTM 用户"}，今天先看这几件事</h2>
          <p>${focusMessage}。工作台按“个人行动、升级决策、项目雷达”组织，让每天的操盘入口更像一张可执行的作战桌。</p>
          <div class="wb-command-actions">
            <button class="primary-action" data-route="tasks">${icon.clipboardList} 进入任务中心</button>
            <button class="ghost-action" data-route="projects">查看项目池</button>
          </div>
        </div>
        <div class="wb-command-stats">
          ${renderWorkbenchStat("我的待办", totalMyTasks, "待处理", "blue")}
          ${renderWorkbenchStat("今日/超期", urgentCount, "优先处理", overdueMyTasks.length ? "red" : "amber")}
          ${renderWorkbenchStat("P0/P1 风险", allOpenRisks.length, "需升级", allOpenRisks.length ? "red" : "green")}
          ${renderWorkbenchStat("待评审", upcomingReviews.length, "近期门禁", upcomingReviews.length ? "purple" : "green")}
        </div>
      </section>

      <section class="wb-top-grid">
        <article class="wb-focus-panel">
          <div class="wb-panel-head">
            <div>
              <p class="eyebrow">Action Queue</p>
              <h3>我的行动队列</h3>
            </div>
            <button class="ghost-action" data-route="tasks">查看全部</button>
          </div>
          <p class="wb-panel-note">先处理超期与今日到期任务，再推进本周关键动作。</p>
          ${renderMyTasksBlock(overdueMyTasks, todayMyTasks, weekMyTasks)}
        </article>

        <aside class="wb-escalation-stack">
          <article class="wb-panel wb-alert-panel">
            <div class="wb-panel-head">
              <div>
                <p class="eyebrow">Escalation</p>
                <h3>升级与风险</h3>
              </div>
              <button class="ghost-action" data-route="risks">进入台账</button>
            </div>
            ${renderHighRiskAlerts(allOpenRisks)}
          </article>
          <article class="wb-panel">
            <div class="wb-panel-head">
              <div>
                <p class="eyebrow">Gate Review</p>
                <h3>近期评审</h3>
              </div>
              <button class="ghost-action" data-route="reviews">查看全部</button>
            </div>
            ${renderUpcomingReviews(upcomingReviews)}
          </article>
        </aside>
      </section>

      <section class="wb-portfolio-panel">
        <div class="wb-panel-head">
          <div>
            <p class="eyebrow">Project Radar</p>
            <h3>重点项目雷达</h3>
          </div>
          <div class="wb-panel-meta">
            <span>${state.projectData.length} 个项目在跑</span>
            <span>${healthWatchProjects} 个需要关注</span>
          </div>
        </div>
        <div class="wb-radar-grid">
          ${state.projectData.map(renderProjectRadarCard).join("")}
        </div>
      </section>

      <section class="wb-lower-grid">
        <article class="wb-panel">
          <div class="wb-panel-head">
            <div>
              <p class="eyebrow">Stage Pulse</p>
              <h3>阶段节奏</h3>
            </div>
            <span class="wb-soft-badge">平均成熟度 ${avgReadiness}%</span>
          </div>
          ${renderStageStack(stageGroups)}
        </article>
        <article class="wb-panel wb-rule-panel">
          <div class="wb-panel-head">
            <div>
              <p class="eyebrow">Operating Rule</p>
              <h3>工作台使用逻辑</h3>
            </div>
          </div>
          <div class="wb-rule-list">
            <div><strong>1</strong><span>先清个人行动：超期、今日到期、本周关键任务。</span></div>
            <div><strong>2</strong><span>再看升级事项：P0/P1 风险、待决策评审。</span></div>
            <div><strong>3</strong><span>最后扫项目雷达：确认哪些项目需要进入单产品操盘。</span></div>
          </div>
        </article>
      </section>
    </div>
  `;
}

function renderWorkbenchStat(label, value, caption, tone = "blue") {
  return `
    <article class="wb-stat ${tone}">
      <span>${label}</span>
      <strong>${value}</strong>
      <small>${caption}</small>
    </article>
  `;
}

function renderWbInsightCard(eyebrow, title, content, actionHtml = "") {
  return `
    <div class="pj-insight-card">
      <div class="pj-insight-head">
        <div>
          <p class="eyebrow">${eyebrow}</p>
          <h3>${title}</h3>
        </div>
        ${actionHtml}
      </div>
      ${content}
    </div>
  `;
}

function renderStageStack(stageGroups) {
  return `
    <div class="wb-stage-stack">
      ${stageOrder.map((stageKey) => {
        const count = stageGroups[stageKey]?.length || 0;
        return `
          <div class="wb-stack-row" data-stage-detail="${stageKey}">
            <span>${stageLabels[stageKey]}</span>
            <strong>${count}</strong>
          </div>
        `;
      }).join("")}
    </div>
  `;
}

function renderProjectRadarCard(project) {
  const projectTasks = state.taskData.filter((task) => task.projectId === project.id);
  const doneTasks = projectTasks.filter((task) => task.status === "completed_task").length;
  const overdue = projectTasks.filter((task) => isTaskOverdue(task)).length;
  const readinessSummary = calculateReadinessSummary(state.readinessData.filter((item) => item.projectId === project.id));
  const openRisks = state.riskData.filter((risk) => risk.projectId === project.id && risk.status !== "closed");
  const p0p1 = openRisks.filter((risk) => risk.priority === "P0" || risk.priority === "P1").length;
  const openReviews = state.reviewData.filter((review) => review.projectId === project.id && review.status === "open");
  const health = deriveProjectHealth(project, state.taskData, state.riskData, state.readinessData);
  const nextAction = openReviews[0]?.title || (openRisks[0]?.title ? `关闭风险：${openRisks[0].title}` : stageGateLabels[project.currentStage]);

  return `
    <article class="project-radar-card" data-open-project="${project.id}">
      <div class="radar-card-head">
        <div>
          <strong>${escapeHtml(project.name)}</strong>
          <small>${escapeHtml(project.productCategory)} · ${escapeHtml(project.targetChannel)}</small>
        </div>
        ${statusBadge(health)}
      </div>
      <div class="radar-stage">
        <span>${stageLabels[project.currentStage]}</span>
        <small>计划上市：${project.plannedLaunchDate}</small>
      </div>
      <div class="radar-metrics">
        <div><b>${doneTasks}/${projectTasks.length || 0}</b><span>任务</span></div>
        <div><b>${readinessSummary.score}%</b><span>成熟度</span></div>
        <div><b>${p0p1}</b><span>P0/P1风险</span></div>
        <div><b>${openReviews.length}</b><span>待评审</span></div>
      </div>
      <div class="radar-next">
        <span>下一动作</span>
        <p>${escapeHtml(nextAction)}</p>
      </div>
      ${overdue ? `<div class="radar-warning">${overdue} 个任务已超期</div>` : ""}
    </article>
  `;
}

function renderMyTasksBlock(overdue, today, week) {
  const groups = [];
  if (overdue.length) groups.push({ label: "已超期", items: overdue, cls: "overdue" });
  if (today.length) groups.push({ label: "今日到期", items: today, cls: "today" });
  if (week.length) groups.push({ label: "本周到期", items: week, cls: "upcoming" });

  if (!groups.length) return emptyState("暂无待办", "当前没有分配给你的未完成任务");

  return groups.map((g) => `
    <div class="my-tasks-group">
      <h3>${g.label} (${g.items.length})</h3>
      ${g.items.map((task) => `
        <div class="my-task-item" data-select-project="${task.projectId}">
          <div class="task-meta">
            <strong>${escapeHtml(task.title)}</strong>
            <small>${projectName(task.projectId)} · ${task.domain}</small>
          </div>
          <span class="task-due ${g.cls}">${task.dueDate}</span>
        </div>
      `).join("")}
    </div>
  `).join("");
}

function renderHighRiskAlerts(risks) {
  if (!risks.length) return emptyState("暂无高风险", "当前无 P0/P1 级别风险");
  return `<div class="risk-list">${risks.map((risk) => {
    const daysOpen = Math.floor((Date.now() - new Date(risk.createdAt || risk.dueDate).getTime()) / 86400000);
    const escalated = risk.priority === "P0" || daysOpen > 7;
    return `
      <div class="risk-alert-card ${risk.priority === "P1" ? "p1-alert" : ""}">
        <div>${priorityBadge(risk.priority)}${escalated ? ' <span class="escalated">升级</span>' : ""}</div>
        <div class="risk-alert-body">
          <strong>${escapeHtml(risk.title)}</strong>
          <small>${projectName(risk.projectId)} · ${userName(risk.ownerId)} · ${risk.domain}</small>
        </div>
      </div>
    `;
  }).join("")}</div>`;
}

function renderUpcomingReviews(reviews) {
  if (!reviews.length) return emptyState("暂无待处理评审", "当前没有待召开的评审会议");
  return reviews.map((review) => `
    <div class="upcoming-review-item" data-select-project="${review.projectId}">
      <div class="review-meta">
        <strong>${escapeHtml(review.title)}</strong>
        <small>${projectName(review.projectId)} · ${stageLabels[review.stageKey]} · ${review.scheduledAt}</small>
      </div>
      ${statusBadge(review.decision)}
    </div>
  `).join("");
}

function metricCard(label, value, caption) {
  return `
    <article class="metric-card">
      <span>${label}</span>
      <strong>${value}</strong>
      <small>${caption}</small>
    </article>
  `;
}

function emptyState(title, desc) {
  return `<div class="empty-state"><strong>${title}</strong><p>${desc}</p></div>`;
}

/* ============================================
   视图：项目详情（全新设计）
   ============================================ */
function renderProjectDetail() {
  const project = state.projectData.find((entry) => entry.id === state.selectedProjectId);
  const projectTasks = state.taskData.filter((task) => task.projectId === project.id);
  const projectReadiness = state.readinessData.filter((item) => item.projectId === project.id);
  const projectReviews = state.reviewData.filter((review) => review.projectId === project.id);
  const projectRisks = state.riskData.filter((risk) => risk.projectId === project.id);
  const health = deriveProjectHealth(project, state.taskData, state.riskData, state.readinessData);
  const readinessSummary = calculateReadinessSummary(projectReadiness);
  const moveCheck = canMoveToNextStage(project, state.reviewData, state.readinessData);
  const next = nextStageKey(project.currentStage);
  const currentIdx = stageOrder.indexOf(project.currentStage);
  const currentStageL2 = introL2Flows[currentIdx];
  const openRisks = projectRisks.filter((r) => r.status !== "closed");
  const overdueProjectTasks = projectTasks.filter((t) => isTaskOverdue(t));
  const currentStageTasks = projectTasks.filter((t) => t.stageKey === project.currentStage);

  const daysToLaunch = Math.ceil((new Date(project.plannedLaunchDate) - new Date()) / 86400000);
  const countdownText = daysToLaunch > 0 ? `距上市 ${daysToLaunch} 天` : daysToLaunch === 0 ? "今日上市" : `超期 ${Math.abs(daysToLaunch)} 天`;
  const countdownColor = daysToLaunch > 30 ? "#059669" : daysToLaunch > 7 ? "#b45309" : "#dc2626";

  const todoTasks = projectTasks.filter((t) => t.status === "not_started");
  const doingTasks = projectTasks.filter((t) => t.status === "in_progress" || t.status === "pending_review");
  const doneTasks = projectTasks.filter((t) => t.status === "completed_task");
  const taskProgress = projectTasks.length ? Math.round((doneTasks.length / projectTasks.length) * 100) : 0;

  const healthColor = health === "normal" ? "#059669" : health === "at_risk" ? "#b45309" : "#dc2626";
  const healthBg = health === "normal" ? "#f0fdf4" : health === "at_risk" ? "#fffbeb" : "#fef2f2";
  const healthLabel = health === "normal" ? "健康" : health === "at_risk" ? "有风险" : "延期/阻塞";

  const closedReviews = projectReviews.filter((r) => r.status === "closed");
  const openReviews = projectReviews.filter((r) => r.status === "open");
  const p0Risks = openRisks.filter((r) => r.priority === "P0");
  const p1Risks = openRisks.filter((r) => r.priority === "P1");
  const nextAction = openReviews[0]?.title || openRisks[0]?.title || (next ? `推进至${stageLabels[next]}` : "进入经营复盘");
  const launchTone = daysToLaunch > 30 ? "normal" : daysToLaunch > 7 ? "warning" : "critical";
  const detailTab = state.projectDetailTab || "overview";

  return `
    <div class="pd-page">
    <!-- 项目标题区 -->
    <div class="pd-header pd-brief-shell">
      <div class="pd-header-main">
        <p class="eyebrow">Single Product War Room</p>
        <h1>${escapeHtml(project.name)}</h1>
        <p>${escapeHtml(project.commercialGoal)}</p>
        <div class="pd-hero-tags">
          <span>${escapeHtml(project.productCategory)}</span>
          <span>${escapeHtml(project.targetChannel)}</span>
          <span>${stageLabels[project.currentStage]}</span>
        </div>
      </div>
      <div class="pd-header-actions">
        <div class="pd-countdown ${launchTone}" style="color:${countdownColor}">
          <span class="pd-countdown-num">${daysToLaunch > 0 ? daysToLaunch : Math.abs(daysToLaunch)}</span>
          <span class="pd-countdown-label">${countdownText}</span>
        </div>
        <span class="pd-health" style="color:${healthColor};background:${healthBg}">${healthLabel}</span>
        <button class="primary-action" id="move-stage" ${moveCheck.allowed ? "" : "disabled"}>${moveCheck.allowed ? icon.playCircle + " 推进至 " + stageLabels[next] : "暂不能流转"}</button>
        <button class="danger-action" id="delete-project-btn" title="移除项目">${icon.trash}</button>
      </div>
    </div>

    <!-- 元信息 -->
    <div class="pd-meta">
      <span><b>品类</b>${escapeHtml(project.productCategory)}</span>
      <span class="pd-meta-dot"></span>
      <span><b>渠道</b>${escapeHtml(project.targetChannel)}</span>
      <span class="pd-meta-dot"></span>
      <span><b>上市</b>${project.plannedLaunchDate}</span>
      <span class="pd-meta-dot"></span>
      <span><b>GTM</b>${userName(project.gtmOwnerId)}</span>
      <span class="pd-meta-dot"></span>
      <span><b>产品</b>${userName(project.productOwnerId)}</span>
      <span class="pd-meta-dot"></span>
      <span><b>阶段</b>${stageLabels[project.currentStage]}</span>
    </div>

    <div class="pd-detail-tabs">
      <button class="${detailTab === "overview" ? "active" : ""}" data-project-detail-tab="overview">总览</button>
      <button class="${detailTab === "execution" ? "active" : ""}" data-project-detail-tab="execution">执行</button>
      <button class="${detailTab === "gates" ? "active" : ""}" data-project-detail-tab="gates">门禁</button>
      <button class="${detailTab === "risks" ? "active" : ""}" data-project-detail-tab="risks">风险</button>
    </div>

    <div id="project-detail-dynamic">
    ${detailTab === "overview" ? `
      <div class="pd-action-strip">
        <div class="pd-next-action">
          <span>下一关键动作</span>
          <strong>${escapeHtml(nextAction)}</strong>
          <small>${moveCheck.allowed ? "阶段门满足，可推进下一阶段" : escapeHtml(moveCheck.reason)}</small>
        </div>
        <button class="ghost-action" data-route="countdown">上市倒排</button>
        <button class="ghost-action" data-route="readiness">成熟度检查</button>
        <button class="ghost-action" data-route="risks">风险台账</button>
        <button class="primary-action" id="new-task-btn-detail">${icon.plus} 新建任务</button>
      </div>

      <div class="pd-timeline-wrap">
        ${renderStageTimeline(project)}
      </div>

      <div class="pd-kpi-bar">
        <div class="pd-kpi-item">
          <span class="pd-kpi-label">任务</span>
          <span class="pd-kpi-value">${doneTasks.length}<small>/${projectTasks.length}</small></span>
          <div class="pd-kpi-track"><div class="pd-kpi-fill" style="width:${taskProgress}%"></div></div>
        </div>
        <div class="pd-kpi-item">
          <span class="pd-kpi-label">成熟度</span>
          <span class="pd-kpi-value">${readinessSummary.score}<small>%</small></span>
          <div class="pd-kpi-track"><div class="pd-kpi-fill ${readinessSummary.score >= 80 ? "green" : readinessSummary.score >= 50 ? "amber" : "red"}" style="width:${readinessSummary.score}%"></div></div>
        </div>
        <div class="pd-kpi-item">
          <span class="pd-kpi-label">评审</span>
          <span class="pd-kpi-value">${closedReviews.length}<small>/${projectReviews.length}</small></span>
          <div class="pd-kpi-track"><div class="pd-kpi-fill" style="width:${projectReviews.length ? Math.round((closedReviews.length/projectReviews.length)*100) : 0}%"></div></div>
        </div>
        <div class="pd-kpi-item">
          <span class="pd-kpi-label">风险</span>
          <span class="pd-kpi-value" style="color:${openRisks.length ? '#dc2626' : '#059669'}">${openRisks.length}</span>
          <span class="pd-kpi-sublabel">${p0Risks.length ? p0Risks.length + ' P0' : p1Risks.length ? p1Risks.length + ' P1' : '无风险'}</span>
        </div>
      </div>

      <div class="pd-overview-grid">
        <div class="pd-sidebar-card">
          <div class="pd-sidebar-title">当前阶段</div>
          <div class="pd-current-stage">${stageLabels[project.currentStage]}</div>
          ${currentStageL2 ? `
            <div class="pd-sidebar-row"><span class="pd-sidebar-key">L2 动作</span><span class="pd-sidebar-val">${currentStageL2[1]}</span></div>
            <div class="pd-sidebar-row"><span class="pd-sidebar-key">核心交付物</span><span class="pd-sidebar-val">${currentStageL2[2]}</span></div>
          ` : ""}
          ${currentStageTasks.length ? `<div class="pd-sidebar-row"><span class="pd-sidebar-key">任务进度</span><span class="pd-sidebar-val">${currentStageTasks.filter((t) => t.status === "completed_task").length}/${currentStageTasks.length} 完成</span></div>` : ""}
        </div>
        <div class="pd-sidebar-card">
          <div class="pd-sidebar-title">本项目提醒</div>
          <div class="pd-sidebar-row"><span class="pd-sidebar-key">超期任务</span><span class="pd-sidebar-val">${overdueProjectTasks.length}</span></div>
          <div class="pd-sidebar-row"><span class="pd-sidebar-key">待评审</span><span class="pd-sidebar-val">${openReviews.length}</span></div>
          <div class="pd-sidebar-row"><span class="pd-sidebar-key">开放风险</span><span class="pd-sidebar-val">${openRisks.length}</span></div>
        </div>
      </div>
    ` : ""}

    ${detailTab === "execution" ? `
      <div class="pd-kanban-area">
        <div class="pd-section-header">
          <div>
            <p class="eyebrow">Execution Board</p>
            <h3>操盘任务看板</h3>
          </div>
          <div class="pd-section-actions">
            <button class="ghost-action" id="apply-task-template">套用模板</button>
          </div>
        </div>
        ${renderKanbanBoard(projectTasks)}

        <div class="pd-log-section">
          <div class="pd-section-header">
            <div>
              <p class="eyebrow">Operating Log</p>
              <h3>阶段流转记录</h3>
            </div>
          </div>
          ${renderStageHistory(project.id)}
        </div>
      </div>
    ` : ""}

    ${detailTab === "gates" ? `
      <div class="pd-governance-grid">
        <div class="pd-sidebar-card">
          <div class="pd-sidebar-header">
            <span class="pd-sidebar-title">上市成熟度</span>
            <button class="ghost-action" data-route="readiness" style="font-size:11px;padding:2px 8px;">详情</button>
          </div>
          <div class="pd-gauge-row">
            <div class="pd-gauge-ring">
              <svg viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#f1f5f9" stroke-width="10"/>
                <circle cx="50" cy="50" r="42" fill="none" stroke="${readinessSummary.score >= 80 ? '#10b981' : readinessSummary.score >= 50 ? '#f59e0b' : '#ef4444'}" stroke-width="10" stroke-linecap="round"
                  stroke-dasharray="${Math.round(2 * Math.PI * 42)}"
                  stroke-dashoffset="${Math.round(2 * Math.PI * 42 * (1 - readinessSummary.score / 100))}" />
              </svg>
              <div class="pd-gauge-text"><strong>${readinessSummary.score}%</strong></div>
            </div>
            <div class="pd-gauge-list">
              ${projectReadiness.slice(0, 4).map((item) => `
                <div class="pd-gauge-item ${item.status === "ready" ? "ready" : ""}">
                  <span class="pd-gauge-dot" style="background:${item.status === 'ready' ? '#10b981' : item.status === 'blocked' ? '#ef4444' : '#f59e0b'}"></span>
                  <span>${item.dimension}</span>
                </div>
              `).join("")}
            </div>
          </div>
        </div>

        <div class="pd-sidebar-card">
          <div class="pd-sidebar-header">
            <span class="pd-sidebar-title">评审</span>
            <div style="display:flex;gap:4px;">
              <button class="ghost-action" id="new-review-btn-detail" style="font-size:11px;padding:2px 8px;">${icon.plus}</button>
              <button class="primary-action" id="decide-review-btn" style="font-size:11px;padding:2px 8px;">处理</button>
            </div>
          </div>
          ${projectReviews.length
            ? `<div class="pd-review-list">${projectReviews.slice(0, 4).map((review) => {
                const relatedTasks = state.taskData.filter((t) => t.sourceReviewId === review.id);
                const relatedDone = relatedTasks.filter((t) => t.status === "completed_task").length;
                return `
                <div class="pd-review-item">
                  <div class="pd-review-title">
                    <span>${escapeHtml(review.title)}</span>
                    <span class="pd-review-status ${review.status === 'closed' ? 'closed' : 'open'}">${review.status === 'closed' ? icon.check : icon.clock}</span>
                  </div>
                  <div class="pd-review-meta">${stageLabels[review.stageKey]} · ${review.scheduledAt}${review.decision && review.decision !== "pending" ? ` · ${statusLabels[review.decision] || review.decision}` : ""}</div>
                  ${relatedTasks.length ? `<div class="pd-review-meta" style="color:var(--accent);">关联待办 ${relatedDone}/${relatedTasks.length} 已完成</div>` : ""}
                </div>`;
              }).join("")}</div>
              ${projectReviews.length > 4 ? `<div class="pd-more">+${projectReviews.length - 4} 项</div>` : ""}`
            : `<div class="pd-allclear">暂无评审记录</div>`
          }
        </div>
        <div class="pd-sidebar-card">
          <div class="pd-sidebar-title">阶段流转记录</div>
          ${renderStageHistory(project.id)}
        </div>
      </div>
    ` : ""}
    ${detailTab === "risks" ? `
      <div class="pd-governance-grid">
        <div class="pd-sidebar-card">
          <div class="pd-sidebar-header">
            <span class="pd-sidebar-title">风险清单</span>
            <button class="ghost-action" id="quick-risk-panel" style="font-size:11px;padding:2px 8px;">${icon.plus}</button>
          </div>
          ${openRisks.length
            ? `<div class="pd-risk-list">${openRisks.map((risk) => `
                <div class="pd-risk-item">
                  <div class="pd-risk-title">
                    <span class="pd-risk-priority ${risk.priority === 'P0' ? 'p0' : risk.priority === 'P1' ? 'p1' : 'p2'}">${risk.priority}</span>
                    <span>${escapeHtml(risk.title)}</span>
                  </div>
                  <div class="pd-risk-meta">${risk.domain} · ${userName(risk.ownerId)} · ${risk.dueDate}</div>
                </div>
              `).join("")}</div>`
            : `<div class="pd-allclear">${icon.checkCircle} 无开放风险</div>`
          }
        </div>
        <div class="pd-sidebar-card">
          <div class="pd-sidebar-title">风险处置原则</div>
          <div class="pd-sidebar-row"><span class="pd-sidebar-key">P0/P1</span><span class="pd-sidebar-val">进入周度操盘会升级</span></div>
          <div class="pd-sidebar-row"><span class="pd-sidebar-key">跨部门</span><span class="pd-sidebar-val">明确责任人与协同部门</span></div>
          <div class="pd-sidebar-row"><span class="pd-sidebar-key">关闭标准</span><span class="pd-sidebar-val">有证据、有结论、有复盘</span></div>
        </div>
      </div>
    ` : ""}
    </div>
    </div>
  `;
}

function renderStageHistory(projectId) {
  const history = state.stageHistory.filter((h) => h.projectId === projectId);
  if (!history.length) return emptyState("暂无流转记录", "项目尚未发生阶段流转");
  return `
    <div class="history-timeline">
      ${history.map((h) => `
        <div class="history-entry">
          <div class="history-content">
            <strong>${escapeHtml(h.note)}</strong>
            <small>${userName(h.operatorId)} · ${new Date(h.timestamp).toLocaleString("zh-CN")}</small>
          </div>
        </div>
      `).join("")}
    </div>
  `;
}

function projectStageIcon(stageKey) {
  const map = {
    planning_initiation: icon.target,
    expansion_preparation: icon.package,
    market_expansion: icon.store,
    launch_preparation: icon.shieldCheck,
    launch_sales: icon.trendingUp,
    stable_sales: icon.activity,
    phase_out: icon.flag,
  };
  return map[stageKey] || icon.circle;
}

function renderStageTimeline(project) {
  const moveCheck = canMoveToNextStage(project, state.reviewData, state.readinessData);
  const nxt = nextStageKey(project.currentStage);
  const currentIdx = stageOrder.indexOf(project.currentStage);
  const selectedDetail = state.projectDetailStage;

  return `
    <div class="timeline">
      ${stageOrder
        .map((stageKey, idx) => {
          const active = stageKey === project.currentStage;
          const done = idx < currentIdx;
          const future = idx > currentIdx;
          const selected = stageKey === selectedDetail;
          const clickable = done || active;
          return `
            <div class="stage-node ${active ? "active" : ""} ${done ? "done" : ""} ${future ? "future" : ""} ${selected ? "selected" : ""}" ${clickable ? `data-project-stage-detail="${stageKey}"` : ""}>
              <b class="stage-node-icon">${projectStageIcon(stageKey)}</b>
              <span>${stageLabels[stageKey]}</span>
              <small>${stageGateLabels[stageKey]}</small>
              ${done ? '<small style="color:var(--green);">已完成</small>' : ""}
            </div>
          `;
        })
        .join("")}
    </div>
    <div class="gate-callout">
      <div>
        <strong>${nxt ? "下一阶段：" + stageLabels[nxt] : "已是最后阶段"}</strong>
        <span>${moveCheck.reason}</span>
      </div>
    </div>
    ${selectedDetail ? renderStageDetailPanel(project, selectedDetail) : ""}
  `;
}

function renderStageDetailPanel(project, stageKey) {
  const stageTasks = state.taskData.filter((t) => t.projectId === project.id && t.stageKey === stageKey);
  const stageReviews = state.reviewData.filter((r) => r.projectId === project.id && r.stageKey === stageKey);
  const stageRisks = state.riskData.filter((r) => r.projectId === project.id && r.stageKey === stageKey);
  const stageHistoryEntries = state.stageHistory.filter((h) => h.projectId === project.id && (h.fromStage === stageKey || h.toStage === stageKey));
  const stageIdx = stageOrder.indexOf(stageKey);
  const stageL2 = introL2Flows[stageIdx];
  const isCurrent = stageKey === project.currentStage;

  const doneTasks = stageTasks.filter((t) => t.status === "completed_task").length;
  const closedRisks = stageRisks.filter((r) => r.status === "closed").length;
  const closedReviews = stageReviews.filter((r) => r.status === "closed").length;

  return `
    <div class="stage-detail-panel">
      <div class="stage-detail-panel-header">
        <div>
          <h3>${stageLabels[stageKey]}${isCurrent ? "（当前阶段）" : ""}</h3>
          <p>${stageGateLabels[stageKey]}</p>
        </div>
        <button class="ghost-action" data-clear-stage-detail>关闭</button>
      </div>

      ${stageL2 ? `
        <div class="stage-detail-section">
          <h4>L2 流程动作与交付物</h4>
          <div class="detail-row"><b>动作：</b>${stageL2[1]}</div>
          <div class="detail-row"><b>交付物：</b>${stageL2[2]}</div>
        </div>
      ` : ""}

      <div class="stage-detail-section">
        <h4>阶段流转记录</h4>
        ${stageHistoryEntries.length
          ? `<div class="history-timeline">${stageHistoryEntries.map((h) => `
              <div class="history-entry">
                <div class="history-content">
                  <strong>${escapeHtml(h.note)}</strong>
                  <small>${userName(h.operatorId)} · ${new Date(h.timestamp).toLocaleString("zh-CN")}</small>
                </div>
              </div>
            `).join("")}</div>`
          : '<p class="text-muted">暂无流转记录</p>'
        }
      </div>

      <div class="stage-detail-columns">
        <div class="stage-detail-section">
          <h4>任务 (${doneTasks}/${stageTasks.length} 完成)</h4>
          ${stageTasks.length
            ? `<div class="compact-list">${stageTasks.map((t) => `
                <article>
                  <div style="display:flex;justify-content:space-between;align-items:center;">
                    <strong>${escapeHtml(t.title)}</strong>
                    ${statusBadge(t.status)}
                  </div>
                  <span>${t.domain} · ${userName(t.ownerId)} · 截止 ${t.dueDate}${isTaskOverdue(t) ? ' <span class="danger-text">已超期</span>' : ""}</span>
                </article>
              `).join("")}</div>`
            : '<p class="text-muted">暂无任务</p>'
          }
        </div>

        <div class="stage-detail-section">
          <h4>评审 (${closedReviews}/${stageReviews.length} 已关闭)</h4>
          ${stageReviews.length
            ? `<div class="compact-list">${stageReviews.map((r) => `
                <article>
                  <div style="display:flex;justify-content:space-between;align-items:center;">
                    <strong>${escapeHtml(r.title)}</strong>
                    ${statusBadge(r.decision)}
                  </div>
                  <span>${r.scheduledAt} · ${r.status === "closed" ? "已关闭" : "待处理"}</span>
                  ${r.decisionNote ? `<p style="margin-top:4px;font-size:12px;color:var(--text-secondary);">${escapeHtml(r.decisionNote)}</p>` : ""}
                </article>
              `).join("")}</div>`
            : '<p class="text-muted">暂无评审</p>'
          }
        </div>

        <div class="stage-detail-section">
          <h4>风险 (${closedRisks}/${stageRisks.length} 已关闭)</h4>
          ${stageRisks.length
            ? `<div class="compact-list">${stageRisks.map((r) => `
                <article style="border-left:3px solid ${r.priority === 'P0' ? 'var(--red)' : r.priority === 'P1' ? 'var(--amber)' : 'var(--line)'};padding-left:10px;">
                  <div style="display:flex;justify-content:space-between;align-items:center;">
                    <strong>${escapeHtml(r.title)}</strong>
                    <div style="display:flex;gap:4px;align-items:center;">
                      ${priorityBadge(r.priority)}
                      ${statusBadge(r.status)}
                      ${r.escalationFlag ? '<span class="escalated">升级</span>' : ""}
                    </div>
                  </div>
                  <span>${r.domain} · ${userName(r.ownerId)} · ${r.dueDate}</span>
                  ${r.description ? `<p style="margin-top:4px;font-size:12px;color:var(--text-secondary);">${escapeHtml(r.description)}</p>` : ""}
                </article>
              `).join("")}</div>`
            : '<p class="text-muted">暂无风险</p>'
          }
        </div>
      </div>
    </div>
  `;
}

/* ============================================
   视图：项目池
   ============================================ */
function renderProjects() {
  let filtered = state.projectData;
  if (state.projectStageFilter !== "all") filtered = filtered.filter((p) => p.currentStage === state.projectStageFilter);
  if (state.projectStatusFilter !== "all") {
    filtered = filtered.filter((p) => {
      const health = deriveProjectHealth(p, state.taskData, state.riskData, state.readinessData);
      return health === state.projectStatusFilter;
    });
  }
  if (state.projectCategoryFilter !== "all") filtered = filtered.filter((p) => p.productCategory === state.projectCategoryFilter);

  const categories = [...new Set(state.projectData.map((p) => p.productCategory))];
  const viewMode = state.projectViewMode || "card";

  /* === KPI 计算 === */
  const totalProjects = state.projectData.length;
  const healthyProjects = state.projectData.filter((p) => {
    const h = deriveProjectHealth(p, state.taskData, state.riskData, state.readinessData);
    return h === "healthy" || h === "normal";
  }).length;
  const upcomingLaunch = state.projectData.filter((p) => {
    const days = Math.ceil((new Date(p.plannedLaunchDate) - new Date()) / 86400000);
    return days > 0 && days <= 30;
  }).length;
  const totalOpenRisks = state.riskData.filter((r) => r.status !== "closed").length;
  const p0p1Risks = state.riskData.filter((r) => r.status !== "closed" && (r.priority === "P0" || r.priority === "P1")).length;

  /* === 洞察数据 === */
  const stageGroups = groupBy(state.projectData, (p) => p.currentStage);
  const stageMax = Math.max(...stageOrder.map((k) => stageGroups[k]?.length || 0), 1);

  const highRiskProjects = state.projectData
    .map((p) => {
      const risks = state.riskData.filter((r) => r.projectId === p.id && r.status !== "closed");
      const p0p1 = risks.filter((r) => r.priority === "P0" || r.priority === "P1");
      return { project: p, riskCount: risks.length, p0p1Count: p0p1.length };
    })
    .filter((p) => p.riskCount > 0)
    .sort((a, b) => b.p0p1Count - a.p0p1Count || b.riskCount - a.riskCount)
    .slice(0, 5);

  const launchingSoon = state.projectData
    .map((p) => {
      const days = Math.ceil((new Date(p.plannedLaunchDate) - new Date()) / 86400000);
      return { project: p, days };
    })
    .filter((p) => p.days > 0 && p.days <= 60)
    .sort((a, b) => a.days - b.days)
    .slice(0, 5);

  return `
    <!-- Hero -->
    <section class="pj-hero">
      <div class="pj-hero-text">
        <p class="eyebrow">GTM PROJECT PORTFOLIO</p>
        <h2>GTM 产品项目池</h2>
        <p>管理全部产品上市项目，跟踪阶段推进、风险状态和上市节奏。</p>
      </div>
      <div class="pj-hero-stat">
        <strong>${totalProjects}</strong>
        <span>在跑项目</span>
      </div>
    </section>

    <!-- KPI -->
    <section class="pj-kpi-grid">
      <div class="pj-kpi-card">
        <span>在跑项目</span>
        <strong>${totalProjects}</strong>
        <small>${filtered.length} 个符合筛选</small>
      </div>
      <div class="pj-kpi-card green">
        <span>健康项目</span>
        <strong>${healthyProjects}</strong>
        <small>${totalProjects ? Math.round((healthyProjects / totalProjects) * 100) : 0}% 正常</small>
      </div>
      <div class="pj-kpi-card amber">
        <span>即将上市</span>
        <strong>${upcomingLaunch}</strong>
        <small>30天内计划上市</small>
      </div>
      <div class="pj-kpi-card ${p0p1Risks ? 'red' : 'green'}">
        <span>开放风险</span>
        <strong>${totalOpenRisks}</strong>
        <small>${p0p1Risks ? p0p1Risks + ' 个 P0/P1' : '无高风险'}</small>
      </div>
    </section>

    <!-- 筛选工具栏 -->
    <section class="pj-toolbar">
      <div class="pj-filters">
        <select id="project-stage-filter" aria-label="阶段筛选">
          <option value="all">全部阶段</option>
          ${stageOrder.map((k) => `<option value="${k}" ${state.projectStageFilter === k ? "selected" : ""}>${stageLabels[k]}</option>`).join("")}
        </select>
        <select id="project-status-filter" aria-label="状态筛选">
          <option value="all">全部状态</option>
          <option value="normal" ${state.projectStatusFilter === "normal" ? "selected" : ""}>正常</option>
          <option value="at_risk" ${state.projectStatusFilter === "at_risk" ? "selected" : ""}>风险</option>
          <option value="delayed" ${state.projectStatusFilter === "delayed" ? "selected" : ""}>延期</option>
        </select>
        <select id="project-category-filter" aria-label="品类筛选">
          <option value="all">全部品类</option>
          ${categories.map((c) => `<option value="${c}" ${state.projectCategoryFilter === c ? "selected" : ""}>${c}</option>`).join("")}
        </select>
      </div>
      <div class="pj-toolbar-actions">
        <button class="pj-view-btn ${viewMode === 'card' ? 'active' : ''}" data-project-view="card">${icon.layout} 卡片</button>
        <button class="pj-view-btn ${viewMode === 'list' ? 'active' : ''}" data-project-view="list">${icon.list} 列表</button>
        <button class="primary-action" id="create-demo-project">${icon.plus} 新增</button>
      </div>
    </section>

    <!-- 主内容 -->
    <div class="pj-main-layout">
      <div class="pj-content">
        ${viewMode === "card" ? renderProjectCards(filtered) : renderProjectList(filtered)}
      </div>
      <div class="pj-insight">
        ${renderProjectInsight(stageGroups, stageMax, highRiskProjects, launchingSoon)}
      </div>
    </div>
  `;
}

function renderProjectCards(projects) {
  if (!projects.length) return emptyState("暂无符合条件的项目", "尝试调整筛选条件");
  return `
    <div class="pj-grid">
      ${projects.map((project) => {
        const health = deriveProjectHealth(project, state.taskData, state.riskData, state.readinessData);
        const riskCount = state.riskData.filter((r) => r.projectId === project.id && r.status !== "closed").length;
        const projectTasks = state.taskData.filter((t) => t.projectId === project.id);
        const doneTasks = projectTasks.filter((t) => t.status === "completed_task").length;
        const projectProgress = projectTasks.length ? Math.round((doneTasks / projectTasks.length) * 100) : 0;
        const readiness = calculateReadinessSummary(state.readinessData.filter((item) => item.projectId === project.id));
        const daysToLaunch = Math.ceil((new Date(project.plannedLaunchDate) - new Date()) / 86400000);
        return `
          <article class="pj-card ${state.selectedProjectId === project.id ? 'selected' : ''}" data-select-project="${project.id}">
            <div class="pj-card-top">
              <strong>${escapeHtml(project.name)}</strong>
              ${statusBadge(health)}
            </div>
            <div class="pj-card-goal">${escapeHtml(project.commercialGoal)}</div>
            <div class="pj-card-meta">
              <span>${stageLabels[project.currentStage]}</span>
              <span>${escapeHtml(project.targetChannel)}</span>
              <span>${escapeHtml(project.productCategory)}</span>
            </div>
            <div class="pj-card-bars">
              <p><b>任务</b><i><em style="width:${projectProgress}%"></em></i><small>${projectProgress}%</small></p>
              <p><b>成熟</b><i><em style="width:${readiness.score}%;background:${readiness.score >= 80 ? '#10b981' : readiness.score >= 50 ? '#f59e0b' : '#ef4444'}"></em></i><small>${readiness.score}%</small></p>
            </div>
            <footer>
              <span>${riskCount > 0 ? `<span style="color:#dc2626;font-weight:600;">${riskCount} 风险</span>` : "<span style='color:#059669;'>无风险</span>"}</span>
              <span>${userName(project.gtmOwnerId)}</span>
            </footer>
            <div class="pj-card-launch">
              <span>${icon.calendar} ${project.plannedLaunchDate}</span>
              <span class="${daysToLaunch <= 14 && daysToLaunch >= 0 ? 'urgent' : daysToLaunch < 0 ? 'overdue' : ''}">${daysToLaunch > 0 ? daysToLaunch + ' 天后上市' : daysToLaunch === 0 ? '今日上市' : '已超期 ' + Math.abs(daysToLaunch) + ' 天'}</span>
            </div>
            <button class="pj-card-link" data-open-project="${project.id}">打开详情 →</button>
          </article>
        `;
      }).join("")}
    </div>
  `;
}

function renderProjectList(projects) {
  if (!projects.length) return emptyState("暂无符合条件的项目", "尝试调整筛选条件");
  return `
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>项目</th><th>品类</th><th>目标渠道</th><th>当前阶段</th>
            <th>状态</th><th>风险数</th><th>GTM负责人</th><th>计划上市</th>
          </tr>
        </thead>
        <tbody>
          ${projects.map((project) => {
            const health = deriveProjectHealth(project, state.taskData, state.riskData, state.readinessData);
            const riskCount = state.riskData.filter((r) => r.projectId === project.id && r.status !== "closed").length;
            return `
              <tr class="clickable" data-select-project="${project.id}">
                <td><strong>${escapeHtml(project.name)}</strong><small>${escapeHtml(project.commercialGoal)}</small><button class="link-action" data-open-project="${project.id}">打开详情</button></td>
                <td>${escapeHtml(project.productCategory)}</td>
                <td>${escapeHtml(project.targetChannel)}</td>
                <td>${stageLabels[project.currentStage]}</td>
                <td>${statusBadge(health)}</td>
                <td>${riskCount > 0 ? `<span class="danger-text">${riskCount}</span>` : "0"}</td>
                <td>${userName(project.gtmOwnerId)}</td>
                <td>${project.plannedLaunchDate}</td>
              </tr>
            `;
          }).join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderProjectInsight(stageGroups, stageMax, highRiskProjects, launchingSoon) {
  return `
    <div class="pj-insight-card">
      <div class="pj-insight-head">
        <p class="eyebrow">Stage Distribution</p>
        <h3>阶段分布</h3>
      </div>
      <div class="pj-stage-mini">
        ${stageOrder.map((stageKey) => {
          const count = stageGroups[stageKey]?.length || 0;
          const height = Math.max(4, Math.round((count / stageMax) * 100));
          return `
            <div class="pj-stage-mini-col">
              <div class="pj-stage-mini-bar" style="--h:${height}%;"></div>
              <strong>${count}</strong>
              <span>${stageLabels[stageKey]}</span>
            </div>
          `;
        }).join("")}
      </div>
    </div>

    <div class="pj-insight-card">
      <div class="pj-insight-head">
        <p class="eyebrow">Risk Alert</p>
        <h3>高风险项目</h3>
      </div>
      ${highRiskProjects.length
        ? `<div class="pj-insight-list">${highRiskProjects.map(({ project, riskCount, p0p1Count }) => `
            <div class="pj-insight-item ${p0p1Count > 0 ? 'hot' : ''}" data-select-project="${project.id}">
              <div>
                <strong>${escapeHtml(project.name)}</strong>
                <span>${p0p1Count > 0 ? p0p1Count + ' P0/P1' : riskCount + ' 风险'}</span>
              </div>
              <span>${stageLabels[project.currentStage]}</span>
            </div>
          `).join("")}</div>`
        : `<div class="pj-insight-empty">${icon.shieldCheck} 当前无高风险项目</div>`
      }
    </div>

    <div class="pj-insight-card">
      <div class="pj-insight-head">
        <p class="eyebrow">Launch Window</p>
        <h3>即将上市</h3>
      </div>
      ${launchingSoon.length
        ? `<div class="pj-insight-list">${launchingSoon.map(({ project, days }) => `
            <div class="pj-insight-item" data-select-project="${project.id}">
              <div>
                <strong>${escapeHtml(project.name)}</strong>
                <span class="${days <= 14 ? 'urgent' : ''}">${days} 天后上市</span>
              </div>
              <span>${stageLabels[project.currentStage]}</span>
            </div>
          `).join("")}</div>`
        : `<div class="pj-insight-empty">${icon.calendar} 未来60天内无计划上市</div>`
      }
    </div>
  `;
}

/* ============================================
   视图：上市倒排
   ============================================ */
function renderCountdown() {
  const selected = state.projectData.find((project) => project.id === state.selectedProjectId);
  const flatItems = countdownPlan.flatMap((week) => week.items.map((item) => ({ ...item, week: week.week })));
  const domainGroups = groupBy(flatItems, (item) => item.domain);
  const totalActions = flatItems.length;
  const domains = Object.keys(domainGroups);
  const projectTasks = state.taskData.filter((task) => task.projectId === selected?.id);
  const projectRisks = state.riskData.filter((risk) => risk.projectId === selected?.id && risk.status !== "closed");
  const projectReadiness = state.readinessData.filter((item) => item.projectId === selected?.id);
  const readinessSummary = calculateReadinessSummary(projectReadiness);
  const doneTasks = projectTasks.filter((task) => task.status === "completed_task").length;
  const taskProgress = projectTasks.length ? Math.round((doneTasks / projectTasks.length) * 100) : 0;
  const daysToLaunch = selected ? Math.ceil((new Date(selected.plannedLaunchDate) - new Date()) / 86400000) : 0;
  const launchTone = daysToLaunch <= 30 ? "critical" : daysToLaunch <= 60 ? "warning" : "normal";

  const domainColors = {
    GTM: "--accent",
    "运营": "--blue",
    "MKT / 流量": "--amber",
    "供应链 / 交付": "--red",
    "财经": "--green",
  };
  const criticalPath = [
    ["上市策略", "卖点、价格、渠道节奏达成一致", taskProgress],
    ["成熟度门禁", "产品、物料、服务、库存满足上市要求", readinessSummary.score],
    ["风险闭环", "P0/P1 风险有责任人和升级机制", Math.max(0, 100 - projectRisks.length * 22)],
    ["首销复盘", "首销监控与复盘口径提前定义", Math.min(100, 60 + doneTasks * 6)],
  ];

  return `
    <section class="cd-hero cd-hero-upgraded">
      <div class="cd-hero-left">
        <p class="eyebrow">Launch Countdown Control Tower</p>
        <h2>${escapeHtml(selected?.name || "当前项目")} · 上市倒排作战图</h2>
        <p>围绕计划上市日 ${selected?.plannedLaunchDate || "未设置"}，把上市前关键动作拆成周节奏、责任域和门禁检查，帮助团队按同一张表推进。</p>
      </div>
      <div class="cd-launch-orbit ${launchTone}">
        <span class="cd-orbit-ring"></span>
        <strong>${daysToLaunch > 0 ? daysToLaunch : 0}</strong>
        <small>天后上市</small>
      </div>
    </section>

    <section class="cd-command-grid">
      ${countdownStatCard("上市窗口", selected?.plannedLaunchDate || "未设置", stageLabels[selected?.currentStage] || "当前阶段", launchTone)}
      ${countdownStatCard("倒排动作", totalActions, `${domains.length} 个业务域协同`, "normal")}
      ${countdownStatCard("项目任务", `${taskProgress}%`, `${doneTasks}/${projectTasks.length} 已完成`, taskProgress >= 70 ? "normal" : "warning")}
      ${countdownStatCard("准备成熟度", `${readinessSummary.score}%`, `${readinessSummary.readyLike}/${readinessSummary.total} 项就绪`, readinessSummary.score >= 75 ? "normal" : "warning")}
    </section>

    <section class="cd-toolbar">
      <div class="cd-domain-pills">
        <button class="cd-pill active" data-cd-filter="all">全部</button>
        ${domains.map((d) => `<button class="cd-pill" data-cd-filter="${d}" style="--pill-color:var(${domainColors[d] || "--line"})">${d} <small>${domainGroups[d].length}</small></button>`).join("")}
      </div>
      <div style="display:flex;gap:8px;">
        <button class="ghost-action" data-route="templates">资料模板</button>
        <button class="primary-action" id="countdown-gen-tasks">根据项目生成任务</button>
      </div>
    </section>

    <section class="cd-board">
      <div class="cd-board-head">
        <div>
          <p class="eyebrow">8-Week Launch Gantt</p>
          <h2>上市前 8 周节奏</h2>
        </div>
        <span>从策略冻结到上市发布，再到首销承接</span>
      </div>
      <div class="cd-week-ruler">
        ${countdownPlan.map((week, index) => `<span style="--delay:${index * 70}ms;">W${8 - index}</span>`).join("")}
      </div>
      <div class="cd-domain-gantt">
        ${domains.map((domain, domainIndex) => `
          <div class="cd-gantt-row" style="--delay:${domainIndex * 90}ms;">
            <strong style="--domain-color:var(${domainColors[domain] || "--line"});">${domain}</strong>
            <div>
              ${countdownPlan.map((week, weekIndex) => {
                const count = week.items.filter((item) => item.domain === domain).length;
                return `<span class="${count ? "active" : ""}" style="--domain-color:var(${domainColors[domain] || "--line"});--delay:${(domainIndex + weekIndex) * 45}ms;">${count || ""}</span>`;
              }).join("")}
            </div>
          </div>
        `).join("")}
      </div>
    </section>

    <section class="cd-insight-grid">
      <div class="cd-path-card">
        <div class="cd-board-head">
          <div>
            <p class="eyebrow">Critical Path</p>
            <h2>关键路径健康度</h2>
          </div>
        </div>
        <div class="cd-path-list">
          ${criticalPath.map(([name, desc, value], index) => `
            <article style="--value:${Math.max(8, Math.min(value, 100))}%;--delay:${index * 100}ms;">
              <div><strong>${name}</strong><span>${Math.round(Math.max(0, Math.min(value, 100)))}%</span></div>
              <p>${desc}</p>
              <i><em></em></i>
            </article>
          `).join("")}
        </div>
      </div>

      <div class="cd-risk-card">
        <div class="cd-board-head">
          <div>
            <p class="eyebrow">Launch Alerts</p>
            <h2>上市预警</h2>
          </div>
          <span>${projectRisks.length} 个开放风险</span>
        </div>
        <div class="cd-alert-list">
          ${projectRisks.length
            ? projectRisks.slice(0, 4).map((risk, index) => `
              <article class="${risk.priority === "P0" ? "hot" : risk.priority === "P1" ? "warm" : ""}" style="--delay:${index * 90}ms;">
                <b>${risk.priority}</b>
                <div><strong>${escapeHtml(risk.title)}</strong><span>${risk.domain} · ${userName(risk.ownerId)} · ${risk.dueDate}</span></div>
              </article>
            `).join("")
            : `<div class="cd-clear-state">${icon.shieldCheck} 当前项目暂无开放风险</div>`
          }
        </div>
      </div>
    </section>

    <section class="cd-timeline cd-timeline-upgraded">
      ${countdownPlan.map((week, weekIdx) => {
        const weekNum = 8 - weekIdx;
        const urgency = weekNum <= 2 ? "critical" : weekNum <= 4 ? "warning" : "normal";
        return `
          <div class="cd-week" data-week-idx="${weekIdx}">
            <div class="cd-week-marker cd-${urgency}">
              <span class="cd-week-num">W${weekNum}</span>
              <span class="cd-week-label">${week.week.replace("上市 - ", "").replace(" 周", "")}</span>
            </div>
            <div class="cd-week-content">
              <div class="cd-week-header">
                <strong>${week.week}</strong>
                <span>${week.items.length} 项动作</span>
              </div>
              <div class="cd-week-items">
                ${week.items.map((item) => {
                  const color = domainColors[item.domain] || "--line";
                  return `
                    <div class="cd-item" data-cd-domain="${item.domain}">
                      <span class="cd-item-domain" style="background:var(${color});">${item.domain}</span>
                      <span class="cd-item-action">${item.action}</span>
                    </div>
                  `;
                }).join("")}
              </div>
            </div>
          </div>
        `;
      }).join("")}
    </section>
  `;
}

function countdownStatCard(label, value, caption, tone = "normal") {
  return `
    <article class="cd-command-card ${tone}">
      <span>${label}</span>
      <strong>${value}</strong>
      <small>${caption}</small>
    </article>
  `;
}

/* ============================================
   视图：模板中心
   ============================================ */
function renderTemplates() {
  const filteredChecklistItems =
    state.templateStageFilter === "all"
      ? checklistItems
      : checklistItems.filter((item) => item.stageKey === state.templateStageFilter);
  const stageGroups = groupBy(checklistItems, (item) => item.stageKey);

  return `
    <section class="content-grid">
      <div class="panel span-2">
        <div class="panel-header">
          <div>
          <h2>GTM资料模板</h2>
            <p>模板用于把咨询交付物固化成可执行任务、检查项和评审动作；当前已内置 ${checklistItems.length} 条参考流程动作。</p>
          </div>
          <button class="primary-action" id="apply-task-template">套用任务模板</button>
        </div>
        <div class="template-grid">
          ${templates
            .map(
              (template) => `
                <article class="template-card">
                  <div>
                    <span>${template.type}</span>
                    ${template.stageKey === "all" ? statusBadge("normal") : `<span class="badge badge-not_started">${stageLabels[template.stageKey]}</span>`}
                    <span style="font-size:11px;color:var(--text-tertiary);">v${template.version || "1.0"}</span>
                  </div>
                  <strong>${escapeHtml(template.name)}</strong>
                  <p>${Array.isArray(template.items) ? (typeof template.items[0] === "string" ? template.items.join(" / ") : template.items.map((i) => i.title || i).join(" / ")) : ""}</p>
                  <button class="table-action" data-edit-template="${template.id}">编辑</button>
                </article>
              `
            )
            .join("")}
        </div>
      </div>
      <div class="panel">
        <div class="panel-header">
          <h2>模板使用规则</h2>
        </div>
        <div class="compact-list">
          <article><strong>先模板后执行</strong><p>新建项目后按阶段生成任务和检查项。</p></article>
          <article><strong>允许微调</strong><p>模板用于起步，不替代项目负责人判断。</p></article>
          <article><strong>沉淀复盘</strong><p>试点完成后把有效动作固化为下一版模板。</p></article>
        </div>
      </div>
    </section>

    <section class="panel">
      <div class="panel-header">
        <div>
          <p class="eyebrow">Workflow Library</p>
          <h2>GTM流程动作与核心交付件库</h2>
          <p>从客户参考 Checklist 中抽取，后续可升级为"按阶段生成任务 / 按角色生成待办 / 按交付物追踪证据"。</p>
        </div>
        <select id="template-stage-filter" aria-label="按阶段筛选模板动作">
          <option value="all" ${state.templateStageFilter === "all" ? "selected" : ""}>全部阶段</option>
          ${stageOrder
            .map((stageKey) => `<option value="${stageKey}" ${state.templateStageFilter === stageKey ? "selected" : ""}>${stageLabels[stageKey]}（${stageGroups[stageKey]?.length || 0}）</option>`)
            .join("")}
        </select>
      </div>
      <div class="stage-template-summary">
        ${stageOrder
          .map(
            (stageKey) => `
              <article class="${state.templateStageFilter === stageKey ? "active" : ""}" data-filter-stage="${stageKey}">
                <span>${stageLabels[stageKey]}</span>
                <strong>${stageGroups[stageKey]?.length || 0}</strong>
              </article>
            `
          )
          .join("")}
      </div>
      <div class="workflow-library">
        ${filteredChecklistItems.length
          ? filteredChecklistItems
              .map(
                (item) => `
                  <article>
                    <div>
                      <span>${item.stageName}</span>
                      <strong>${item.role}</strong>
                    </div>
                    <h3>${escapeHtml(item.activity)}</h3>
                    <p>${escapeHtml(item.deliverable)}</p>
                    ${item.note ? `<small>${escapeHtml(item.note)}</small>` : ""}
                  </article>
                `
              )
              .join("")
          : emptyState("该阶段暂无流程动作", "切换其他阶段查看")
        }
      </div>
    </section>
  `;
}

/* ============================================
   视图：系统设置
   ============================================ */
function renderSettings() {
  return `
    <section class="content-grid">
      <div class="panel span-2">
        <div class="panel-header">
          <h2>用户与角色</h2>
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr><th>姓名</th><th>角色</th><th>部门</th><th>权限定位</th></tr>
            </thead>
            <tbody>
              ${users
                .map((user) => `<tr><td><strong>${user.name}</strong></td><td>${user.role}</td><td>${user.department}</td><td>${permissionHint(user.role)}</td></tr>`)
                .join("")}
            </tbody>
          </table>
        </div>
      </div>
      <div class="panel">
        <div class="panel-header">
          <h2>本地数据</h2>
        </div>
        <div class="compact-list">
          <article><strong>自动保存</strong><p>当前浏览器会保存任务、风险、成熟度和阶段状态。</p></article>
          <article><strong>演示重置</strong><p>如需重新演示，可以恢复到种子数据。</p></article>
        </div>
        <button class="primary-action danger-action" id="reset-demo-data">重置演示数据</button>
      </div>
    </section>
  `;
}

function permissionHint(role) {
  if (role.includes("GTM")) return "管理项目、阶段、任务、评审和风险";
  if (role.includes("负责人")) return "维护本领域任务、交付物和风险";
  if (role.includes("协同")) return "提交协同材料并更新支撑状态";
  return "查看项目和处理待办";
}

/* ============================================
   视图：消息中心
   ============================================ */
function renderMessages() {
  const userMessages = state.messageData
    .filter((m) => m.userId === state.currentUserId)
    .sort((a, b) => (a.read === b.read ? 0 : a.read ? 1 : -1));

  const typeLabels = { task: "任务", risk: "风险", review: "评审", system: "系统" };
  const typeColors = { task: "#3b82f6", risk: "#dc2626", review: "#d97706", system: "#059669" };

  return `
    <section class="panel">
      <div class="panel-header">
        <div>
          <h2>消息中心</h2>
          <p>${userMessages.filter((m) => !m.read).length} 条未读消息</p>
        </div>
        <button class="ghost-action" id="mark-all-read">全部已读</button>
      </div>
      <div class="message-list">
        ${userMessages.length
          ? userMessages
              .map(
                (msg) => `
                  <article class="message-item ${msg.read ? "read" : "unread"}" data-msg-id="${msg.id}">
                    <div class="message-dot" style="background:${typeColors[msg.type] || "#94a3b8"}"></div>
                    <div class="message-body">
                      <div class="message-head">
                        <strong>${escapeHtml(msg.title)}</strong>
                        <span class="message-type">${typeLabels[msg.type] || msg.type}</span>
                        <span class="message-date">${msg.createdAt}</span>
                      </div>
                      <p>${escapeHtml(msg.content)}</p>
                    </div>
                    ${msg.read
                      ? `<span class="message-status">已读</span>`
                      : `<button class="table-action" data-mark-read="${msg.id}">标记已读</button>`
                    }
                  </article>
                `
              )
              .join("")
          : emptyState("暂无消息", "当前没有收到任何通知")
        }
      </div>
    </section>
  `;
}

/* ============================================
   视图：任务中心
   ============================================ */
function renderTasks() {
  let rows = state.taskData;
  if (state.taskProjectFilter !== "all") rows = rows.filter((task) => task.projectId === state.taskProjectFilter);
  if (state.taskStatusFilter !== "all") rows = rows.filter((task) => task.status === state.taskStatusFilter);
  if (state.taskPriorityFilter !== "all") rows = rows.filter((task) => task.priority === state.taskPriorityFilter);
  if (state.taskSearchQuery) {
    const q = state.taskSearchQuery.toLowerCase();
    rows = rows.filter((task) =>
      task.title.toLowerCase().includes(q) ||
      task.domain.toLowerCase().includes(q) ||
      userName(task.ownerId).toLowerCase().includes(q)
    );
  }

  const projectOpts = state.projectData.map((p) => `<option value="${p.id}" ${state.taskProjectFilter === p.id ? "selected" : ""}>${escapeHtml(p.name)}</option>`).join("");

  return `
    <section class="panel">
      <div class="panel-header">
        <div>
          <h2>操盘任务</h2>
          <p>按项目、阶段、业务域和责任人跟踪跨部门动作。</p>
        </div>
        <div style="display:flex;gap:10px;align-items:center;">
          <div class="search-box">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input type="text" id="task-search" placeholder="搜索任务..." value="${escapeHtml(state.taskSearchQuery)}">
          </div>
          <select id="task-project-filter" aria-label="项目筛选">
            <option value="all" ${state.taskProjectFilter === "all" ? "selected" : ""}>全部项目</option>
            ${projectOpts}
          </select>
          <select id="task-status-filter" aria-label="任务状态筛选">
            ${[["all", "全部状态"],["not_started", "未开始"],["in_progress", "进行中"],["pending_review", "待评审"],["completed_task", "已完成"],["canceled", "已取消"]]
              .map(([v, l]) => `<option value="${v}" ${state.taskStatusFilter === v ? "selected" : ""}>${l}</option>`)
              .join("")}
          </select>
          <select id="task-priority-filter" aria-label="优先级筛选">
            ${[["all", "全部优先级"],["P0", "P0"],["P1", "P1"],["P2", "P2"],["P3", "P3"]]
              .map(([v, l]) => `<option value="${v}" ${state.taskPriorityFilter === v ? "selected" : ""}>${l}</option>`)
              .join("")}
          </select>
          <button class="primary-action" id="new-task-btn-inline">新建任务</button>
        </div>
      </div>
      ${rows.length ? renderTaskTable(rows) : emptyState("没有找到匹配的任务", "尝试调整筛选条件或搜索关键词")}
    </section>
  `;
}

function renderTaskTable(rows) {
  if (!rows.length) return emptyState("暂无任务", "当前条件下没有任务数据");
  return `
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>任务</th>
            <th>项目</th>
            <th>阶段</th>
            <th>业务域</th>
            <th>责任人</th>
            <th>截止时间</th>
            <th>优先级</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (task) => {
                const overdue = isTaskOverdue(task) && task.status !== "completed_task" && task.status !== "canceled";
                return `
                <tr class="${overdue ? "row-overdue" : ""} ${task.status === "canceled" ? "row-canceled" : ""}">
                  <td><strong>${escapeHtml(task.title)}</strong>${overdue ? '<small class="danger-text">已超期</small>' : ""}</td>
                  <td>${projectName(task.projectId)}</td>
                  <td>${stageLabels[task.stageKey]}</td>
                  <td>${task.domain}</td>
                  <td>${userName(task.ownerId)}</td>
                  <td>${task.dueDate}</td>
                  <td>${priorityBadge(task.priority)}</td>
                  <td>${statusBadge(task.status)}</td>
                  <td>
                    <div style="display:flex;gap:4px;">
                      <button class="table-action" data-complete-task="${task.id}" ${task.status === "completed_task" || task.status === "canceled" ? "disabled" : ""}>完成</button>
                      <button class="table-action" data-cancel-task="${task.id}" ${task.status === "completed_task" || task.status === "canceled" ? "disabled" : ""} style="color:var(--text-tertiary);">取消</button>
                    </div>
                  </td>
                </tr>`;
              }
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderKanbanBoard(tasks) {
  if (!tasks.length) return emptyState("暂无任务", "点击「新建任务」添加");
  const cols = [
    { key: "not_started", label: "待开始", icon: icon.circle, cls: "kanban-todo" },
    { key: "in_progress", label: "进行中", icon: icon.arrowRightCircle, cls: "kanban-doing" },
    { key: "pending_review", label: "待评审", icon: icon.eye, cls: "kanban-review" },
    { key: "completed_task", label: "已完成", icon: icon.checkCircle, cls: "kanban-done" },
  ];
  return `
    <div class="kanban-board">
      ${cols.map((col) => {
        const colTasks = tasks.filter((t) => t.status === col.key);
        return `
          <div class="kanban-column ${col.cls}">
            <div class="kanban-col-header">
              <span>${col.icon} ${col.label}</span>
              <span class="kanban-count">${colTasks.length}</span>
            </div>
            <div class="kanban-col-body">
              ${colTasks.map((task) => {
                const overdue = isTaskOverdue(task);
                const daysLeft = Math.ceil((new Date(task.dueDate) - new Date()) / 86400000);
                const priorityBorder = task.priority === "P0" ? "kanban-border-p0" : task.priority === "P1" ? "kanban-border-p1" : "";
                const overdueBorder = overdue ? "kanban-border-overdue" : "";
                return `
                  <div class="kanban-card ${priorityBorder} ${overdueBorder}">
                    <div class="kanban-card-title">${escapeHtml(task.title)}</div>
                    <div class="kanban-card-tags">
                      <span class="kanban-tag-domain">${task.domain}</span>
                      <span class="kanban-tag-priority ${task.priority === "P0" ? "p0" : task.priority === "P1" ? "p1" : "p2"}">${task.priority}</span>
                    </div>
                    <div class="kanban-card-footer">
                      <span class="kanban-card-owner">${userName(task.ownerId)}</span>
                      <span class="kanban-card-due ${overdue ? "overdue" : daysLeft <= 3 && daysLeft >= 0 ? "urgent" : ""}">
                        ${overdue ? icon.alertCircle + " " + task.dueDate : daysLeft <= 3 && daysLeft >= 0 ? icon.zap + " " + task.dueDate : icon.calendar + " " + task.dueDate}
                      </span>
                    </div>
                    ${task.status !== "completed_task" ? `<button class="kanban-card-action" data-complete-task="${task.id}">${icon.checkSquare} 完成</button>` : ""}
                  </div>
                `;
              }).join("")}
            </div>
          </div>
        `;
      }).join("")}
    </div>
  `;
}

/* ============================================
   视图：成熟度
   ============================================ */
function renderReadiness() {
  const selected = state.projectData.find((project) => project.id === state.selectedProjectId);
  const items = state.readinessData.filter((item) => item.projectId === selected.id);
  const summary = calculateReadinessSummary(items);
  return `
    <section class="content-grid">
      <div class="panel">
        <div class="panel-header">
          <h2>成熟度概览</h2>
        </div>
        <div class="readiness-score">
          <strong>${summary.score}%</strong>
          <span>${summary.readyLike}/${summary.total} 基本就绪或已就绪</span>
        </div>
      </div>
      <div class="panel span-2">
        <div class="panel-header">
          <h2>${escapeHtml(selected.name)} · 上市准备检查</h2>
        </div>
        ${items.length ? `<div class="readiness-grid">
          ${items
            .map(
              (item) => `
                <article class="readiness-card">
                  <div>
                    <span>${item.dimension}</span>
                    ${statusBadge(item.status)}
                  </div>
                  <strong>${escapeHtml(item.title)}</strong>
                  <p>${escapeHtml(item.evidence)}</p>
                  ${item.blockerNote ? `<small class="danger-text">${escapeHtml(item.blockerNote)}</small>` : ""}
                  <button class="table-action" data-ready-item="${item.id}" ${item.status === "ready" ? "disabled" : ""}>标记已就绪</button>
                </article>
              `
            )
            .join("")}
        </div>` : emptyState("暂无成熟度检查项", "当前项目尚未配置上市准备检查项，请先套用成熟度模板或手动添加")}
      </div>
    </section>
  `;
}

/* ============================================
   视图：评审决策
   ============================================ */
function renderReviews() {
  const selected = state.projectData.find((project) => project.id === state.selectedProjectId);
  const rows = state.reviewData.filter((review) => review.projectId === selected?.id);

  return `
    <section class="panel">
      <div class="panel-header">
        <div>
          <h2>评审决策</h2>
          <p>${escapeHtml(selected?.name || "当前项目")} · 阶段门会议、评审结论和决策说明。</p>
        </div>
        <div style="display:flex;gap:10px;">
          <button class="ghost-action" id="new-review-btn">创建评审</button>
          <button class="primary-action" id="decide-review-btn">处理评审结论</button>
        </div>
      </div>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>评审</th>
              <th>项目</th>
              <th>阶段</th>
              <th>时间</th>
              <th>状态</th>
              <th>结论</th>
              <th>说明</th>
            </tr>
          </thead>
          <tbody>
            ${rows
              .map(
                (review) => `
                  <tr>
                    <td><strong>${escapeHtml(review.title)}</strong></td>
                    <td>${projectName(review.projectId)}</td>
                    <td>${stageLabels[review.stageKey]}</td>
                    <td>${review.scheduledAt}</td>
                    <td>${review.status === "closed" ? statusBadge("completed_task") : statusBadge("pending_review")}</td>
                    <td>${statusBadge(review.decision)}</td>
                    <td>${escapeHtml(review.decisionNote)}</td>
                  </tr>
                `
              )
              .join("")}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

/* ============================================
   视图：风险台账
   ============================================ */
function renderRisks() {
  const selected = state.projectData.find((project) => project.id === state.selectedProjectId);
  let rows = state.riskData.filter((risk) => risk.projectId === selected?.id);
  if (state.riskSearchQuery) {
    const q = state.riskSearchQuery.toLowerCase();
    rows = rows.filter((risk) =>
      risk.title.toLowerCase().includes(q) ||
      risk.domain.toLowerCase().includes(q) ||
      userName(risk.ownerId).toLowerCase().includes(q)
    );
  }

  return `
    <section class="panel">
      <div class="panel-header">
        <div>
          <h2>风险台账</h2>
          <p>${escapeHtml(selected?.name || "当前项目")} · 问题升级、责任人、截止时间和关闭状态。</p>
        </div>
        <div style="display:flex;gap:10px;align-items:center;">
          <div class="search-box">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input type="text" id="risk-search" placeholder="搜索风险..." value="${escapeHtml(state.riskSearchQuery)}">
          </div>
          <button class="primary-action" id="quick-risk-panel">新增风险</button>
        </div>
      </div>
      ${rows.length
        ? `<div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>问题/风险</th>
                  <th>项目</th>
                  <th>阶段</th>
                  <th>业务域</th>
                  <th>优先级</th>
                  <th>责任人</th>
                  <th>截止时间</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                ${rows
                  .map(
                    (risk) => `
                      <tr>
                        <td><strong>${escapeHtml(risk.title)}</strong>${risk.escalationFlag ? '<small class="danger-text">已升级</small>' : ""}</td>
                        <td>${projectName(risk.projectId)}</td>
                        <td>${stageLabels[risk.stageKey]}</td>
                        <td>${risk.domain}</td>
                        <td>${priorityBadge(risk.priority)}</td>
                        <td>${userName(risk.ownerId)}</td>
                        <td>${risk.dueDate}</td>
                        <td>${statusBadge(risk.status)}</td>
                        <td><button class="table-action" data-close-risk="${risk.id}" ${risk.status === "closed" ? "disabled" : ""}>关闭</button></td>
                      </tr>
                    `
                  )
                  .join("")}
              </tbody>
            </table>
          </div>`
        : emptyState("没有找到匹配的风险", "尝试调整搜索关键词")
      }
    </section>
  `;
}

/* ============================================
   视图：复盘改进表
   ============================================ */
function renderImprovements() {
  const selected = state.projectData.find((project) => project.id === state.selectedProjectId);
  const dateInDays = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
  const projectSet = selected ? [selected] : [];
  const stableProjects = projectSet.filter((project) => ["launch_sales", "stable_sales", "exit_review"].includes(project.currentStage));
  const openRisks = state.riskData.filter((risk) => risk.projectId === selected?.id && risk.status !== "closed");
  const blockedReadiness = state.readinessData.filter((item) => item.projectId === selected?.id && (item.status === "blocked" || item.status === "basically_ready"));

  const rows = [
    ...stableProjects.map((project, index) => ({
      id: `review-${project.id}`,
      projectId: project.id,
      topic: project.currentStage === "stable_sales" ? "稳定销售经营复盘" : "首销上市复盘",
      finding: project.currentStage === "stable_sales" ? "终端动销与服务体验反馈需要形成持续改进闭环" : "首销动作、渠道承接与传播资源需要复盘有效性",
      rootCause: "上市动作跨部门分散，缺少统一经营节奏与复盘口径",
      action: project.currentStage === "stable_sales" ? "输出经营优化清单，刷新渠道动作和服务FAQ" : "沉淀首销复盘报告，更新下一批上市倒排模板",
      ownerId: index % 2 === 0 ? "u-gtm" : "u-retail",
      dueDate: dateInDays(7 + index * 5),
      status: index === 0 ? "in_progress" : "not_started",
      asset: "复盘报告 / SOP更新",
    })),
    ...openRisks.slice(0, 4).map((risk, index) => ({
      id: `risk-${risk.id}`,
      projectId: risk.projectId,
      topic: "风险闭环复盘",
      finding: risk.title,
      rootCause: `${risk.domain}准备度或资源协同不足`,
      action: risk.escalationFlag ? "升级管理层决策，并固化预警规则" : "补齐责任动作，纳入周度操盘会跟踪",
      ownerId: risk.ownerId,
      dueDate: risk.dueDate,
      status: risk.priority === "P0" ? "pending_review" : "in_progress",
      asset: "风险案例 / 预警规则",
    })),
    ...blockedReadiness.slice(0, 4).map((item, index) => ({
      id: `ready-${item.id}`,
      projectId: item.projectId,
      topic: "成熟度检查复盘",
      finding: item.blockerNote || `${item.dimension}尚未完全就绪`,
      rootCause: "上市准备检查项未前置到任务计划",
      action: `将「${item.title}」前置为阶段门必查项`,
      ownerId: index % 2 === 0 ? "u-product" : "u-service",
      dueDate: dateInDays(10 + index * 3),
      status: item.status === "blocked" ? "in_progress" : "not_started",
      asset: "成熟度检查项 / 阶段门规则",
    })),
  ];

  const completed = rows.filter((row) => row.status === "completed_task").length;
  const inProgress = rows.filter((row) => row.status === "in_progress" || row.status === "pending_review").length;
  const pending = rows.filter((row) => row.status === "not_started").length;
  const assetCount = new Set(rows.map((row) => row.asset)).size;

  return `
    <section class="grid metrics-grid">
      ${metricCard("改进项总数", rows.length, "来自复盘、风险和成熟度检查")}
      ${metricCard("推进中", inProgress, "需要周度操盘跟踪")}
      ${metricCard("待启动", pending, "需明确责任人与截止时间")}
      ${metricCard("沉淀资产", assetCount, "报告、规则、模板和SOP")}
    </section>

    <section class="panel">
      <div class="panel-header">
        <div>
          <h2>复盘改进表</h2>
          <p>${escapeHtml(selected?.name || "当前项目")} · 把“卖得怎么样、哪里没做好、下次怎么改”沉淀为可追踪动作，而不是停留在会议纪要。</p>
        </div>
        <button class="ghost-action" data-route="templates">查看模板</button>
      </div>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>复盘主题</th>
              <th>项目</th>
              <th>发现问题</th>
              <th>根因归类</th>
              <th>改进动作</th>
              <th>责任人</th>
              <th>截止时间</th>
              <th>状态</th>
              <th>沉淀资产</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((row) => `
              <tr>
                <td><strong>${escapeHtml(row.topic)}</strong></td>
                <td>${projectName(row.projectId)}</td>
                <td>${escapeHtml(row.finding)}</td>
                <td>${escapeHtml(row.rootCause)}</td>
                <td>${escapeHtml(row.action)}</td>
                <td>${userName(row.ownerId)}</td>
                <td>${row.dueDate}</td>
                <td>${statusBadge(row.status)}</td>
                <td>${escapeHtml(row.asset)}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    </section>

    <section class="content-grid">
      <div class="panel">
        <div class="panel-header"><h2>复盘来源</h2></div>
        <div class="stage-stack">
          <div class="stack-row"><span>上市/首销复盘</span><strong>${stableProjects.length}</strong></div>
          <div class="stack-row"><span>开放风险闭环</span><strong>${openRisks.length}</strong></div>
          <div class="stack-row"><span>成熟度补强</span><strong>${blockedReadiness.length}</strong></div>
        </div>
      </div>
      <div class="panel span-2">
        <div class="panel-header"><h2>运营闭环逻辑</h2></div>
        <div class="compact-list">
          <article><strong>复盘不是文档归档</strong><p>每个结论必须转成责任人、截止时间、状态和沉淀资产。</p></article>
          <article><strong>改进项反哺流程</strong><p>高频问题进入上市倒排表、成熟度检查表、风险预警规则或资料模板。</p></article>
          <article><strong>下次上市直接复用</strong><p>让经验沉淀为下一批产品的标准动作，提升产品商业成功率。</p></article>
        </div>
      </div>
    </section>
  `;
}

/* ============================================
   视图：经营看板
   ============================================ */
function renderDashboards() {
  const tab = state.dashboardTab || "overview";

  return `
    <section class="ops-hero">
      <div>
        <p class="eyebrow">GTM Operating Cockpit</p>
        <h2>运营总览</h2>
        <p>用一张驾驶舱看清全部项目组合、上市节奏、风险预警和跨职能执行状态。</p>
      </div>
      <div class="ops-hero-pulse">
        <span></span>
        <strong>Live</strong>
        <small>周度操盘视图</small>
      </div>
    </section>

    <section class="ops-tabs">
      <button class="${tab === "overview" ? "active" : ""}" data-dashboard-tab="overview">运营总览</button>
      <button class="${tab === "project" ? "active" : ""}" data-dashboard-tab="project">项目横向进展</button>
      <button class="${tab === "process" ? "active" : ""}" data-dashboard-tab="process">专题执行看板</button>
      <button class="${tab === "operations" ? "active" : ""}" data-dashboard-tab="operations">上市经营复盘</button>
      <button class="${tab === "team" ? "active" : ""}" data-dashboard-tab="team">团队Workload</button>
    </section>

    ${tab === "overview" ? renderDashboardOverview() : ""}
    ${tab === "project" ? renderDashboardProject() : ""}
    ${tab === "process" ? renderDashboardProcess() : ""}
    ${tab === "operations" ? renderDashboardOperations() : ""}
    ${tab === "team" ? renderDashboardTeam() : ""}
  `;
}

function renderDashboardOverview() {
  const stageGroups = groupBy(state.projectData, (project) => project.currentStage);
  const totalTasks = state.taskData.length;
  const doneTasks = state.taskData.filter((task) => task.status === "completed_task").length;
  const overdueTasks = state.taskData.filter((task) => isTaskOverdue(task));
  const openRisks = state.riskData.filter((risk) => risk.status !== "closed");
  const p0p1Risks = openRisks.filter((risk) => risk.priority === "P0" || risk.priority === "P1");
  const closedReviews = state.reviewData.filter((review) => review.status === "closed").length;
  const taskRate = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : 0;
  const reviewRate = state.reviewData.length ? Math.round((closedReviews / state.reviewData.length) * 100) : 0;
  const readinessSummaries = state.projectData.map((project) =>
    calculateReadinessSummary(state.readinessData.filter((item) => item.projectId === project.id))
  );
  const avgReadiness = readinessSummaries.length
    ? Math.round(readinessSummaries.reduce((sum, item) => sum + item.score, 0) / readinessSummaries.length)
    : 0;
  const healthGroups = groupBy(state.projectData, (project) => deriveProjectHealth(project, state.taskData, state.riskData, state.readinessData));
  const healthScore = Math.max(12, Math.round(((healthGroups.normal?.length || 0) / Math.max(state.projectData.length, 1)) * 100));
  const stageMax = Math.max(...stageOrder.map((stageKey) => stageGroups[stageKey]?.length || 0), 1);
  const domainGroups = groupBy(state.taskData, (task) => task.domain);
  const projectCards = state.projectData.map((project) => {
    const projectTasks = state.taskData.filter((task) => task.projectId === project.id);
    const projectDone = projectTasks.filter((task) => task.status === "completed_task").length;
    const projectProgress = projectTasks.length ? Math.round((projectDone / projectTasks.length) * 100) : 0;
    const readiness = calculateReadinessSummary(state.readinessData.filter((item) => item.projectId === project.id));
    const risks = state.riskData.filter((risk) => risk.projectId === project.id && risk.status !== "closed");
    const health = deriveProjectHealth(project, state.taskData, state.riskData, state.readinessData);
    return { project, projectProgress, readiness, risks, health };
  });

  return `
    <section class="ops-kpi-grid">
      ${opsKpiCard("项目组合健康度", `${healthScore}%`, "正常项目占比", healthScore, "green")}
      ${opsKpiCard("任务完成率", `${taskRate}%`, `${doneTasks}/${totalTasks} 已完成`, taskRate, taskRate >= 70 ? "green" : "amber")}
      ${opsKpiCard("平均成熟度", `${avgReadiness}%`, "上市准备度", avgReadiness, avgReadiness >= 75 ? "green" : "amber")}
      ${opsKpiCard("P0/P1风险", p0p1Risks.length, `${openRisks.length} 个开放风险`, Math.min(p0p1Risks.length * 24, 100), p0p1Risks.length ? "red" : "green")}
    </section>

    <section class="ops-grid">
      <div class="ops-card ops-card-large">
        <div class="ops-card-head">
          <div>
            <p class="eyebrow">Portfolio Flow</p>
            <h3>项目阶段流动</h3>
          </div>
          <span>${state.projectData.length} 个项目在跑</span>
        </div>
        <div class="ops-stage-flow">
          ${stageOrder.map((stageKey, index) => {
            const count = stageGroups[stageKey]?.length || 0;
            const height = Math.max(10, Math.round((count / stageMax) * 100));
            return `
              <div class="ops-stage-col" style="--delay:${index * 80}ms;--height:${height}%;">
                <div class="ops-stage-bar"><i></i></div>
                <strong>${count}</strong>
                <span>${stageLabels[stageKey]}</span>
              </div>
            `;
          }).join("")}
        </div>
      </div>

      <div class="ops-card ops-radar-card">
        <div class="ops-card-head">
          <div>
            <p class="eyebrow">Control Radar</p>
            <h3>操盘雷达</h3>
          </div>
        </div>
        <div class="ops-radar">
          <svg viewBox="0 0 220 220" role="img" aria-label="GTM操盘雷达">
            <polygon class="radar-grid" points="110,18 190,64 190,156 110,202 30,156 30,64"></polygon>
            <polygon class="radar-grid inner" points="110,54 158,82 158,138 110,166 62,138 62,82"></polygon>
            <polygon class="radar-shape" points="110,${110 - avgReadiness * 0.82} ${110 + taskRate * 0.72},${110 - taskRate * 0.42} ${110 + Math.max(18, reviewRate) * 0.72},${110 + Math.max(18, reviewRate) * 0.42} 110,${110 + Math.max(18, healthScore) * 0.82} ${110 - Math.max(18, 100 - p0p1Risks.length * 18) * 0.72},${110 + Math.max(18, 100 - p0p1Risks.length * 18) * 0.42} ${110 - Math.max(18, taskRate) * 0.72},${110 - Math.max(18, taskRate) * 0.42}"></polygon>
            <circle cx="110" cy="110" r="4"></circle>
          </svg>
          <div class="ops-radar-labels">
            <span>成熟度</span><span>任务</span><span>评审</span><span>健康</span><span>风险</span><span>协同</span>
          </div>
        </div>
      </div>

      <div class="ops-card ops-card-large">
        <div class="ops-card-head">
          <div>
            <p class="eyebrow">Project Heatmap</p>
            <h3>重点项目热力图</h3>
          </div>
          <button class="ghost-action" data-route="projects">进入项目池</button>
        </div>
        <div class="ops-project-strip">
          ${projectCards.map(({ project, projectProgress, readiness, risks, health }, index) => `
            <article class="ops-project-tile" data-open-project="${project.id}" style="--delay:${index * 100}ms;">
              <div>
                <strong>${escapeHtml(project.name)}</strong>
                ${statusBadge(health)}
              </div>
              <span>${stageLabels[project.currentStage]} · ${escapeHtml(project.targetChannel)}</span>
              <div class="ops-tile-bars">
                <p><b>任务</b><i><em style="width:${projectProgress}%"></em></i><small>${projectProgress}%</small></p>
                <p><b>成熟</b><i><em style="width:${readiness.score}%"></em></i><small>${readiness.score}%</small></p>
              </div>
              <footer><span>${risks.length} 个开放风险</span><span>上市 ${project.plannedLaunchDate}</span></footer>
            </article>
          `).join("")}
        </div>
      </div>

      <div class="ops-card">
        <div class="ops-card-head">
          <div>
            <p class="eyebrow">Risk Pulse</p>
            <h3>风险脉冲</h3>
          </div>
          <span>${overdueTasks.length} 超期任务</span>
        </div>
        <div class="ops-risk-pulse">
          ${openRisks.slice(0, 5).map((risk, index) => `
            <div class="ops-risk-line ${risk.priority === "P0" ? "hot" : risk.priority === "P1" ? "warm" : ""}" style="--delay:${index * 120}ms;">
              <b>${risk.priority}</b>
              <span>${escapeHtml(risk.title)}</span>
              <small>${projectName(risk.projectId)}</small>
            </div>
          `).join("") || emptyState("暂无开放风险", "当前风险已闭环")}
        </div>
      </div>

      <div class="ops-card">
        <div class="ops-card-head">
          <div>
            <p class="eyebrow">Domain Execution</p>
            <h3>业务域执行率</h3>
          </div>
        </div>
        <div class="ops-domain-bars">
          ${Object.entries(domainGroups).map(([domain, tasks], index) => {
            const done = tasks.filter((task) => task.status === "completed_task").length;
            const percent = Math.round((done / tasks.length) * 100);
            return `<div class="ops-domain-row" style="--delay:${index * 80}ms;--value:${percent}%;">
              <span>${domain}</span>
              <i><em></em></i>
              <strong>${percent}%</strong>
            </div>`;
          }).join("")}
        </div>
      </div>
    </section>
  `;
}

function opsKpiCard(label, value, caption, percent, tone = "green") {
  return `
    <article class="ops-kpi-card ${tone}">
      <div>
        <span>${label}</span>
        <strong>${value}</strong>
        <small>${caption}</small>
      </div>
      <div class="ops-ring" style="--value:${Math.max(0, Math.min(percent, 100))};">
        <b>${Math.max(0, Math.min(Math.round(percent), 100))}</b>
      </div>
    </article>
  `;
}

function renderDashboardProject() {
  const stageGroups = groupBy(state.projectData, (p) => p.currentStage);
  const delayed = state.projectData.filter((p) => deriveProjectHealth(p, state.taskData, state.riskData, state.readinessData) === "delayed");
  const atRisk = state.projectData.filter((p) => deriveProjectHealth(p, state.taskData, state.riskData, state.readinessData) === "at_risk");
  const upcomingLaunch = state.projectData.filter((p) => {
    const d = new Date(p.plannedLaunchDate);
    return d > new Date() && d < new Date(Date.now() + 60 * 86400000);
  });

  return `
    <section class="grid metrics-grid">
      ${metricCard("项目总数", state.projectData.length, "全部GTM产品项目")}
      ${metricCard("延期项目", delayed.length, "需要关注")}
      ${metricCard("风险项目", atRisk.length, "存在阻塞或P0/P1风险")}
      ${metricCard("即将上市", upcomingLaunch.length, "60天内计划上市")}
    </section>
    <section class="content-grid">
      <div class="panel span-2">
        <div class="panel-header"><h2>各阶段项目分布</h2></div>
        <div class="bar-list">
          ${stageOrder.map((stageKey) => {
            const count = stageGroups[stageKey]?.length || 0;
            const pct = state.projectData.length ? Math.round((count / state.projectData.length) * 100) : 0;
            return `<div class="bar-row"><span>${stageLabels[stageKey]}</span><div><i style="width:${pct}%"></i></div><strong>${count}</strong></div>`;
          }).join("")}
        </div>
      </div>
      <div class="panel">
        <div class="panel-header"><h2>项目健康度</h2></div>
        <div class="stage-stack">
          ${state.projectData.map((p) => {
            const health = deriveProjectHealth(p, state.taskData, state.riskData, state.readinessData);
            return `<div class="stack-row"><span>${escapeHtml(p.name)}</span>${statusBadge(health)}</div>`;
          }).join("")}
        </div>
      </div>
    </section>
  `;
}

function renderDashboardProcess() {
  const selected = state.projectData.find((project) => project.id === state.selectedProjectId);
  const readiness = state.readinessData.filter((item) => item.projectId === selected?.id);
  const readinessGroups = groupBy(readiness, (item) => item.dimension);
  const taskGroups = groupBy(state.taskData, (task) => task.domain);
  const totalTasks = state.taskData.length;
  const doneTasks = state.taskData.filter((t) => t.status === "completed_task").length;
  const overdueCount = state.taskData.filter((t) => isTaskOverdue(t)).length;
  const closedReviews = state.reviewData.filter((r) => r.status === "closed").length;
  const totalReviews = state.reviewData.length;
  const closedRisks = state.riskData.filter((r) => r.status === "closed").length;
  const totalRisks = state.riskData.length;

  return `
    <section class="grid metrics-grid">
      ${metricCard("任务完成率", totalTasks ? `${Math.round((doneTasks / totalTasks) * 100)}%` : "0%", `${doneTasks}/${totalTasks} 已完成`)}
      ${metricCard("超期率", totalTasks ? `${Math.round((overdueCount / totalTasks) * 100)}%` : "0%", `${overdueCount} 个超期任务`)}
      ${metricCard("评审完成率", totalReviews ? `${Math.round((closedReviews / totalReviews) * 100)}%` : "0%", `${closedReviews}/${totalReviews} 已关闭`)}
      ${metricCard("问题关闭率", totalRisks ? `${Math.round((closedRisks / totalRisks) * 100)}%` : "0%", `${closedRisks}/${totalRisks} 已关闭`)}
    </section>
    <section class="content-grid">
      <div class="panel span-2">
        <div class="panel-header"><h2>各业务域任务完成率</h2></div>
        <div class="bar-list">
          ${Object.entries(taskGroups).length
            ? Object.entries(taskGroups)
                .map(([domain, domainTasks]) => {
                  const done = domainTasks.filter((task) => task.status === "completed_task").length;
                  const percent = Math.round((done / domainTasks.length) * 100);
                  return `<div class="bar-row"><span>${domain}</span><div><i style="width:${percent}%"></i></div><strong>${percent}%</strong></div>`;
                })
                .join("")
            : emptyState("暂无任务数据", "请先创建或导入任务")
          }
        </div>
      </div>
      <div class="panel">
        <div class="panel-header"><h2>成熟度分布</h2></div>
        <div class="stage-stack">
          ${Object.entries(readinessGroups).length
            ? Object.entries(readinessGroups)
                .map(([dimension, items]) => {
                  const summary = calculateReadinessSummary(items);
                  return `<div class="stack-row"><span>${dimension}</span><strong>${summary.score}%</strong></div>`;
                })
                .join("")
            : emptyState("暂无成熟度数据", "请先进行上市准备检查")
          }
        </div>
      </div>
    </section>
  `;
}

function renderDashboardOperations() {
  const selected = state.projectData.find((project) => project.id === state.selectedProjectId);
  const projectTasks = state.taskData.filter((task) => task.projectId === selected?.id);
  const projectRisks = state.riskData.filter((risk) => risk.projectId === selected?.id);
  const projectReviews = state.reviewData.filter((review) => review.projectId === selected?.id);
  const projectReadiness = state.readinessData.filter((item) => item.projectId === selected?.id);
  const readinessSummary = calculateReadinessSummary(projectReadiness);

  // 超期任务TOP5
  const overdueTasks = projectTasks.filter((t) => isTaskOverdue(t)).slice(0, 5);
  // 待处理评审
  const pendingReviews = projectReviews.filter((r) => r.status === "open");
  // 开放风险TOP5
  const openRisksProject = projectRisks.filter((r) => r.status !== "closed").slice(0, 5);

  return `
    <section class="grid metrics-grid">
      ${metricCard("任务完成率", projectTasks.length ? `${Math.round((projectTasks.filter((t) => t.status === "completed_task").length / projectTasks.length) * 100)}%` : "—", `${projectTasks.filter((t) => t.status === "completed_task").length}/${projectTasks.length} 已完成`)}
      ${metricCard("上市成熟度", `${readinessSummary.score}%`, `${readinessSummary.readyLike}/${readinessSummary.total} 项就绪`)}
      ${metricCard("开放风险", `${projectRisks.filter((r) => r.status !== "closed").length}`, `${projectRisks.filter((r) => r.priority === "P0").length} P0 / ${projectRisks.filter((r) => r.priority === "P1").length} P1`)}
      ${metricCard("待处理评审", `${pendingReviews.length}`, `${projectReviews.filter((r) => r.status === "closed").length} 已闭环`)}
    </section>

    <section class="content-grid">
      <div class="panel span-2">
        <div class="panel-header"><h2>超期任务预警</h2></div>
        ${overdueTasks.length
          ? `<div class="compact-list">${overdueTasks.map((task) => `
              <article>
                <div style="display:flex;justify-content:space-between;align-items:center;">
                  <strong>${escapeHtml(task.title)}</strong>
                  <span class="task-due overdue">${task.dueDate}</span>
                </div>
                <span>${task.domain} · ${userName(task.ownerId)} · ${stageLabels[task.stageKey]}</span>
              </article>
            `).join("")}</div>`
          : `<div style="padding:20px;text-align:center;color:var(--text-secondary);">${icon.checkCircle} 暂无超期任务，项目推进正常</div>`
        }
      </div>
      <div class="panel">
        <div class="panel-header"><h2>开放风险清单</h2></div>
        ${openRisksProject.length
          ? `<div class="compact-list">${openRisksProject.map((risk) => `
              <article>
                <div style="display:flex;justify-content:space-between;align-items:center;">
                  <strong>${escapeHtml(risk.title)}</strong>
                  <div style="display:flex;gap:4px;">${priorityBadge(risk.priority)}${risk.escalationFlag ? '<span class="escalated">升级</span>' : ""}</div>
                </div>
                <span>${risk.domain} · ${userName(risk.ownerId)} · ${risk.dueDate}</span>
              </article>
            `).join("")}</div>`
          : `<div style="padding:20px;text-align:center;color:var(--text-secondary);">${icon.shieldCheck} 暂无开放风险</div>`
        }
      </div>
    </section>

    <section class="panel">
      <div class="panel-header"><h2>成熟度维度检查</h2></div>
      ${projectReadiness.length
        ? `<div class="bar-list">${projectReadiness.map((item) => {
            const isReady = item.status === "ready";
            const isBlocked = item.status === "blocked";
            return `<div class="bar-row">
              <span>${item.dimension}</span>
              <div><i style="width:${isReady ? 100 : isBlocked ? 30 : 60}%;background:${isReady ? '#10b981' : isBlocked ? '#ef4444' : '#f59e0b'}"></i></div>
              <strong>${isReady ? '100%' : isBlocked ? '30%' : '60%'}</strong>
            </div>`;
          }).join("")}</div>`
        : emptyState("暂无成熟度数据", "请先进行上市准备检查")
      }
    </section>

    <section class="panel">
      <div class="panel-header"><h2>经营复盘指标</h2></div>
      ${(state.kpiData && state.kpiData.length)
        ? (() => {
            const projectKpis = state.kpiData.filter((k) => k.projectId === selected?.id);
            if (!projectKpis.length) return emptyState("暂无经营数据", "当前项目暂未录入经营复盘指标");
            return `<div class="table-wrap"><table>
              <thead><tr><th>指标</th><th>实际值</th><th>目标值</th><th>达成</th><th>周期</th></tr></thead>
              <tbody>${projectKpis.map((kpi) => {
                const hit = kpi.target > 0 ? Math.round((kpi.value / kpi.target) * 100) : null;
                return `<tr>
                  <td><strong>${escapeHtml(kpi.metric)}</strong></td>
                  <td>${kpi.value}${kpi.unit}</td>
                  <td>${kpi.target}${kpi.unit}</td>
                  <td>${hit !== null ? `<span style="color:${hit >= 100 ? 'var(--green)' : hit >= 80 ? 'var(--amber)' : 'var(--red)'};font-weight:700;">${hit}%</span>` : "—"}</td>
                  <td>${kpi.period}</td>
                </tr>`;
              }).join("")}</tbody>
            </table></div>`;
          })()
        : emptyState("暂无经营数据", "当前项目暂未录入经营复盘指标")
      }
    </section>
  `;
}

function renderDashboardTeam() {
  // 按人员分组统计
  const userTasks = {};
  state.taskData.forEach((task) => {
    if (!userTasks[task.ownerId]) {
      userTasks[task.ownerId] = { tasks: [], risks: [], reviews: [] };
    }
    userTasks[task.ownerId].tasks.push(task);
  });
  state.riskData.forEach((risk) => {
    if (userTasks[risk.ownerId]) userTasks[risk.ownerId].risks.push(risk);
  });
  state.reviewData.forEach((review) => {
    // review 没有 ownerId，我们用 project 的 gtmOwnerId
    const project = state.projectData.find((p) => p.id === review.projectId);
    if (project && userTasks[project.gtmOwnerId]) {
      userTasks[project.gtmOwnerId].reviews.push(review);
    }
  });

  const userList = Object.entries(userTasks).sort((a, b) => b[1].tasks.length - a[1].tasks.length);

  return `
    <section class="grid metrics-grid">
      ${metricCard("团队成员", Object.keys(userTasks).length, "有任务分配")}
      ${metricCard("人均任务", Object.keys(userTasks).length ? Math.round(state.taskData.length / Object.keys(userTasks).length) : 0, "平均 workload")}
      ${metricCard("最高负荷", userList.length ? userList[0][1].tasks.length : 0, userList.length ? userName(userList[0][0]) : "—")}
      ${metricCard("全员完成率", state.taskData.length ? `${Math.round((state.taskData.filter((t) => t.status === "completed_task").length / state.taskData.length) * 100)}%` : "0%", "任务总体完成")}
    </section>

    <section class="content-grid">
      <div class="panel span-2">
        <div class="panel-header"><h2>成员任务负荷分布</h2></div>
        <div class="bar-list">
          ${userList.map(([uid, data]) => {
            const total = data.tasks.length;
            const done = data.tasks.filter((t) => t.status === "completed_task").length;
            const overdue = data.tasks.filter((t) => isTaskOverdue(t)).length;
            const percent = total ? Math.round((done / total) * 100) : 0;
            return `<div class="bar-row">
              <span style="display:flex;align-items:center;gap:6px;">
                <span style="width:8px;height:8px;border-radius:50%;background:${overdue > 0 ? '#ef4444' : percent >= 80 ? '#10b981' : '#f59e0b'}"></span>
                ${userName(uid)}
              </span>
              <div><i style="width:${percent}%;background:${overdue > 0 ? '#ef4444' : percent >= 80 ? '#10b981' : '#f59e0b'}"></i></div>
              <strong>${done}/${total}</strong>
            </div>`;
          }).join("")}
        </div>
      </div>
      <div class="panel">
        <div class="panel-header"><h2>成员风险承担</h2></div>
        <div class="stage-stack">
          ${userList.map(([uid, data]) => {
            const openRisks = data.risks.filter((r) => r.status !== "closed");
            return `<div class="stack-row">
              <span>${userName(uid)}</span>
              <div style="display:flex;align-items:center;gap:6px;">
                ${openRisks.length ? `<span style="font-size:11px;color:#dc2626;font-weight:600;">${openRisks.length} 开放</span>` : "<span style='font-size:11px;color:#059669;'>无风险</span>"}
              </div>
            </div>`;
          }).join("")}
        </div>
      </div>
    </section>

    <section class="panel">
      <div class="panel-header"><h2>成员任务明细</h2></div>
      <div class="table-wrap">
        <table>
          <thead>
            <tr><th>成员</th><th>任务数</th><th>已完成</th><th>进行中</th><th>超期</th><th>风险</th><th>负荷状态</th></tr>
          </thead>
          <tbody>
            ${userList.map(([uid, data]) => {
              const total = data.tasks.length;
              const done = data.tasks.filter((t) => t.status === "completed_task").length;
              const doing = data.tasks.filter((t) => t.status === "in_progress").length;
              const overdue = data.tasks.filter((t) => isTaskOverdue(t)).length;
              const risks = data.risks.filter((r) => r.status !== "closed").length;
              const loadStatus = overdue > 2 ? "过载" : overdue > 0 ? "紧张" : total > 5 ? "饱和" : "正常";
              const loadColor = overdue > 2 ? "red" : overdue > 0 ? "amber" : total > 5 ? "amber" : "green";
              return `<tr>
                <td><strong>${userName(uid)}</strong></td>
                <td>${total}</td>
                <td>${done}</td>
                <td>${doing}</td>
                <td>${overdue > 0 ? `<span style="color:#dc2626;font-weight:600;">${overdue}</span>` : "0"}</td>
                <td>${risks > 0 ? `<span style="color:#dc2626;font-weight:600;">${risks}</span>` : "0"}</td>
                <td><span style="font-size:11px;padding:2px 8px;border-radius:10px;font-weight:600;background:${loadColor === 'red' ? '#fee2e2' : loadColor === 'amber' ? '#fef3c7' : '#d1fae5'};color:${loadColor === 'red' ? '#dc2626' : loadColor === 'amber' ? '#b45309' : '#059669'}">${loadStatus}</span></td>
              </tr>`;
            }).join("")}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

/* ============================================
   Canvas 流程图
   ============================================ */
function drawIntroFlowCanvas() {
  const canvas = document.querySelector("#gtm-flow-canvas");
  if (!canvas) return;

  const parent = canvas.parentElement;
  const width = Math.max(parent.clientWidth, 1360);
  const dpr = window.devicePixelRatio || 1;

  const L1_TOP = 126;
  const L1_H = 80;
  const L2_TOP = L1_TOP + L1_H + 56;
  const L2_H = 230;
  const L3_TOP = L2_TOP + L2_H + 56;
  const L3_H = 80;
  const OBJ_TOP = L3_TOP + L3_H + 60;
  const OBJ_H = 72;
  const FB_Y = OBJ_TOP + OBJ_H + 50;
  const height = FB_Y + 60;

  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);

  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);

  canvasHitRegions = [];
  layerPositions = { l1: [], l2: [] };
  const hl = state.flowHighlightStage || "all";

  drawCanvasBackground(ctx, width, height);
  drawCanvasTitle(ctx);
  drawL1Flow(ctx, width, hl, L1_TOP, L1_H);
  drawL2Flow(ctx, width, hl, L2_TOP, L2_H);
  drawLayerConnectors(ctx, width, hl, "l1-l2");
  drawLayerConnectors(ctx, width, hl, "l2-l3");
  drawL3Execution(ctx, width, height, hl, L3_TOP, L3_H, OBJ_TOP, OBJ_H, FB_Y);
}

function drawCanvasBackground(ctx, width, height) {
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, "#f8fafc");
  gradient.addColorStop(0.55, "#f0fdfa");
  gradient.addColorStop(1, "#f8fafc");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = "rgba(13, 148, 136, 0.06)";
  ctx.lineWidth = 1;
  for (let x = 40; x < width; x += 48) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
  for (let y = 40; y < height; y += 48) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }
}

function drawCanvasTitle(ctx) {
  ctx.fillStyle = "#0f766e";
  ctx.font = "800 22px PingFang SC, Microsoft YaHei, sans-serif";
  ctx.fillText("九牧 GTM L1-L3 流程可视化", 32, 32);
  ctx.fillStyle = "#64748b";
  ctx.font = "13px PingFang SC, Microsoft YaHei, sans-serif";
  ctx.fillText("L1 七阶段主链路 → L2 九牧本地化动作与交付物 → L3 将IPD互锁、GR门禁和经营复盘固化到系统。", 32, 52);

  ctx.fillStyle = "rgba(13, 148, 136, 0.06)";
  ctx.fillRect(32, 64, 360, 28);
  ctx.fillStyle = "#0d9488";
  ctx.font = "800 10px PingFang SC, Microsoft YaHei, sans-serif";
  const legendItems = [
    { label: "L1 七阶段主链路", color: "#0d9488" },
    { label: "L2 阶段活动", color: "#3b82f6" },
    { label: "L3 执行闭环", color: "#d97706" },
  ];
  let lx = 44;
  legendItems.forEach((item) => {
    ctx.fillStyle = item.color;
    ctx.beginPath();
    ctx.arc(lx, 78, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#475569";
    ctx.font = "11px PingFang SC, Microsoft YaHei, sans-serif";
    ctx.fillText(item.label, lx + 8, 82);
    lx += ctx.measureText(item.label).width + 28;
  });
}

function drawL1Flow(ctx, width, highlightStage, cardTop, cardHeight) {
  const left = 32;
  const gap = 10;
  const cardWidth = (width - left * 2 - gap * 6) / 7;

  drawLaneLabel(ctx, "L1", "七阶段主流程", left, cardTop - 18, "#0d9488");

  stageOrder.forEach((stageKey, index) => {
    const x = left + index * (cardWidth + gap);
    const isPilotStage = index === 3;
    const isDimmed = highlightStage !== "all" && highlightStage !== stageKey;
    const isHighlighted = highlightStage === stageKey;

    canvasHitRegions.push({ x, y: cardTop, w: cardWidth, h: cardHeight, stage: stageKey });
    layerPositions.l1.push({ x, y: cardTop, w: cardWidth, h: cardHeight, stage: stageKey, cx: x + cardWidth / 2 });

    if (isDimmed) { ctx.save(); ctx.globalAlpha = 0.3; }

    const fill = isPilotStage ? "#0d9488" : (isHighlighted ? "#ccfbf1" : "#ffffff");
    const stroke = isPilotStage ? "#0d9488" : (isHighlighted ? "#0d9488" : "#e2e8f0");
    const lw = isHighlighted && !isPilotStage ? 2.5 : 1;
    roundedRect(ctx, x, cardTop, cardWidth, cardHeight, 10, fill, stroke, lw);

    if (isHighlighted && !isPilotStage) {
      ctx.shadowColor = "rgba(13,148,136,0.3)"; ctx.shadowBlur = 12; ctx.shadowOffsetY = 4;
      roundedRect(ctx, x, cardTop, cardWidth, cardHeight, 10, fill, stroke, lw);
      ctx.shadowColor = "transparent"; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    }

    const numColor = isPilotStage ? "#ffffff" : "#0d9488";
    const titleColor = isPilotStage ? "#ffffff" : "#0f172a";
    const subColor = isPilotStage ? "rgba(255,255,255,0.8)" : "#64748b";

    ctx.fillStyle = numColor;
    ctx.font = "900 11px sans-serif";
    ctx.fillText(String(index + 1).padStart(2, "0"), x + 10, cardTop + 18);

    ctx.fillStyle = titleColor;
    ctx.font = "800 13px PingFang SC, sans-serif";
    wrapText(ctx, stageLabels[stageKey], x + 10, cardTop + 36, cardWidth - 20, 16, 1);

    ctx.fillStyle = subColor;
    ctx.font = "11px PingFang SC, sans-serif";
    wrapText(ctx, stageGateLabels[stageKey], x + 10, cardTop + 56, cardWidth - 20, 14, 1);

    if (index < stageOrder.length - 1) {
      drawArrow(ctx, x + cardWidth + 1, cardTop + cardHeight / 2, x + cardWidth + gap - 2, cardTop + cardHeight / 2, "#0d9488");
    }

    if (isDimmed) { ctx.restore(); }
  });
}

function drawL2Flow(ctx, width, highlightStage, cardTop, cardHeight) {
  const left = 32;
  const gap = 10;
  const cardWidth = (width - left * 2 - gap * 6) / 7;

  drawLaneLabel(ctx, "L2", "阶段内全量子流程", left, cardTop - 18, "#3b82f6");

  stageOrder.forEach((stageKey, index) => {
    const x = left + index * (cardWidth + gap);
    const stage = stageLabels[stageKey];
    const output = introL2Flows[index]?.[2] || "";
    const items = l2SubProcesses[stageKey] || [];
    const isDimmed = highlightStage !== "all" && highlightStage !== stageKey;
    const isHighlighted = highlightStage === stageKey;

    canvasHitRegions.push({ x, y: cardTop, w: cardWidth, h: cardHeight, stage: stageKey });
    layerPositions.l2.push({ x, y: cardTop, w: cardWidth, h: cardHeight, stage: stageKey, cx: x + cardWidth / 2 });

    if (isDimmed) { ctx.save(); ctx.globalAlpha = 0.3; }

    const fill = isHighlighted ? "#dbeafe" : "#ffffff";
    const stroke = isHighlighted ? "#3b82f6" : "#e2e8f0";
    const lw = isHighlighted ? 2.5 : 1;
    roundedRect(ctx, x, cardTop, cardWidth, cardHeight, 10, fill, stroke, lw);

    if (isHighlighted) {
      ctx.shadowColor = "rgba(59,130,246,0.3)"; ctx.shadowBlur = 12; ctx.shadowOffsetY = 4;
      roundedRect(ctx, x, cardTop, cardWidth, cardHeight, 10, fill, stroke, lw);
      ctx.shadowColor = "transparent"; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    }

    ctx.fillStyle = "#3b82f6";
    ctx.font = "800 11px PingFang SC, sans-serif";
    ctx.fillText(stage, x + 10, cardTop + 16);

    items.forEach((item, i) => {
      const rowH = 24;
      const py = cardTop + 26 + i * rowH;
      roundedRect(ctx, x + 10, py, cardWidth - 20, rowH - 4, 8, "#eff6ff", "#bfdbfe");
      ctx.fillStyle = "#2563eb";
      ctx.font = "800 9px PingFang SC, sans-serif";
      ctx.fillText(String(i + 1), x + 17, py + 14);
      ctx.fillStyle = "#1d4ed8";
      ctx.font = "10px PingFang SC, sans-serif";
      wrapText(ctx, item.name, x + 30, py + 13, cardWidth - 44, 12, 1);

      canvasHitRegions.push({
        x: x + 10,
        y: py,
        w: cardWidth - 20,
        h: rowH,
        stage: stageKey,
        l2View: item.name,
        isL2Item: true,
      });
    });

    ctx.fillStyle = "#475569";
    ctx.font = "10px PingFang SC, sans-serif";
    wrapText(ctx, `输出：${output}`, x + 10, cardTop + cardHeight - 34, cardWidth - 20, 13, 2);

    if (isDimmed) { ctx.restore(); }
  });
}

function drawL3Execution(ctx, width, height, highlightStage, l3Top, l3H, objTop, objH, fbY) {
  const left = 32;
  const flowWidth = width - 64;
  const actionGap = 18;
  const actionWidth = (flowWidth - actionGap * 3) / 4;

  drawLaneLabel(ctx, "L3", "系统执行闭环", left, l3Top - 18, "#d97706");

  const isAnyDimmed = highlightStage !== "all";

  introL3Actions.forEach(([title, text], index) => {
    const x = left + index * (actionWidth + actionGap);
    if (isAnyDimmed) { ctx.save(); ctx.globalAlpha = 0.3; }

    roundedRect(ctx, x, l3Top, actionWidth, l3H, 12, "#fffbeb", "#fcd34d");

    ctx.fillStyle = "#d97706";
    ctx.font = "800 12px PingFang SC, sans-serif";
    ctx.fillText(title, x + 14, l3Top + 22);

    ctx.fillStyle = "#0f172a";
    ctx.font = "11px PingFang SC, sans-serif";
    wrapText(ctx, text, x + 14, l3Top + 42, actionWidth - 28, 14, 2);

    if (index < introL3Actions.length - 1) {
      drawArrow(ctx, x + actionWidth + 2, l3Top + l3H / 2, x + actionWidth + actionGap - 3, l3Top + l3H / 2, "#d97706");
    }
    if (isAnyDimmed) { ctx.restore(); }
  });

  const objects = [
    ["产品项目", "单品主线"],
    ["IPD互锁", "研发节点"],
    ["五点三包", "关键交付"],
    ["上市倒排", "节点推进"],
    ["阶段门禁", "GR1-GR6"],
    ["风险台账", "升级闭环"],
    ["经营复盘", "案例沉淀"],
  ];
  const objectGap = 8;
  const objectWidth = (flowWidth - objectGap * 6) / 7;

  objects.forEach(([title, text], index) => {
    const x = left + index * (objectWidth + objectGap);
    if (isAnyDimmed) { ctx.save(); ctx.globalAlpha = 0.3; }
    roundedRect(ctx, x, objTop, objectWidth, objH, 10, "#ffffff", "#e2e8f0");
    ctx.fillStyle = "#0f766e";
    ctx.font = "800 11px PingFang SC, sans-serif";
    wrapText(ctx, title, x + 10, objTop + 20, objectWidth - 20, 14, 1);
    ctx.fillStyle = "#64748b";
    ctx.font = "10px PingFang SC, sans-serif";
    wrapText(ctx, text, x + 10, objTop + 42, objectWidth - 20, 13, 2);
    if (isAnyDimmed) { ctx.restore(); }
  });

  if (isAnyDimmed) { ctx.save(); ctx.globalAlpha = 0.3; }
  drawFeedbackLoop(ctx, width / 2, fbY, Math.min(width - 160, 700));
  if (isAnyDimmed) { ctx.restore(); }
}

function drawLayerConnectors(ctx, width, hl, type) {
  if (hl !== "all") return;

  if (type === "l1-l2") {
    layerPositions.l1.forEach((l1) => {
      const l2 = layerPositions.l2.find((l) => l.stage === l1.stage);
      if (!l2) return;

      const x = l1.cx;
      const y1 = l1.y + l1.h;
      const y2 = l2.y;

      ctx.save();
      ctx.strokeStyle = "rgba(13, 148, 136, 0.25)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(x, y1 + 2);
      ctx.lineTo(x, y2 - 2);
      ctx.stroke();
      ctx.setLineDash([]);

      const midY = (y1 + y2) / 2;
      ctx.fillStyle = "rgba(13, 148, 136, 0.35)";
      ctx.beginPath();
      ctx.moveTo(x, y2 - 4);
      ctx.lineTo(x - 4, y2 - 10);
      ctx.lineTo(x + 4, y2 - 10);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    });
  }

  if (type === "l2-l3") {
    const l2Bottom = layerPositions.l2.length > 0
      ? Math.max(...layerPositions.l2.map((l) => l.y + l.h))
      : 440;
    const l3Cards = document.querySelector("#gtm-flow-canvas");
    const l3Top = l2Bottom + 56;
    const midY = (l2Bottom + l3Top) / 2;
    const centerX = width / 2;

    ctx.save();
    ctx.strokeStyle = "rgba(217, 119, 6, 0.2)";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);

    layerPositions.l2.forEach((l2) => {
      const x = l2.cx;
      const y1 = l2.y + l2.h;
      ctx.beginPath();
      ctx.moveTo(x, y1 + 2);
      ctx.quadraticCurveTo(x, midY, centerX, l3Top - 6);
      ctx.stroke();
    });

    ctx.setLineDash([]);
    ctx.fillStyle = "rgba(217, 119, 6, 0.3)";
    ctx.beginPath();
    ctx.moveTo(centerX, l3Top - 2);
    ctx.lineTo(centerX - 5, l3Top - 10);
    ctx.lineTo(centerX + 5, l3Top - 10);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = "rgba(217, 119, 6, 0.5)";
    ctx.font = "600 11px PingFang SC, Microsoft YaHei, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("\u25BC  \u8F93\u5165 \u2192 \u52A8\u4F5C \u2192 \u95E8\u7981 \u2192 \u8F93\u51FA", centerX, midY + 4);
    ctx.textAlign = "start";
    ctx.restore();
  }
}

function drawLaneLabel(ctx, code, title, x, y, color) {
  roundedRect(ctx, x, y - 16, 40, 22, 11, color, color);
  ctx.fillStyle = "#ffffff";
  ctx.font = "900 11px PingFang SC, Microsoft YaHei, sans-serif";
  ctx.fillText(code, x + 12, y - 2);
  ctx.fillStyle = "#0f172a";
  ctx.font = "800 14px PingFang SC, Microsoft YaHei, sans-serif";
  ctx.fillText(title, x + 50, y - 1);
}

function drawFeedbackLoop(ctx, centerX, y, width) {
  const left = centerX - width / 2;
  const right = centerX + width / 2;

  ctx.save();

  ctx.strokeStyle = "rgba(13, 148, 136, 0.12)";
  ctx.lineWidth = 24;
  ctx.beginPath();
  ctx.moveTo(right, y);
  ctx.bezierCurveTo(right - 100, y + 56, left + 100, y + 56, left, y);
  ctx.stroke();

  ctx.strokeStyle = "#0d9488";
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 6]);
  ctx.beginPath();
  ctx.moveTo(right, y);
  ctx.bezierCurveTo(right - 100, y + 56, left + 100, y + 56, left, y);
  ctx.stroke();
  ctx.setLineDash([]);

  drawArrow(ctx, left + 2, y, left + 40, y - 3, "#0d9488");

  ctx.textAlign = "center";
  ctx.fillStyle = "#0f766e";
  ctx.font = "800 14px PingFang SC, Microsoft YaHei, sans-serif";
  ctx.fillText("\u590D\u76D8\u6C89\u6DC0\u4E3A\u4E0B\u4E00\u7248\u6A21\u677F\u4E0E\u6D41\u7A0B\u89C4\u5219", centerX, y + 44);
  ctx.textAlign = "start";

  ctx.restore();
}

function roundedRect(ctx, x, y, width, height, radius, fill, stroke, lineWidth = 1) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
}

function drawArrow(ctx, fromX, fromY, toX, toY, color) {
  const angle = Math.atan2(toY - fromY, toX - fromX);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(toX, toY);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(toX, toY);
  ctx.lineTo(toX - 8 * Math.cos(angle - Math.PI / 6), toY - 8 * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(toX - 8 * Math.cos(angle + Math.PI / 6), toY - 8 * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 3) {
  const characters = String(text).split("");
  let line = "";
  let lineIndex = 0;

  for (let index = 0; index < characters.length; index += 1) {
    const character = characters[index];
    const testLine = line + character;
    const shouldWrap = ctx.measureText(testLine).width > maxWidth && line;

    if (shouldWrap) {
      if (lineIndex === maxLines - 1) {
        ctx.fillText(`${line.slice(0, Math.max(1, line.length - 1))}…`, x, y + lineIndex * lineHeight);
        return;
      }
      ctx.fillText(line, x, y + lineIndex * lineHeight);
      line = character;
      lineIndex += 1;
    } else {
      line = testLine;
    }

    if (index === characters.length - 1) {
      ctx.fillText(line, x, y + lineIndex * lineHeight);
    }
  }
}

function refreshProjectDetailDynamic() {
  const dynamic = document.querySelector("#project-detail-dynamic");
  const tabs = document.querySelector(".pd-detail-tabs");
  if (!dynamic || !tabs || state.route !== "project-detail") return false;

  const draft = document.createElement("div");
  draft.innerHTML = renderProjectDetail();
  const nextDynamic = draft.querySelector("#project-detail-dynamic");
  const nextTabs = draft.querySelector(".pd-detail-tabs");
  if (!nextDynamic || !nextTabs) return false;

  dynamic.innerHTML = nextDynamic.innerHTML;
  tabs.innerHTML = nextTabs.innerHTML;
  bindProjectDetailScopedEvents(document.querySelector(".pd-page"));
  return true;
}

function bindProjectDetailScopedEvents(root = document) {
  root.querySelectorAll("[data-project-detail-tab]").forEach((el) => {
    el.addEventListener("click", () => {
      state.projectDetailTab = el.dataset.projectDetailTab;
      persistState();
      if (!refreshProjectDetailDynamic()) render();
    });
  });

  root.querySelectorAll("[data-project-stage-detail]").forEach((el) => {
    el.addEventListener("click", () => {
      state.projectDetailStage = el.dataset.projectStageDetail;
      persistState();
      if (!refreshProjectDetailDynamic()) render();
    });
  });

  root.querySelector("[data-clear-stage-detail]")?.addEventListener("click", () => {
    state.projectDetailStage = null;
    persistState();
    if (!refreshProjectDetailDynamic()) render();
  });

  root.querySelectorAll("[data-route]").forEach((button) => {
    button.addEventListener("click", () => navigateTo(button.dataset.route));
  });

  root.querySelectorAll("[data-complete-task]").forEach((button) => {
    button.addEventListener("click", () => {
      const task = state.taskData.find((item) => item.id === button.dataset.completeTask);
      if (!task) return;
      task.status = "completed_task";
      task.completedAt = new Date().toISOString();
      persistState();
      showToast(`任务「${task.title}」已标记完成`, "success");
      if (!refreshProjectDetailDynamic()) render();
    });
  });

  root.querySelector("#quick-risk-panel")?.addEventListener("click", openRiskModal);
  root.querySelector("#new-task-btn-detail")?.addEventListener("click", openTaskModal);
  root.querySelector("#new-review-btn-detail")?.addEventListener("click", openReviewModal);
  root.querySelector("#decide-review-btn")?.addEventListener("click", openReviewDecisionModal);
  root.querySelectorAll("#apply-task-template").forEach((button) => {
    button.addEventListener("click", () => {
      applyLaunchPreparationTaskTemplate();
    });
  });
}

/* ============================================
   事件绑定
   ============================================ */
function bindEvents() {
  document.querySelectorAll("[data-route]").forEach((button) => {
    button.addEventListener("click", () => {
      if (button.dataset.route === "dashboards") {
        state.dashboardTab = "overview";
      }
      navigateTo(button.dataset.route);
    });
  });

  document.querySelector("#project-picker")?.addEventListener("change", (event) => {
    state.selectedProjectId = event.target.value;
    persistState();
    render();
  });

  document.querySelector("#task-project-filter")?.addEventListener("change", (event) => {
    state.taskProjectFilter = event.target.value;
    persistState();
    render();
  });

  document.querySelector("#task-status-filter")?.addEventListener("change", (event) => {
    state.taskStatusFilter = event.target.value;
    persistState();
    render();
  });

  document.querySelector("#task-priority-filter")?.addEventListener("change", (event) => {
    state.taskPriorityFilter = event.target.value;
    persistState();
    render();
  });

  document.querySelector("#template-stage-filter")?.addEventListener("change", (event) => {
    state.templateStageFilter = event.target.value;
    persistState();
    render();
  });

  document.querySelector("#project-stage-filter")?.addEventListener("change", (event) => {
    state.projectStageFilter = event.target.value;
    persistState();
    render();
  });

  document.querySelector("#project-status-filter")?.addEventListener("change", (event) => {
    state.projectStatusFilter = event.target.value;
    persistState();
    render();
  });

  document.querySelector("#project-category-filter")?.addEventListener("change", (event) => {
    state.projectCategoryFilter = event.target.value;
    persistState();
    render();
  });

  document.querySelectorAll("[data-filter-stage]").forEach((el) => {
    el.addEventListener("click", () => {
      state.templateStageFilter = el.dataset.filterStage;
      persistState();
      render();
    });
  });

  let searchTimer;
  document.querySelector("#task-search")?.addEventListener("input", (event) => {
    state.taskSearchQuery = event.target.value;
    persistState();
    clearTimeout(searchTimer);
    searchTimer = setTimeout(render, 200);
  });

  document.querySelector("#risk-search")?.addEventListener("input", (event) => {
    state.riskSearchQuery = event.target.value;
    persistState();
    clearTimeout(searchTimer);
    searchTimer = setTimeout(render, 200);
  });

  document.querySelectorAll("[data-complete-task]").forEach((button) => {
    button.addEventListener("click", () => {
      const task = state.taskData.find((item) => item.id === button.dataset.completeTask);
      task.status = "completed_task";
      task.completedAt = new Date().toISOString();
      persistState();
      showToast(`任务「${task.title}」已标记完成`, "success");
      if (state.route === "project-detail" && refreshProjectDetailDynamic()) return;
      render();
    });
  });

  document.querySelectorAll("[data-cancel-task]").forEach((button) => {
    button.addEventListener("click", () => {
      const task = state.taskData.find((item) => item.id === button.dataset.cancelTask);
      if (!task) return;
      task.status = "canceled";
      persistState();
      showToast(`任务「${task.title}」已取消`, "info");
      render();
    });
  });

  document.querySelectorAll("[data-ready-item]").forEach((button) => {
    button.addEventListener("click", () => {
      const item = state.readinessData.find((entry) => entry.id === button.dataset.readyItem);
      item.status = "ready";
      item.blockerNote = "";
      persistState();
      showToast(`「${item.dimension}」已标记已就绪`, "success");
      render();
    });
  });

  document.querySelectorAll("[data-close-risk]").forEach((button) => {
    button.addEventListener("click", () => {
      const risk = state.riskData.find((entry) => entry.id === button.dataset.closeRisk);
      if (!risk) return;
      openRiskCloseModal(risk);
    });
  });

  document.querySelectorAll("[data-select-project]").forEach((row) => {
    row.addEventListener("click", () => {
      state.selectedProjectId = row.dataset.selectProject;
      navigateTo("project-detail");
    });
  });

  document.querySelectorAll("#move-stage, #move-stage-inline").forEach((button) => button.addEventListener("click", () => {
    const project = state.projectData.find((entry) => entry.id === state.selectedProjectId);
    const moveCheck = canMoveToNextStage(project, state.reviewData, state.readinessData);
    if (moveCheck.allowed) {
      const next = nextStageKey(project.currentStage);
      if (next) {
        const fromStage = project.currentStage;
        state.stageHistory.push({
          id: `sh-${Date.now()}`,
          projectId: project.id,
          fromStage,
          toStage: next,
          operatorId: state.currentUserId,
          timestamp: new Date().toISOString(),
          note: `从「${stageLabels[fromStage]}」流转至「${stageLabels[next]}」`,
        });
        project.currentStage = next;
        persistState();
        showToast(`项目已流转至「${stageLabels[next]}」`, "success");
      }
    } else {
      showToast(moveCheck.reason, "error");
    }
    render();
  }));

  document.querySelectorAll("[data-open-project]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      state.selectedProjectId = button.dataset.openProject;
      navigateTo("project-detail");
    });
  });

  document.querySelectorAll("[data-project-view]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.projectViewMode = btn.dataset.projectView;
      persistState();
      render();
    });
  });

  document.querySelector("#quick-risk")?.addEventListener("click", openRiskModal);
  document.querySelector("#quick-risk-panel")?.addEventListener("click", openRiskModal);
  document.querySelector("#new-task-btn")?.addEventListener("click", openTaskModal);
  document.querySelector("#new-task-btn-inline")?.addEventListener("click", openTaskModal);
  document.querySelector("#new-task-btn-detail")?.addEventListener("click", openTaskModal);
  document.querySelector("#edit-project-btn")?.addEventListener("click", openProjectEditModal);
  document.querySelector("#delete-project-btn")?.addEventListener("click", openDeleteProjectModal);
  document.querySelector("#new-review-btn")?.addEventListener("click", openReviewModal);
  document.querySelector("#new-review-btn-detail")?.addEventListener("click", openReviewModal);
  document.querySelector("#decide-review-btn")?.addEventListener("click", openReviewDecisionModal);

  document.querySelector("#create-demo-project")?.addEventListener("click", () => {
    const id = `p-demo-${Date.now()}`;
    state.projectData.push({
      id,
      name: "新建GTM产品项目",
      productCategory: "待确认",
      targetChannel: "零售渠道",
      currentStage: "planning_initiation",
      status: "normal",
      gtmOwnerId: "u-gtm",
      productOwnerId: "u-product",
      plannedLaunchDate: "2026-09-30",
      commercialGoal: "用于验证GTM项目建项流程",
    });
    state.selectedProjectId = id;
    persistState();
    showToast("新项目已创建", "success");
    navigateTo("project-detail");
  });

  document.querySelectorAll("#apply-task-template").forEach((button) => {
    button.addEventListener("click", applyLaunchPreparationTaskTemplate);
  });

  document.querySelector("#countdown-gen-tasks")?.addEventListener("click", openCountdownGenModal);

  document.querySelectorAll("[data-cd-filter]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const domain = btn.dataset.cdFilter;
      document.querySelectorAll("[data-cd-filter]").forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      document.querySelectorAll(".cd-item").forEach((item) => {
        if (domain === "all" || item.dataset.cdDomain === domain) {
          item.style.display = "";
        } else {
          item.style.display = "none";
        }
      });
    });
  });

  document.querySelectorAll("[data-edit-template]").forEach((button) => {
    button.addEventListener("click", () => openTemplateEditModal(button.dataset.editTemplate));
  });

  document.querySelectorAll("[data-stage-detail]").forEach((el) => {
    el.addEventListener("click", () => {
      state.workbenchStageDetail = el.dataset.stageDetail;
      persistState();
      render();
    });
  });

  document.querySelectorAll("[data-goto-stage-detail]").forEach((el) => {
    el.addEventListener("click", () => {
      state.workbenchStageDetail = el.dataset.gotoStageDetail;
      persistState();
      render();
    });
  });

  document.querySelectorAll("[data-project-stage-detail]").forEach((el) => {
    el.addEventListener("click", () => {
      state.projectDetailStage = el.dataset.projectStageDetail;
      persistState();
      if (!refreshProjectDetailDynamic()) render();
    });
  });

  document.querySelector("[data-clear-stage-detail]")?.addEventListener("click", () => {
    state.projectDetailStage = null;
    persistState();
    if (!refreshProjectDetailDynamic()) render();
  });

  document.querySelectorAll("[data-dashboard-tab]").forEach((el) => {
    el.addEventListener("click", () => {
      state.dashboardTab = el.dataset.dashboardTab;
      persistState();
      render();
    });
  });

  document.querySelectorAll("[data-project-detail-tab]").forEach((el) => {
    el.addEventListener("click", () => {
      state.projectDetailTab = el.dataset.projectDetailTab;
      persistState();
      if (!refreshProjectDetailDynamic()) render();
    });
  });

  const canvas = document.querySelector("#gtm-flow-canvas");
  if (canvas) {
    canvas.style.cursor = "default";

    function getCanvasHit(e) {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;
      const dpr = window.devicePixelRatio || 1;
      return { x: x / dpr, y: y / dpr };
    }

    canvas.addEventListener("mousemove", (e) => {
      const { x, y } = getCanvasHit(e);
      let hovering = false;
      for (const region of canvasHitRegions) {
        if (x >= region.x && x <= region.x + region.w && y >= region.y && y <= region.y + region.h) {
          hovering = true;
          break;
        }
      }
      canvas.style.cursor = hovering ? "pointer" : "default";
    });

    canvas.addEventListener("click", (e) => {
      const { x, y } = getCanvasHit(e);

      // 优先检查 L2 子流程项（精准跳转到 L3 泳道图）
      for (const region of canvasHitRegions) {
        if (region.isL2Item && x >= region.x && x <= region.x + region.w && y >= region.y && y <= region.y + region.h) {
          state.stageL2View = region.l2View;
          persistState();
          navigateTo("stage-detail", { stageKey: region.stage });
          return;
        }
      }

      // 再检查 L1/L2 卡片（跳转到阶段总览）
      for (const region of canvasHitRegions) {
        if (!region.isL2Item && x >= region.x && x <= region.x + region.w && y >= region.y && y <= region.y + region.h) {
          navigateTo("stage-detail", { stageKey: region.stage });
          return;
        }
      }
    });
  }

  document.querySelector("#back-to-intro")?.addEventListener("click", () => navigateTo("intro"));
  document.querySelector("#back-to-stage")?.addEventListener("click", () => {
    state.stageL2View = null;
    persistState();
    render();
  });
  document.querySelectorAll("[data-goto-stage]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.stageL2View = null;
      navigateTo("stage-detail", { stageKey: btn.dataset.gotoStage });
    });
  });
  document.querySelectorAll("[data-stage-l2-jump]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.stageL2View = btn.dataset.l2View;
      persistState();
      navigateTo("stage-detail", { stageKey: btn.dataset.stageL2Jump });
    });
  });
  document.querySelectorAll("[data-l2-view]").forEach((card) => {
    card.addEventListener("click", () => {
      state.stageL2View = card.dataset.l2View;
      persistState();
      render();
    });
  });

  /* 登录表单提交 */
  document.querySelector("#login-form")?.addEventListener("submit", (e) => {
    e.preventDefault();
    const username = document.querySelector("#login-username")?.value.trim();
    const password = document.querySelector("#login-password")?.value;
    const user = users.find((u) => u.username === username && u.password === password);
    if (user) {
      state.currentUserId = user.id;
      persistState();
      showToast(`欢迎回来，${user.name}`, "success");
      const target = location.hash.slice(1);
      if (validRoutes.includes(target) && target !== "intro" && target !== "login") {
        navigateTo(target);
      } else {
        navigateTo("workbench");
      }
    } else {
      showToast("用户名或密码错误", "error");
    }
  });

  /* 测试账号快速填入 */
  document.querySelectorAll("[data-demo-user]").forEach((tag) => {
    tag.addEventListener("click", () => {
      const usernameInput = document.querySelector("#login-username");
      const passwordInput = document.querySelector("#login-password");
      if (usernameInput) usernameInput.value = tag.dataset.demoUser;
      if (passwordInput) {
        passwordInput.value = tag.dataset.demoUser === "admin" ? "admin" : "123456";
      }
      usernameInput?.focus();
    });
  });

  document.querySelector("#sidebar-toggle")?.addEventListener("click", () => {
    state.sidebarCollapsed = !state.sidebarCollapsed;
    persistState();
    render();
  });

  /* 流程阶段导航：平滑滚动 + 滚动高亮 */
  document.querySelectorAll(".flow-nav-item").forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const target = document.querySelector(link.getAttribute("href"));
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  });

  const stagePanels = document.querySelectorAll(".flow-stage-panel");
  const navItems = document.querySelectorAll(".flow-nav-item");
  if (stagePanels.length && navItems.length) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const stageKey = entry.target.id.replace("flow-stage-", "");
            navItems.forEach((nav) => nav.classList.toggle("active", nav.dataset.navStage === stageKey));
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    stagePanels.forEach((panel) => observer.observe(panel));
  }

  document.querySelector("#logout-btn")?.addEventListener("click", () => {
    state.currentUserId = null;
    persistState();
    navigateTo("intro");
  });

  /* 用户下拉菜单 */
  const dropdown = document.querySelector("#user-dropdown");
  const trigger = document.querySelector("#user-menu-trigger");

  trigger?.addEventListener("click", (e) => {
    e.stopPropagation();
    dropdown?.classList.toggle("open");
  });

  if (dropdown && !window._gtmDropdownBound) {
    window._gtmDropdownBound = true;
    document.addEventListener("click", (e) => {
      if (!dropdown.contains(e.target) && e.target !== trigger) {
        dropdown.classList.remove("open");
      }
    });
  }

  document.querySelectorAll("[data-mark-read]").forEach((button) => {
    button.addEventListener("click", () => {
      const msg = state.messageData.find((m) => m.id === button.dataset.markRead);
      if (msg) {
        msg.read = true;
        persistState();
        render();
      }
    });
  });

  document.querySelector("#mark-all-read")?.addEventListener("click", () => {
    state.messageData.forEach((msg) => {
      if (msg.userId === state.currentUserId) msg.read = true;
    });
    persistState();
    showToast("全部消息已标记为已读", "success");
    render();
  });

  document.querySelector("#reset-demo-data")?.addEventListener("click", () => {
    localStorage.removeItem(storageKey);
    showToast("演示数据已重置，页面即将刷新", "info");
    setTimeout(() => location.reload(), 800);
  });
}

function addDemoRisk() {
  const project = state.projectData.find((entry) => entry.id === state.selectedProjectId);
  state.riskData.unshift({
    id: `risk-${Date.now()}`,
    projectId: state.selectedProjectId,
    stageKey: project?.currentStage || "planning_initiation",
    domain: "GTM",
    type: "风险",
    title: "新增待确认协同事项",
    ownerId: "u-gtm",
    dueDate: "2026-05-18",
    priority: "P2",
    status: "open",
    escalationFlag: false,
  });
  persistState();
  showToast("新风险已添加", "info");
  navigateTo("risks");
}

const domainOptions = ["GTM", "市场营销", "零售", "渠道", "电商", "服务", "财经", "IT", "供应链"];
const priorityOptions = ["P0", "P1", "P2", "P3"];

function userOptions(selectedId) {
  return users.map((u) => `<option value="${u.id}" ${u.id === selectedId ? "selected" : ""}>${u.name} (${u.role})</option>`).join("");
}

function projectOptions(selectedId) {
  return state.projectData.map((p) => `<option value="${p.id}" ${p.id === selectedId ? "selected" : ""}>${escapeHtml(p.name)}</option>`).join("");
}

function stageOptions(selectedKey) {
  return stageOrder.map((k) => `<option value="${k}" ${k === selectedKey ? "selected" : ""}>${stageLabels[k]}</option>`).join("");
}

function openTaskModal() {
  const project = state.projectData.find((p) => p.id === state.selectedProjectId);
  const defaultStage = project?.currentStage || "planning_initiation";
  const today = new Date();
  const defaultDue = new Date(today.getTime() + 14 * 86400000).toISOString().slice(0, 10);

  const body = `
    <div class="form-row">
      <div class="form-group">
        <label>所属项目 *</label>
        <select id="form-task-project">${projectOptions(state.selectedProjectId)}</select>
      </div>
      <div class="form-group">
        <label>阶段 *</label>
        <select id="form-task-stage">${stageOptions(defaultStage)}</select>
      </div>
    </div>
    <div class="form-group">
      <label>任务标题 *</label>
      <input type="text" id="form-task-title" placeholder="输入任务标题" />
    </div>
    <div class="form-row">
      <div class="form-group">
        <label>业务域 *</label>
        <select id="form-task-domain">${domainOptions.map((d) => `<option value="${d}">${d}</option>`).join("")}</select>
      </div>
      <div class="form-group">
        <label>优先级</label>
        <select id="form-task-priority">${priorityOptions.map((p) => `<option value="${p}" ${p === "P2" ? "selected" : ""}>${p}</option>`).join("")}</select>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label>责任人 *</label>
        <select id="form-task-owner">${userOptions(state.currentUserId)}</select>
      </div>
      <div class="form-group">
        <label>截止时间 *</label>
        <input type="date" id="form-task-due" value="${defaultDue}" />
      </div>
    </div>
  `;

  const footer = `
    <button class="ghost-action" onclick="document.querySelector('.modal-overlay')?.remove()">取消</button>
    <button class="primary-action" id="form-task-submit">创建任务</button>
  `;

  openModal("新建任务", body, footer);

  document.querySelector("#form-task-submit")?.addEventListener("click", () => {
    const title = document.querySelector("#form-task-title")?.value.trim();
    const projectId = document.querySelector("#form-task-project")?.value;
    const stageKey = document.querySelector("#form-task-stage")?.value;
    const domain = document.querySelector("#form-task-domain")?.value;
    const ownerId = document.querySelector("#form-task-owner")?.value;
    const dueDate = document.querySelector("#form-task-due")?.value;
    const priority = document.querySelector("#form-task-priority")?.value;

    if (!title) { showToast("请输入任务标题", "error"); return; }
    if (!dueDate) { showToast("请选择截止时间", "error"); return; }

    state.taskData.push({
      id: `t-${Date.now()}`,
      projectId,
      stageKey,
      domain,
      title,
      ownerId,
      dueDate,
      status: "not_started",
      priority: priority || "P2",
    });
    persistState();
    closeModal();
    showToast(`任务「${title}」已创建`, "success");
    render();
  });
}

function openRiskModal() {
  const project = state.projectData.find((p) => p.id === state.selectedProjectId);
  const defaultStage = project?.currentStage || "planning_initiation";
  const today = new Date();
  const defaultDue = new Date(today.getTime() + 7 * 86400000).toISOString().slice(0, 10);

  const body = `
    <div class="form-row">
      <div class="form-group">
        <label>所属项目 *</label>
        <select id="form-risk-project">${projectOptions(state.selectedProjectId)}</select>
      </div>
      <div class="form-group">
        <label>阶段 *</label>
        <select id="form-risk-stage">${stageOptions(defaultStage)}</select>
      </div>
    </div>
    <div class="form-group">
      <label>风险标题 *</label>
      <input type="text" id="form-risk-title" placeholder="输入风险/问题标题" />
    </div>
    <div class="form-row">
      <div class="form-group">
        <label>业务域 *</label>
        <select id="form-risk-domain">${domainOptions.map((d) => `<option value="${d}">${d}</option>`).join("")}</select>
      </div>
      <div class="form-group">
        <label>优先级 *</label>
        <select id="form-risk-priority">${priorityOptions.map((p) => `<option value="${p}" ${p === "P2" ? "selected" : ""}>${p}</option>`).join("")}</select>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label>责任人 *</label>
        <select id="form-risk-owner">${userOptions("u-gtm")}</select>
      </div>
      <div class="form-group">
        <label>截止时间 *</label>
        <input type="date" id="form-risk-due" value="${defaultDue}" />
      </div>
    </div>
    <div class="form-group">
      <label>风险描述</label>
      <textarea id="form-risk-desc" placeholder="描述风险详情、影响范围和已采取措施"></textarea>
    </div>
    <div class="form-group">
      <label><input type="checkbox" id="form-risk-escalate" /> 标记为升级（需要管理层关注）</label>
    </div>
  `;

  const footer = `
    <button class="ghost-action" onclick="document.querySelector('.modal-overlay')?.remove()">取消</button>
    <button class="primary-action" id="form-risk-submit">创建风险</button>
  `;

  openModal("新增风险", body, footer);

  document.querySelector("#form-risk-submit")?.addEventListener("click", () => {
    const title = document.querySelector("#form-risk-title")?.value.trim();
    const projectId = document.querySelector("#form-risk-project")?.value;
    const stageKey = document.querySelector("#form-risk-stage")?.value;
    const domain = document.querySelector("#form-risk-domain")?.value;
    const ownerId = document.querySelector("#form-risk-owner")?.value;
    const dueDate = document.querySelector("#form-risk-due")?.value;
    const priority = document.querySelector("#form-risk-priority")?.value;
    const description = document.querySelector("#form-risk-desc")?.value.trim();
    const escalate = document.querySelector("#form-risk-escalate")?.checked;

    if (!title) { showToast("请输入风险标题", "error"); return; }
    if (!dueDate) { showToast("请选择截止时间", "error"); return; }

    state.riskData.unshift({
      id: `risk-${Date.now()}`,
      projectId,
      stageKey,
      domain,
      type: "风险",
      title,
      ownerId,
      dueDate,
      priority: priority || "P2",
      status: "open",
      escalationFlag: !!escalate,
      description: description || "",
      createdAt: new Date().toISOString(),
    });
    persistState();
    closeModal();
    showToast(`风险「${title}」已创建`, "success");
    render();
  });
}

function openReviewModal() {
  const project = state.projectData.find((p) => p.id === state.selectedProjectId);
  const defaultStage = project?.currentStage || "planning_initiation";
  const today = new Date();
  const defaultDate = new Date(today.getTime() + 7 * 86400000).toISOString().slice(0, 10);

  const reviewTypes = [
    ["initiation_review", "立项评审"],
    ["launch_path_review", "上市路径评审"],
    ["launch_readiness_review", "上市准备评审"],
    ["first_sales_review", "首销复盘"],
  ];

  const body = `
    <div class="form-row">
      <div class="form-group">
        <label>所属项目 *</label>
        <select id="form-review-project">${projectOptions(state.selectedProjectId)}</select>
      </div>
      <div class="form-group">
        <label>阶段 *</label>
        <select id="form-review-stage">${stageOptions(defaultStage)}</select>
      </div>
    </div>
    <div class="form-group">
      <label>评审标题 *</label>
      <input type="text" id="form-review-title" placeholder="输入评审名称" />
    </div>
    <div class="form-row">
      <div class="form-group">
        <label>评审类型 *</label>
        <select id="form-review-type">${reviewTypes.map(([v, l]) => `<option value="${v}">${l}</option>`).join("")}</select>
      </div>
      <div class="form-group">
        <label>计划时间 *</label>
        <input type="date" id="form-review-date" value="${defaultDate}" />
      </div>
    </div>
    <div class="form-group">
      <label>评审说明</label>
      <textarea id="form-review-note" placeholder="评审范围、关注点等"></textarea>
    </div>
  `;

  const footer = `
    <button class="ghost-action" onclick="document.querySelector('.modal-overlay')?.remove()">取消</button>
    <button class="primary-action" id="form-review-submit">创建评审</button>
  `;

  openModal("创建评审", body, footer);

  document.querySelector("#form-review-submit")?.addEventListener("click", () => {
    const title = document.querySelector("#form-review-title")?.value.trim();
    const projectId = document.querySelector("#form-review-project")?.value;
    const stageKey = document.querySelector("#form-review-stage")?.value;
    const reviewType = document.querySelector("#form-review-type")?.value;
    const scheduledAt = document.querySelector("#form-review-date")?.value;
    const note = document.querySelector("#form-review-note")?.value.trim();

    if (!title) { showToast("请输入评审标题", "error"); return; }
    if (!scheduledAt) { showToast("请选择计划时间", "error"); return; }

    state.reviewData.push({
      id: `rv-${Date.now()}`,
      projectId,
      stageKey,
      reviewType,
      title,
      scheduledAt,
      status: "open",
      decision: "pending",
      decisionNote: note || "",
    });
    persistState();
    closeModal();
    showToast(`评审「${title}」已创建`, "success");
    render();
  });
}

function openReviewDecisionModal() {
  const openReviews = state.reviewData.filter((r) => r.status === "open");
  if (!openReviews.length) {
    showToast("当前没有待处理的评审", "info");
    return;
  }

  function getDeliverableChecklist(reviewId) {
    const review = state.reviewData.find((r) => r.id === reviewId);
    if (!review) return "";
    const stageItems = checklistItems.filter((i) => i.stageKey === review.stageKey);
    const stageTasks = state.taskData.filter((t) => t.projectId === review.projectId && t.stageKey === review.stageKey);
    const deliverables = [...new Set(stageItems.map((i) => i.deliverable).filter(Boolean))];
    if (!deliverables.length) return '<p class="text-muted">该阶段暂无交付物定义</p>';
    return deliverables.map((d) => {
      const related = stageTasks.filter((t) => t.title.includes(d.slice(0, 6)));
      const done = related.some((t) => t.status === "completed_task");
      return `<div class="stack-row"><span>${d}</span>${done ? '<span style="color:var(--green);font-size:12px;">已提交</span>' : '<span style="color:var(--red);font-size:12px;">未提交</span>'}</div>`;
    }).join("");
  }

  const firstReview = openReviews[0];

  const body = `
    <div class="form-group">
      <label>选择评审 *</label>
      <select id="form-decide-review">
        ${openReviews.map((r) => `<option value="${r.id}">${escapeHtml(r.title)} (${projectName(r.projectId)})</option>`).join("")}
      </select>
    </div>
    <div id="deliverable-check-section" class="form-group">
      <label>交付物齐套检查</label>
      <div class="stage-stack" id="deliverable-checklist">${getDeliverableChecklist(firstReview.id)}</div>
    </div>
    <div class="form-group">
      <label>评审结论 *</label>
      <div class="decision-options">
        <div class="decision-option selected" data-decision="approved">
          <strong>通过</strong>
          <small>允许进入下一阶段</small>
        </div>
        <div class="decision-option" data-decision="conditionally_approved">
          <strong>有条件通过</strong>
          <small>需完成附加待办</small>
        </div>
        <div class="decision-option" data-decision="deferred">
          <strong>暂缓</strong>
          <small>推迟决策，补充材料</small>
        </div>
        <div class="decision-option" data-decision="rejected">
          <strong>不通过</strong>
          <small>阻断阶段流转</small>
        </div>
      </div>
    </div>
    <div id="conditional-section" style="display:none;">
      <div class="conditional-tasks">
        <h4>有条件通过 — 待办事项</h4>
        <div id="conditional-task-list">
          <div class="form-row" style="margin-bottom:8px;">
            <div class="form-group" style="margin-bottom:0;">
              <input type="text" class="cond-task-title" placeholder="待办事项标题" />
            </div>
            <div class="form-group" style="margin-bottom:0;">
              <select class="cond-task-owner">${userOptions(state.currentUserId)}</select>
            </div>
          </div>
        </div>
        <button class="ghost-action" id="add-cond-task" style="margin-top:4px;">+ 添加待办</button>
      </div>
    </div>
    <div class="form-group" style="margin-top:12px;">
      <label>结论说明</label>
      <textarea id="form-decide-note" placeholder="填写评审结论说明"></textarea>
    </div>
  `;

  const footer = `
    <button class="ghost-action" onclick="document.querySelector('.modal-overlay')?.remove()">取消</button>
    <button class="primary-action" id="form-decide-submit">确认结论</button>
  `;

  openModal("处理评审结论", body, footer);

  let selectedDecision = "approved";

  document.querySelector("#form-decide-review")?.addEventListener("change", (e) => {
    document.querySelector("#deliverable-checklist").innerHTML = getDeliverableChecklist(e.target.value);
  });

  document.querySelectorAll(".decision-option").forEach((opt) => {
    opt.addEventListener("click", () => {
      document.querySelectorAll(".decision-option").forEach((o) => o.classList.remove("selected"));
      opt.classList.add("selected");
      selectedDecision = opt.dataset.decision;
      document.querySelector("#conditional-section").style.display = selectedDecision === "conditionally_approved" ? "block" : "none";
    });
  });

  document.querySelector("#add-cond-task")?.addEventListener("click", () => {
    const list = document.querySelector("#conditional-task-list");
    const row = document.createElement("div");
    row.className = "form-row";
    row.style.marginBottom = "8px";
    row.innerHTML = `
      <div class="form-group" style="margin-bottom:0;">
        <input type="text" class="cond-task-title" placeholder="待办事项标题" />
      </div>
      <div class="form-group" style="margin-bottom:0;">
        <select class="cond-task-owner">${userOptions(state.currentUserId)}</select>
      </div>
    `;
    list.appendChild(row);
  });

  document.querySelector("#form-decide-submit")?.addEventListener("click", () => {
    const reviewId = document.querySelector("#form-decide-review")?.value;
    const note = document.querySelector("#form-decide-note")?.value.trim();
    const review = state.reviewData.find((r) => r.id === reviewId);
    if (!review) return;

    const decisionLabels = {
      approved: "通过",
      conditionally_approved: "有条件通过",
      deferred: "暂缓",
      rejected: "不通过",
    };

    review.decision = selectedDecision;
    review.decisionNote = note || `评审结论：${decisionLabels[selectedDecision]}`;

    if (selectedDecision === "approved" || selectedDecision === "conditionally_approved") {
      review.status = "closed";
    }

    if (selectedDecision === "conditionally_approved") {
      const condTitles = document.querySelectorAll(".cond-task-title");
      const condOwners = document.querySelectorAll(".cond-task-owner");
      let added = 0;
      condTitles.forEach((input, i) => {
        const title = input.value.trim();
        if (!title) return;
        state.taskData.push({
          id: `t-cond-${Date.now()}-${i}`,
          projectId: review.projectId,
          stageKey: review.stageKey,
          domain: "GTM",
          title: `[评审待办] ${title}`,
          ownerId: condOwners[i]?.value || state.currentUserId,
          dueDate: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
          status: "not_started",
          priority: "P1",
          sourceReviewId: review.id,
        });
        added++;
      });
      if (added > 0) {
        showToast(`评审结论已记录，已生成 ${added} 条待办任务`, "success");
      } else {
        showToast("评审结论已记录（有条件通过，但未添加待办事项）", "info");
      }
    } else if (selectedDecision === "rejected") {
      state.taskData.push({
        id: `t-rectify-${Date.now()}`,
        projectId: review.projectId,
        stageKey: review.stageKey,
        domain: "GTM",
        title: `[整改] ${review.title} — 评审不通过，需整改`,
        ownerId: state.projectData.find((p) => p.id === review.projectId)?.gtmOwnerId || "u-gtm",
        dueDate: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
        status: "not_started",
        priority: "P0",
        sourceReviewId: review.id,
      });
      showToast("评审不通过，已生成整改任务", "error");
    } else {
      showToast(`评审结论已记录：${decisionLabels[selectedDecision]}`, "success");
    }

    persistState();
    closeModal();
    render();
  });
}

const domainToOwnerMap = {
  GTM: "u-gtm",
  "市场营销": "u-marketing",
  "零售": "u-retail",
  "渠道": "u-channel",
  "电商": "u-ecom",
  "服务": "u-service",
  "财经": "u-finance",
  "IT": "u-it",
  "供应链": "u-gtm",
  "运营": "u-gtm",
};

function openProjectEditModal() {
  const project = state.projectData.find((p) => p.id === state.selectedProjectId);
  if (!project) return;

  const body = `
    <div class="form-group">
      <label>项目名称</label>
      <input type="text" id="form-proj-name" value="${escapeHtml(project.name)}" />
    </div>
    <div class="form-group">
      <label>商业目标</label>
      <textarea id="form-proj-goal">${escapeHtml(project.commercialGoal)}</textarea>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label>品类</label>
        <input type="text" id="form-proj-category" value="${escapeHtml(project.productCategory)}" />
      </div>
      <div class="form-group">
        <label>目标渠道</label>
        <input type="text" id="form-proj-channel" value="${escapeHtml(project.targetChannel)}" />
      </div>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label>计划上市日期</label>
        <input type="date" id="form-proj-launch" value="${project.plannedLaunchDate}" />
      </div>
      <div class="form-group">
        <label>GTM负责人</label>
        <select id="form-proj-gtm">${userOptions(project.gtmOwnerId)}</select>
      </div>
    </div>
    <div class="form-group">
      <label>产品负责人</label>
      <select id="form-proj-product">${userOptions(project.productOwnerId)}</select>
    </div>
  `;

  const footer = `
    <button class="ghost-action" onclick="document.querySelector('.modal-overlay')?.remove()">取消</button>
    <button class="primary-action" id="form-proj-save">保存</button>
  `;

  openModal("编辑项目", body, footer);

  document.querySelector("#form-proj-save")?.addEventListener("click", () => {
    const name = document.querySelector("#form-proj-name")?.value.trim();
    const goal = document.querySelector("#form-proj-goal")?.value.trim();
    if (!name) { showToast("项目名称不能为空", "error"); return; }
    project.name = name;
    project.commercialGoal = goal || project.commercialGoal;
    project.productCategory = document.querySelector("#form-proj-category")?.value.trim() || project.productCategory;
    project.targetChannel = document.querySelector("#form-proj-channel")?.value.trim() || project.targetChannel;
    project.plannedLaunchDate = document.querySelector("#form-proj-launch")?.value || project.plannedLaunchDate;
    project.gtmOwnerId = document.querySelector("#form-proj-gtm")?.value || project.gtmOwnerId;
    project.productOwnerId = document.querySelector("#form-proj-product")?.value || project.productOwnerId;
    persistState();
    closeModal();
    showToast("项目信息已更新", "success");
    render();
  });
}

function openDeleteProjectModal() {
  const project = state.projectData.find((p) => p.id === state.selectedProjectId);
  if (!project) return;

  const body = `
    <div class="form-group">
      <p>确定要移除项目 <strong>${escapeHtml(project.name)}</strong> 吗？</p>
      <p style="font-size:12px;color:var(--text-tertiary);margin-top:8px;">此操作将同时删除该项目的全部任务、风险、评审、成熟度数据和流转记录，且不可恢复。</p>
    </div>
  `;

  const footer = `
    <button class="ghost-action" onclick="document.querySelector('.modal-overlay')?.remove()">取消</button>
    <button class="danger-action" id="form-proj-delete">确认移除</button>
  `;

  openModal("移除项目", body, footer);

  document.querySelector("#form-proj-delete")?.addEventListener("click", () => {
    const pid = project.id;
    state.projectData = state.projectData.filter((p) => p.id !== pid);
    state.taskData = state.taskData.filter((t) => t.projectId !== pid);
    state.riskData = state.riskData.filter((r) => r.projectId !== pid);
    state.reviewData = state.reviewData.filter((r) => r.projectId !== pid);
    state.readinessData = state.readinessData.filter((r) => r.projectId !== pid);
    state.stageHistory = state.stageHistory.filter((h) => h.projectId !== pid);
    if (state.kpiData) state.kpiData = state.kpiData.filter((k) => k.projectId !== pid);
    state.selectedProjectId = state.projectData.length ? state.projectData[0].id : null;
    persistState();
    closeModal();
    showToast(`项目「${project.name}」已移除`, "info");
    navigateTo("projects");
  });
}

function openRiskCloseModal(risk) {
  const body = `
    <div class="form-group">
      <label>风险标题</label>
      <p><strong>${escapeHtml(risk.title)}</strong></p>
      <p style="font-size:12px;color:var(--text-tertiary);">${risk.domain} · ${priorityBadge(risk.priority)} · ${userName(risk.ownerId)}</p>
    </div>
    <div class="form-group">
      <label>关闭说明 *</label>
      <textarea id="form-risk-closure-note" placeholder="说明风险关闭的原因和处理过程"></textarea>
    </div>
    <div class="form-group">
      <label>复盘结论</label>
      <textarea id="form-risk-review-note" placeholder="总结经验教训、改进措施，供后续项目参考"></textarea>
    </div>
  `;

  const footer = `
    <button class="ghost-action" onclick="document.querySelector('.modal-overlay')?.remove()">取消</button>
    <button class="primary-action" id="form-risk-close-submit">确认关闭</button>
  `;

  openModal("关闭风险", body, footer);

  document.querySelector("#form-risk-close-submit")?.addEventListener("click", () => {
    const closureNote = document.querySelector("#form-risk-closure-note")?.value.trim();
    const reviewNote = document.querySelector("#form-risk-review-note")?.value.trim();
    if (!closureNote) { showToast("请填写关闭说明", "error"); return; }
    risk.status = "closed";
    risk.closureNote = closureNote;
    risk.reviewNote = reviewNote || "";
    risk.closedAt = new Date().toISOString();
    persistState();
    closeModal();
    showToast(`风险「${risk.title}」已关闭`, "success");
    render();
  });
}

function openCountdownGenModal() {
  const body = `
    <div class="form-group">
      <label>选择项目 *</label>
      <select id="form-countdown-project">${projectOptions(state.selectedProjectId)}</select>
    </div>
    <div class="form-group">
      <label>生成模式</label>
      <div class="decision-options">
        <div class="decision-option selected" data-gen-mode="batch">
          <strong>一键批量生成</strong>
          <small>全部8周动作生成为任务</small>
        </div>
        <div class="decision-option" data-gen-mode="confirm">
          <strong>逐条确认</strong>
          <small>预览后确认生成</small>
        </div>
      </div>
    </div>
    <p style="font-size:12px;color:var(--text-tertiary);margin-top:8px;">将根据项目上市日期自动反推8周时间窗口，按业务域映射责任人。</p>
  `;

  const footer = `
    <button class="ghost-action" onclick="document.querySelector('.modal-overlay')?.remove()">取消</button>
    <button class="primary-action" id="form-countdown-submit">生成任务</button>
  `;

  openModal("倒排计划生成任务", body, footer);

  let genMode = "batch";
  document.querySelectorAll(".decision-option[data-gen-mode]").forEach((opt) => {
    opt.addEventListener("click", () => {
      document.querySelectorAll(".decision-option[data-gen-mode]").forEach((o) => o.classList.remove("selected"));
      opt.classList.add("selected");
      genMode = opt.dataset.genMode;
    });
  });

  document.querySelector("#form-countdown-submit")?.addEventListener("click", () => {
    const projectId = document.querySelector("#form-countdown-project")?.value;
    const project = state.projectData.find((p) => p.id === projectId);
    if (!project) return;

    const launchDate = new Date(project.plannedLaunchDate);
    const existingTitles = new Set(state.taskData.filter((t) => t.projectId === projectId).map((t) => t.title));
    let added = 0;

    countdownPlan.forEach((week, weekIdx) => {
      const weekOffset = (8 - weekIdx) * 7;
      const weekDate = new Date(launchDate.getTime() - weekOffset * 86400000);
      const dueDate = weekDate.toISOString().slice(0, 10);

      week.items.forEach((item, itemIdx) => {
        const title = `[${week.week}] ${item.action}`;
        if (existingTitles.has(title)) return;

        state.taskData.push({
          id: `t-cd-${Date.now()}-${weekIdx}-${itemIdx}`,
          projectId,
          stageKey: project.currentStage,
          domain: item.domain,
          title,
          ownerId: domainToOwnerMap[item.domain] || "u-gtm",
          dueDate,
          status: "not_started",
          priority: "P2",
        });
        added++;
      });
    });

    persistState();
    closeModal();
    showToast(added > 0 ? `已根据倒排计划生成 ${added} 条任务` : "任务已存在，未重复添加", added > 0 ? "success" : "info");
    navigateTo("project-detail");
  });
}

function applyLaunchPreparationTaskTemplate() {
  const template = templates.find((item) => item.id === "tpl-task-launch-prep");
  const project = state.projectData.find((entry) => entry.id === state.selectedProjectId);
  const existingTitles = new Set(state.taskData.filter((task) => task.projectId === project.id).map((task) => task.title));
  let added = 0;
  template.items.forEach((item, index) => {
    if (existingTitles.has(item.title)) return;
    state.taskData.push({
      id: `t-template-${Date.now()}-${index}`,
      projectId: project.id,
      stageKey: "launch_preparation",
      domain: item.domain,
      title: item.title,
      ownerId: item.ownerId,
      dueDate: "2026-05-22",
      status: "not_started",
      priority: item.priority,
    });
    added++;
  });
  persistState();
  showToast(added > 0 ? `已套用 ${added} 条任务模板` : "任务已存在，未重复添加", added > 0 ? "success" : "info");
  if (state.route === "project-detail") {
    if (!refreshProjectDetailDynamic()) render();
  } else {
    navigateTo("project-detail");
  }
}

function openTemplateEditModal(templateId) {
  const template = templates.find((t) => t.id === templateId);
  if (!template) return;

  const isTaskTemplate = typeof template.items[0] === "object";
  const itemsJson = JSON.stringify(template.items, null, 2);

  const body = `
    <div class="form-group">
      <label>模板名称</label>
      <input type="text" id="form-tpl-name" value="${escapeHtml(template.name)}" />
    </div>
    <div class="form-group">
      <label>模板条目 (JSON)</label>
      <textarea id="form-tpl-items" style="min-height:200px;font-family:monospace;font-size:12px;">${escapeHtml(itemsJson)}</textarea>
    </div>
    <p style="font-size:12px;color:var(--text-tertiary);">编辑 JSON 数组来增删改模板条目。任务模板格式：{ domain, title, ownerId, priority }。其他模板为字符串数组。</p>
  `;

  const footer = `
    <button class="ghost-action" onclick="document.querySelector('.modal-overlay')?.remove()">取消</button>
    <button class="ghost-action" id="form-tpl-save-as">保存为新模板</button>
    <button class="primary-action" id="form-tpl-save">保存</button>
  `;

  openModal(`编辑模板 — ${template.name}`, body, footer);

  document.querySelector("#form-tpl-save")?.addEventListener("click", () => {
    const name = document.querySelector("#form-tpl-name")?.value.trim();
    const itemsText = document.querySelector("#form-tpl-items")?.value.trim();
    if (!name || !itemsText) { showToast("请填写完整", "error"); return; }
    try {
      const items = JSON.parse(itemsText);
      template.name = name;
      template.items = items;
      template.version = (parseFloat(template.version || "1.0") + 0.1).toFixed(1);
      persistState();
      closeModal();
      showToast("模板已保存", "success");
      render();
    } catch (e) {
      showToast("JSON 格式错误，请检查", "error");
    }
  });

  document.querySelector("#form-tpl-save-as")?.addEventListener("click", () => {
    const name = document.querySelector("#form-tpl-name")?.value.trim();
    const itemsText = document.querySelector("#form-tpl-items")?.value.trim();
    if (!name || !itemsText) { showToast("请填写完整", "error"); return; }
    try {
      const items = JSON.parse(itemsText);
      templates.push({
        id: `tpl-custom-${Date.now()}`,
        type: template.type,
        name: name + " (副本)",
        stageKey: template.stageKey,
        items,
        version: "1.0",
      });
      persistState();
      closeModal();
      showToast("已保存为新模板", "success");
      render();
    } catch (e) {
      showToast("JSON 格式错误，请检查", "error");
    }
  });
}

let flowCanvasResizeTimer;
window.addEventListener("resize", () => {
  if (state.route !== "intro") return;
  window.clearTimeout(flowCanvasResizeTimer);
  flowCanvasResizeTimer = window.setTimeout(drawIntroFlowCanvas, 120);
});

window.addEventListener("hashchange", () => {
  restoreRouteFromHash();
  render();
});

render();
